/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Payroll Agentic API
 * API for a graph-based headless agentic platform specialized in payroll, supporting dynamic agent and skill creation, RAG workflows, and knowledge management.
 * OpenAPI spec version: 3.0.1
 */
import { customCoreInstance } from '../api/CoreAxiosInstance';
/**
 * Read/write classification of a capability (LADR-030).

Used as a governance lens (blast radius is reasoned about over ``access``,
``eval_status`` and ``owner`` rather than a stored tier). Write capabilities and
their HITL gate are deferred to a separate record; candidate zero is read-only.
 */
export type AccessMode = typeof AccessMode[keyof typeof AccessMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AccessMode = {
  read: 'read',
  write: 'write',
} as const;

export type AccountStatus = typeof AccountStatus[keyof typeof AccountStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AccountStatus = {
  active: 'active',
  suspended: 'suspended',
  deactivated: 'deactivated',
} as const;

export interface ActiveUsersDataPoint {
  /** Start of the bucket (ISO 8601 date, inclusive) */
  period_start: string;
  /** End of the bucket (ISO 8601 date, inclusive) */
  period_end: string;
  /** Unique users who created at least one thread in this bucket */
  active_users: number;
}

export interface ActiveUsersResponse {
  /** Start of the queried range (ISO 8601) */
  period_start: string;
  /** End of the queried range (ISO 8601) */
  period_end: string;
  /** Granularity of each data point */
  period: Period;
  /** Time-series data points ordered chronologically */
  data_points: ActiveUsersDataPoint[];
  /** Unique active users across the entire range */
  total_active_users: number;
}

/**
 * A registered AI actor in the platform.
 */
export interface Agent {
  id: string;
  blueprint: AgentBlueprint;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active?: boolean;
}

export type AgentBlueprintTemperature = number | null;

export type AgentBlueprintTopP = number | null;

export type AgentBlueprintMaxTokens = number | null;

export type AgentBlueprintFrequencyPenalty = number | null;

export type AgentBlueprintPresencePenalty = number | null;

export type AgentBlueprintSeed = number | null;

export type AgentBlueprintStopSequences = string[] | null;

export type AgentBlueprintParallelToolCalls = boolean | null;

export type AgentBlueprintTimeout = number | null;

export type AgentBlueprintReasoningEffort = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | null;

/**
 * The configuration that defines an Agent's behavior.
 */
export interface AgentBlueprint {
  name: string;
  description: string;
  system_prompt_template: string;
  model_name: string;
  temperature?: AgentBlueprintTemperature;
  top_p?: AgentBlueprintTopP;
  max_tokens?: AgentBlueprintMaxTokens;
  frequency_penalty?: AgentBlueprintFrequencyPenalty;
  presence_penalty?: AgentBlueprintPresencePenalty;
  seed?: AgentBlueprintSeed;
  stop_sequences?: AgentBlueprintStopSequences;
  parallel_tool_calls?: AgentBlueprintParallelToolCalls;
  timeout?: AgentBlueprintTimeout;
  reasoning_effort?: AgentBlueprintReasoningEffort;
  version?: string;
}

/**
 * Request model for creating a new Agent.
 */
export interface AgentCreate {
  blueprint: AgentBlueprint;
}

/**
 * Workflow-specific input payload. Known workflows (e.g., 'shadow_calculator') are strictly validated. Unknown workflows use GenericPayload.
 */
export type AgentInvocationRequestInputPayload = ShadowCalculationWorkflowInput | ShadowRunInput | ShadowRunOrchestratorInput | ShadowCalculatorChildInput | PayrollAnalystWorkflowInput | ShadowLeaveIntegrationInput | ShadowIntegrationInput | OmniValidationInput | CompanyShellProvisionInput | PayComponentImportInput | WorkPatternImportInput | SplitCostingImportInput | SourceMappingInput | AgentRuntimeInput | GenericPayload;

/**
 * Request model for invoking an agent.
 */
export interface AgentInvocationRequest {
  /** Execution identity for the agent invocation */
  service_account_id: string;
  /** Workflow-specific input payload. Known workflows (e.g., 'shadow_calculator') are strictly validated. Unknown workflows use GenericPayload. */
  input_payload: AgentInvocationRequestInputPayload;
  /** Wait for a terminal workflow result before responding. The workflow still runs durably in Temporal if the HTTP wait expires. */
  wait_for_completion?: boolean;
}

/**
 * Workflow result if status is COMPLETED
 */
export type AgentInvocationResponseResult = unknown | null;

/**
 * Error message if status is FAILED
 */
export type AgentInvocationResponseError = string | null;

/**
 * Response envelope for initiated and terminal agent invocations.
 */
export interface AgentInvocationResponse {
  /** Temporal workflow ID (stable identifier for lookups) */
  workflow_id: string;
  /** Temporal run ID (initial run ID, may change during retries) */
  run_id: string;
  /** Current status of the invocation */
  status?: InvocationStatusEnum;
  /** Workflow result if status is COMPLETED */
  result?: AgentInvocationResponseResult;
  /** Error message if status is FAILED */
  error?: AgentInvocationResponseError;
}

/**
 * Workflow result if status is COMPLETED
 */
export type AgentInvocationStatusResult = unknown | null;

/**
 * Error message if status is FAILED
 */
export type AgentInvocationStatusError = string | null;

/**
 * Status model for querying agent invocation state.
 */
export interface AgentInvocationStatus {
  /** Temporal workflow ID (stable identifier for lookups) */
  workflow_id: string;
  /** Temporal run ID (initial run ID, may change during retries) */
  run_id: string;
  /** Current status of the invocation */
  status?: InvocationStatusEnum;
  /** Workflow result if status is COMPLETED */
  result?: AgentInvocationStatusResult;
  /** Error message if status is FAILED */
  error?: AgentInvocationStatusError;
}

/**
 * Input for the generic Agent Runtime workflow.

Triggers execution of a generic Pydantic AI ``TemporalAgent`` driven by a
natural-language ``instruction``.

NOTE: ``agent_id`` is deliberately NOT a field on this client payload model.
It is a **server-authoritative** value from the invocation URL path, passed as
a separate argument to ``AgentWorkflow.run(...)``. A client must never be able
to target a different agent via the request body alone.
 */
export interface AgentRuntimeInput {
  /** Workflow discriminator */
  workflow?: 'agent_runtime';
  /**
   * Natural-language instruction for the agent to execute
   * @minLength 1
   */
  instruction: string;
}

/**
 * An execution identity for an Agent.
Links an Agent to a Workspace with specific Org roles and Capabilities.
 */
export interface AgentServiceAccount {
  id: string;
  agent_id: string;
  workspace_id: string;
  org_roles?: UserOrgRole[];
  capability_grants?: CapabilityGrantOutput[];
  description?: string;
  created_at: string;
  created_by: string;
  is_active?: boolean;
}

/**
 * Request model for creating a new Service Account.
 */
export interface AgentServiceAccountCreate {
  workspace_id: string;
  org_roles?: UserOrgRole[];
  capability_grants?: CapabilityGrantInput[];
  description?: string;
}

/**
 * Which agent's data an eval document belongs to.

Stored as a promoted top-level field on both ``EvalRun`` and
``EvalAnnotation`` in the shared evals container so cross-agent queries
can filter cheaply without descending into the nested model body. The
container's Terraform index policy must include ``/agent_type`` (managed
in the Deployments repo).
 */
export type AgentType = typeof AgentType[keyof typeof AgentType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AgentType = {
  payroll_assistant: 'payroll_assistant',
} as const;

/**
 * Request model for updating an Agent's blueprint.
 */
export interface AgentUpdate {
  blueprint: AgentBlueprint;
}

export type AnswerElementsItem = Markdown | Table | LineGraph;

export type AnswerCitations = ChunkCitation[] | null;

export interface Answer {
  response_kind?: 'answer';
  elements: AnswerElementsItem[];
  citations?: AnswerCitations;
  schema_version?: number;
}

export type ApiKeyCreateExpiresAt = string | null;

export type ApiKeyCreatePurpose = string | null;

/**
 * Request to create a new API key.
 */
export interface ApiKeyCreate {
  developer_id: string;
  developer_email: string;
  expires_at?: ApiKeyCreateExpiresAt;
  workspace_allowlist?: string[];
  purpose?: ApiKeyCreatePurpose;
}

export type ApiKeyCreatedExpiresAt = string | null;

export type ApiKeyCreatedPurpose = string | null;

/**
 * Response after creating a key — contains the plaintext key (shown once).
 */
export interface ApiKeyCreated {
  key_id: string;
  api_key: string;
  developer_id: string;
  expires_at?: ApiKeyCreatedExpiresAt;
  workspace_allowlist?: string[];
  purpose?: ApiKeyCreatedPurpose;
}

export type ApiKeySummaryExpiresAt = string | null;

export type ApiKeySummaryRevokedAt = string | null;

export type ApiKeySummaryLastUsedAt = string | null;

export type ApiKeySummaryPurpose = string | null;

/**
 * API key metadata for listing (no hash or plaintext).
 */
export interface ApiKeySummary {
  key_id: string;
  developer_id: string;
  developer_email: string;
  created_at: string;
  expires_at?: ApiKeySummaryExpiresAt;
  revoked_at?: ApiKeySummaryRevokedAt;
  last_used_at?: ApiKeySummaryLastUsedAt;
  workspace_allowlist?: string[];
  purpose?: ApiKeySummaryPurpose;
}

export type AuditAction = typeof AuditAction[keyof typeof AuditAction];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AuditAction = {
  grant_org_access: 'grant_org_access',
  revoke_org_access: 'revoke_org_access',
  suspend_user: 'suspend_user',
  reactivate_user: 'reactivate_user',
  deactivate_user: 'deactivate_user',
  restore_user: 'restore_user',
  create_group: 'create_group',
  delete_group: 'delete_group',
  add_group_member: 'add_group_member',
  remove_group_member: 'remove_group_member',
  bind_group_to_org: 'bind_group_to_org',
  unbind_group_from_org: 'unbind_group_from_org',
  thread_completion_via_api_key: 'thread_completion_via_api_key',
  thread_create_via_api_key: 'thread_create_via_api_key',
  work_pattern_import_started: 'work_pattern_import_started',
  company_context_validated: 'company_context_validated',
  split_costing_import_committed: 'split_costing_import_committed',
  onboarding_source_imported: 'onboarding_source_imported',
  onboarding_entity_validated: 'onboarding_entity_validated',
  onboarding_entity_reimported: 'onboarding_entity_reimported',
  onboarding_migration_run_started: 'onboarding_migration_run_started',
  onboarding_migration_run_completed: 'onboarding_migration_run_completed',
} as const;

export type AuditEventPageNextCursor = string | null;

/**
 * Response DTO — ``GET .../audit-events`` (cursor-paginated list).

``next_cursor`` is an opaque, caller-must-not-parse token: pass it back
verbatim as the ``cursor`` query parameter to fetch the next page.
``None`` means there is no further page.
 */
export interface AuditEventPage {
  events: AuditEventView[];
  next_cursor?: AuditEventPageNextCursor;
}

export type AuditEventViewDetailsAnyOf = {[key: string]: string};

export type AuditEventViewDetails = AuditEventViewDetailsAnyOf | null;

/**
 * Read DTO — one entry in the ``GET .../audit-events`` page.

Mirrors the audit_trail module's ``AuditEvent`` but omits
``target_workload_id`` (redundant — the caller already scoped the query
to this workload via the path) and the other ``target_*`` fields, which
are always ``None`` for onboarding-emitted actions. ``details`` carries
the action-specific metadata (see LADR-039's audit events table).
 */
export interface AuditEventView {
  event_id: string;
  timestamp: string;
  actor_id: string;
  action: AuditAction;
  details?: AuditEventViewDetails;
}

/**
 * Available pay groups and pay periods for a batch (for filter dropdowns).
 */
export interface BatchFacets {
  /** Distinct pay groups in the batch */
  pay_groups?: PayGroupFacet[];
  /** Distinct pay periods in the batch */
  pay_periods?: PayPeriodFacet[];
}

export type BodyCreateOnboardingImportConfigCode = string | null;

export type BodyCreateOnboardingImportConfigVersion = number | null;

export type BodyCreateOnboardingImportCompanyCode = string | null;

export type BodyCreateOnboardingImportScopeType = 'Employee Group' | null;

export type BodyCreateOnboardingImportProviderId = string | null;

export type BodyCreateOnboardingImportIfMatchSourceVersion = string | null;

export interface BodyCreateOnboardingImport {
  file: string;
  entity_type: EntityType;
  config_code?: BodyCreateOnboardingImportConfigCode;
  config_version?: BodyCreateOnboardingImportConfigVersion;
  company_code?: BodyCreateOnboardingImportCompanyCode;
  scope_type?: BodyCreateOnboardingImportScopeType;
  provider_id?: BodyCreateOnboardingImportProviderId;
  if_match_source_version?: BodyCreateOnboardingImportIfMatchSourceVersion;
}

export type BodyImportPayComponentsProviderId = string | null;

export type BodyImportPayComponentsComponentColumn = string | null;

export interface BodyImportPayComponents {
  file: string;
  provider_id?: BodyImportPayComponentsProviderId;
  component_column?: BodyImportPayComponentsComponentColumn;
}

export type BulkInvitationCreateOrgCategory = typeof BulkInvitationCreateOrgCategory[keyof typeof BulkInvitationCreateOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BulkInvitationCreateOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type BulkInvitationCreateDeliveryMethod = typeof BulkInvitationCreateDeliveryMethod[keyof typeof BulkInvitationCreateDeliveryMethod];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BulkInvitationCreateDeliveryMethod = {
  email: 'email',
  sms: 'sms',
  manual: 'manual',
  none: 'none',
} as const;

export interface BulkInvitationCreate {
  org_id: string;
  org_category: BulkInvitationCreateOrgCategory;
  recipients: Recipient[];
  delivery_method?: BulkInvitationCreateDeliveryMethod;
}

export type BulkInvitationItemResultName = string | null;

export type BulkInvitationItemResultRole = typeof BulkInvitationItemResultRole[keyof typeof BulkInvitationItemResultRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BulkInvitationItemResultRole = {
  admin: 'admin',
  member: 'member',
} as const;

export type BulkInvitationItemResultInvitationId = string | null;

export type BulkInvitationItemResultErrorMessage = string | null;

export type BulkInvitationItemResultOneTimeLink = string | null;

export interface BulkInvitationItemResult {
  email: string;
  name?: BulkInvitationItemResultName;
  role: BulkInvitationItemResultRole;
  invitation_id?: BulkInvitationItemResultInvitationId;
  error_message?: BulkInvitationItemResultErrorMessage;
  one_time_link?: BulkInvitationItemResultOneTimeLink;
}

export interface BulkInvitationResponse {
  succeeded: BulkInvitationItemResult[];
  failed: BulkInvitationItemResult[];
}

export type CapabilityBinding = string | null;

export type CapabilityInputSchema = { [key: string]: unknown };

export type CapabilityOutputSchema = { [key: string]: unknown };

export type CapabilityConfig = { [key: string]: unknown };

/**
 * A registered AI actor capability in the platform catalog.
 */
export interface Capability {
  kind: CapabilityKind;
  binding?: CapabilityBinding;
  input_schema?: CapabilityInputSchema;
  output_schema?: CapabilityOutputSchema;
  /** @minLength 1 */
  owner: string;
  access?: AccessMode;
  config?: CapabilityConfig;
  id: string;
  name: string;
  eval_status?: EvalStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active?: boolean;
}

export type CapabilityCreateBinding = string | null;

export type CapabilityCreateInputSchema = { [key: string]: unknown };

export type CapabilityCreateOutputSchema = { [key: string]: unknown };

export type CapabilityCreateConfig = { [key: string]: unknown };

/**
 * Request model for creating a new Capability.

``extra="forbid"`` rejects unknown body fields with HTTP 422 rather than silently
dropping them, so a client typo (e.g. ``inputSchema``) fails loud. Mirrors
``SourceCreate`` in ``document_ingest``. The stored ``Capability`` stays lenient to
preserve forward-compat on persisted and grant-snapshot documents.
 */
export interface CapabilityCreate {
  kind: CapabilityKind;
  binding?: CapabilityCreateBinding;
  input_schema?: CapabilityCreateInputSchema;
  output_schema?: CapabilityCreateOutputSchema;
  /** @minLength 1 */
  owner: string;
  access?: AccessMode;
  config?: CapabilityCreateConfig;
  name: string;
}

export type CapabilityGrantInputSyncedAt = string | null;

export type CapabilityGrantInputSyncedBy = string | null;

/**
 * Embedded capability snapshot granted to a Service Account.
 */
export interface CapabilityGrantInput {
  source_capability_id: string;
  snapshot: Capability;
  granted_at: string;
  granted_by: string;
  synced_at?: CapabilityGrantInputSyncedAt;
  synced_by?: CapabilityGrantInputSyncedBy;
}

export type CapabilityGrantOutputSyncedAt = string | null;

export type CapabilityGrantOutputSyncedBy = string | null;

/**
 * Embedded capability snapshot granted to a Service Account.
 */
export interface CapabilityGrantOutput {
  source_capability_id: string;
  snapshot: Capability;
  granted_at: string;
  granted_by: string;
  synced_at?: CapabilityGrantOutputSyncedAt;
  synced_by?: CapabilityGrantOutputSyncedBy;
}

/**
 * Top-level capability taxonomy (LADR-030).

- CONNECTOR: a governed connection (auth + transport) whose operations project
  into tools. Not agent-callable directly; tools bind to it. Supersedes the
  LADR-005 ``integration`` type.
- TOOL: a typed, deterministic, contract-bound action. Agent-callable. Retrieval
  is a tool (reclassified from the LADR-005 ``knowledge_base`` skill). Resolved
  via Layer 1 (the typed-capability resolver).
- SKILL: an Agent Skills (agentskills.io) packaged bundle executed in a sandbox.
  Agent-callable. Resolved via Layer 2 (packaged-skill runtime, LADR-031) —
  named here for forward-compatibility; no implementation is built in this slice.
 */
export type CapabilityKind = typeof CapabilityKind[keyof typeof CapabilityKind];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CapabilityKind = {
  connector: 'connector',
  tool: 'tool',
  skill: 'skill',
} as const;

export type CapabilityUpdateName = string | null;

export type CapabilityUpdateBinding = string | null;

export type CapabilityUpdateInputSchemaAnyOf = { [key: string]: unknown };

export type CapabilityUpdateInputSchema = CapabilityUpdateInputSchemaAnyOf | null;

export type CapabilityUpdateOutputSchemaAnyOf = { [key: string]: unknown };

export type CapabilityUpdateOutputSchema = CapabilityUpdateOutputSchemaAnyOf | null;

export type CapabilityUpdateOwner = string | null;

export type CapabilityUpdateAccess = AccessMode | null;

export type CapabilityUpdateEvalStatus = EvalStatus | null;

export type CapabilityUpdateConfigAnyOf = { [key: string]: unknown };

export type CapabilityUpdateConfig = CapabilityUpdateConfigAnyOf | null;

export type CapabilityUpdateIsActive = boolean | null;

/**
 * Request model for updating a Capability.

Contract validation happens in the service layer when merging with the existing
capability (the merged record is re-validated through ``Capability.model_validate``),
since the merged kind/binding/schema are needed to validate the contract.

``kind`` is immutable and is therefore not a field here. Combined with
``extra="forbid"``, a ``PATCH`` carrying ``kind`` (or any other unknown/read-only
field) is rejected with HTTP 422 rather than silently ignored — so a client never
gets a 200 for a change that did not happen. Mirrors ``SourceUpdate`` in
``document_ingest``, whose immutable ``source_type`` is the same shape as ``kind``.
 */
export interface CapabilityUpdate {
  name?: CapabilityUpdateName;
  binding?: CapabilityUpdateBinding;
  input_schema?: CapabilityUpdateInputSchema;
  output_schema?: CapabilityUpdateOutputSchema;
  owner?: CapabilityUpdateOwner;
  access?: CapabilityUpdateAccess;
  eval_status?: CapabilityUpdateEvalStatus;
  config?: CapabilityUpdateConfig;
  is_active?: CapabilityUpdateIsActive;
}

export type ChildWorkflowRefEntityType = EntityType | null;

/**
 * Reference to one child workflow invocation started by a migration run's
orchestrator (e.g. `CompanyShellProvisionWorkflow`).
 */
export interface ChildWorkflowRef {
  workflow_id: string;
  run_id: string;
  entity_type?: ChildWorkflowRefEntityType;
}

export type ChunkCitationDocName = string | null;

export interface ChunkCitation {
  org_category: string;
  org_id: string;
  doc_id: string;
  doc_name?: ChunkCitationDocName;
  chunk_id: string;
  chunk_index: number;
}

export type ChunkReadOrgCategory = typeof ChunkReadOrgCategory[keyof typeof ChunkReadOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ChunkReadOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export interface ChunkRead {
  org_category: ChunkReadOrgCategory;
  org_id: string;
  doc_id: string;
  doc_name: string;
  doc_source: string;
  doc_tags: string[];
  chunk_id: string;
  chunk_index: string;
  chunk_text: string;
}

export interface ClarificationRequest {
  response_kind?: 'clarification_request';
  question: string;
}

/**
 * Human-readable cluster label
 */
export type ClusterSummaryLabel = string | null;

/**
 * Summary statistics for a single cluster.
 */
export interface ClusterSummary {
  /** Cluster identifier (-1 = noise) */
  cluster_id: number;
  /** Human-readable cluster label */
  label?: ClusterSummaryLabel;
  /** Number of threads in this cluster */
  thread_count: number;
  /** Mean x-coordinate of cluster members */
  centroid_x: number;
  /** Mean y-coordinate of cluster members */
  centroid_y: number;
}

/**
 * Request DTO — create a new config and mint version 1.
 *
 * Optional `config_code` may be sent by older clients; Core API ignores it and
 * always mints a UUID (R-GOV-15). Portal create omits the field. Regenerate from
 * Core API OpenAPI once #1060 ships.
 */
export interface CompanyConfigCreate {
  /** Ignored by API when present — durable id is always server-minted. */
  config_code?: string | null;
  /**
   * @minLength 1
   * @maxLength 255
   */
  display_name: string;
  profile: CompanyConfigProfileInput;
  /** @minLength 1 */
  change_description: string;
}

/**
 * The complete company-configuration profile, composed of its sections.

Web derives the section layout from the four sub-profile types, which are
distinct OpenAPI component schemas. A flag lives in exactly one sub-profile,
so catalog⇄profile drift is structurally impossible (R-REF-1 met at section
granularity). The finer Datapay/Direct-Access sub-grouping and Christmas
display labels are a Web concern, not modelled here.
 */
export interface CompanyConfigProfileInput {
  CompanyOptions: CompanyOptionsProfile;
  PayGroupOptions?: PayGroupOptionsProfile;
  CompanySettings?: CompanySettingsProfile;
  PayGroupSettings?: PayGroupSettingsProfile;
}

/**
 * The complete company-configuration profile, composed of its sections.

Web derives the section layout from the four sub-profile types, which are
distinct OpenAPI component schemas. A flag lives in exactly one sub-profile,
so catalog⇄profile drift is structurally impossible (R-REF-1 met at section
granularity). The finer Datapay/Direct-Access sub-grouping and Christmas
display labels are a Web concern, not modelled here.
 */
export interface CompanyConfigProfileOutput {
  CompanyOptions: CompanyOptionsProfile;
  PayGroupOptions?: PayGroupOptionsProfile;
  CompanySettings?: CompanySettingsProfile;
  PayGroupSettings?: PayGroupSettingsProfile;
}

/**
 * Request DTO — retire (soft-delete) a version.
 */
export interface CompanyConfigRetire {
  /** @minLength 1 */
  change_description: string;
}

export type CompanyConfigStatus = typeof CompanyConfigStatus[keyof typeof CompanyConfigStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CompanyConfigStatus = {
  PUBLISHED: 'PUBLISHED',
  RETIRED: 'RETIRED',
} as const;

export type CompanyConfigVersionBasedOn = number | null;

export type CompanyConfigVersionRetiredBy = string | null;

export type CompanyConfigVersionRetiredAt = string | null;

export type CompanyConfigVersionRetireDescription = string | null;

/**
 * One immutable published version of a company configuration template.
 */
export interface CompanyConfigVersion {
  /**
   * @minLength 1
   * @pattern ^[^/\\#?\x00-\x1f]+\z
   */
  config_code: string;
  version: number;
  display_name: string;
  based_on: CompanyConfigVersionBasedOn;
  profile: CompanyConfigProfileOutput;
  change_description: string;
  status: CompanyConfigStatus;
  published_by: string;
  published_at: string;
  retired_by: CompanyConfigVersionRetiredBy;
  retired_at: CompanyConfigVersionRetiredAt;
  retire_description: CompanyConfigVersionRetireDescription;
}

export type CompanyConfigVersionCreateBasedOn = number | null;

/**
 * Request DTO — save a new version onto an existing config.
 */
export interface CompanyConfigVersionCreate {
  /**
   * @minLength 1
   * @maxLength 1024
   */
  display_name: string;
  profile: CompanyConfigProfileInput;
  /** @minLength 1 */
  change_description: string;
  based_on?: CompanyConfigVersionCreateBasedOn;
}

export type CompanyContactName = string | null;

export type CompanyContactPosition = string | null;

export type CompanyContactPhone = string | null;

export type CompanyContactMobile = string | null;

export type CompanyContactFax = string | null;

export type CompanyContactEmail = string | null;

/**
 * Contact block used for both ``mainContact`` and ``secondaryContact``.

The ``secondaryContact`` block is conditionally required — see the CSV
legend; enforced by the validator, not by ``Optional``-ness here.
 */
export interface CompanyContact {
  name?: CompanyContactName;
  position?: CompanyContactPosition;
  phone?: CompanyContactPhone;
  mobile?: CompanyContactMobile;
  fax?: CompanyContactFax;
  email?: CompanyContactEmail;
}

export type CompanyElementMappingComponentName = string | null;

export type CompanyElementMappingParameterQualifiedName = string | null;

/**
 * A company-saved column-to-parameter mapping persisted in Pacific.

Keyed on (companyId, componentName?, elementQualifiedName, parameter identifier)
where the parameter identifier is `parameterQualifiedName` when present, else
`parameterDisplayName`. When ``componentName`` is null the mapping cascades to
all components on the element (legacy); when set it applies only to that
component.
 */
export interface CompanyElementMapping {
  companyId: string;
  componentName?: CompanyElementMappingComponentName;
  elementQualifiedName: string;
  parameterQualifiedName?: CompanyElementMappingParameterQualifiedName;
  parameterDisplayName?: string;
  columnName: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Company start date from Christmas identity.startDate, when present. Used by work-pattern import pre-flight (pattern startDate must be on or after this date).
 */
export type CompanyLookupResultCompanyStartDate = string | null;

/**
 * A resolved company code + name.

Returned by ``GET /api/v1/onboarding/companies/{companyCode}`` on a 200.
The values originate from the Christmas company-details response
(``identity.companyCode`` / ``identity.companyName`` /
``identity.startDate``).
 */
export interface CompanyLookupResult {
  /** The resolved company code (echoed from Christmas). */
  companyCode: string;
  /** Human-readable company name for the portal to display. */
  companyName: string;
  /** Company start date from Christmas identity.startDate, when present. Used by work-pattern import pre-flight (pattern startDate must be on or after this date). */
  companyStartDate?: CompanyLookupResultCompanyStartDate;
}

export type CompanyOptionsProfileLeaveRequestEscalation = number | null;

/**
 * The default company-configuration profile.

Each field is snake_case with a PascalCase Pydantic alias matching the
exact product field name from Appendix A of LADR-032.  All bool fields
default to False; optional non-bool fields default to None.

The leave-request-escalation rule:
    When enable_leave_request_escalation is True,
    leave_request_escalation must be present and > 0.
 */
export interface CompanyOptionsProfile {
  LeaveRequestEscalation?: CompanyOptionsProfileLeaveRequestEscalation;
  EnableLeaveRequestEscalation?: boolean;
  HidePayPacketWarnings?: boolean;
  EnableMultiCompanyEmployeeGroups?: boolean;
  EnableTwoFactorAuthentication?: boolean;
  HidePeersLeaveInDA?: boolean;
  EnableGenerateMultipleBankFiles?: boolean;
  DisableCostBoxCodeZeroTrimming?: boolean;
  AutoMapEmployeeGroupsForNewEmployees?: boolean;
  DisableNewEmployeeWizardInDA?: boolean;
  DisableEmployeeModificationInDA?: boolean;
  EmployeesCanViewOrganisationStructure?: boolean;
  EnableDirectoryCreation?: boolean;
  EnableCopyNoMerge?: boolean;
  RevertToChromeForEmbeddedMfaPage?: boolean;
  RemoveCompanyCodeCheckFromTimesheet?: boolean;
  PayPacketTimesheetApportioning?: boolean;
  UseCompanyMainContactForSTPDeclarer?: boolean;
  ManagerReporteeAccessToPaySlipHistoryAndLeaveBalances?: boolean;
  EnableSmsActivationMobileAndEmailUsers?: boolean;
  AvoidReportCache?: boolean;
  EnableOtherPaymentsInDA?: boolean;
  EnableManualLeavePriority?: boolean;
  CreateTerminationWizardAction?: boolean;
  EnableCompanyGroupAuthorisationDistribution?: boolean;
  ExcludeZeroedBankAccountsInBankFile?: boolean;
  EnableEmployeePayslipsSFTP?: boolean;
  EnableEmployeeJobClassification?: boolean;
  WorkdayEnableTotalRemuneration?: boolean;
  WorkdayIgnoreCompensationElement?: boolean;
  WorkdayIgnoreAllowancePlan?: boolean;
  EnableLatestRuleTemplate?: boolean;
  EnableWorkdayMultiJobTransformation?: boolean;
  EnableMultiJobbing?: boolean;
  EnablePayrollAssistant?: boolean;
  UseUTF8WithBomReportEncoding?: boolean;
}

export type CompanySettingsProfileSetDirectAccessAdditionalResourcesUrl = string | null;

/**
 * Company-entity screen fields from the Christmas Company Options screen.

These 14 booleans and 1 URL string persist on the Christmas `Company` entity
(NOT `CompanyOptions`), so they form their own profile section. Aliases match
the exact `Company.cs` C# property names. Booleans default `False`; the
optional URL string defaults `None`.
 */
export interface CompanySettingsProfile {
  AdvancedSorting?: boolean;
  AllowESSUsersToResetTheirPassword?: boolean;
  SendEmailToAdminOnEmployeePasswordReset?: boolean;
  PasswordProtectPayslipHistory?: boolean;
  EmailESSPaySlipsOnDCDate?: boolean;
  EnableDirectAccess?: boolean;
  ExcludeFromACRFile?: boolean;
  PayPublicHolidays?: boolean;
  HideESSURL?: boolean;
  UseLeaveReasons?: boolean;
  UseSpecifiedLeaveAmountPerPayPeriod?: boolean;
  UseTerminationRequests?: boolean;
  UseCompressedNewEmployeeForm?: boolean;
  EnableTrialComponentVersions?: boolean;
  SetDirectAccessAdditionalResourcesUrl?: CompanySettingsProfileSetDirectAccessAdditionalResourcesUrl;
}

export type CompanyShellPostalAddressLine1 = string | null;

export type CompanyShellPostalAddressLine2 = string | null;

export type CompanyShellPostalAddressLine3 = string | null;

/**
 * Postal or courier address (three lines, matching the CSV columns).
 */
export interface CompanyShellPostalAddress {
  line1?: CompanyShellPostalAddressLine1;
  line2?: CompanyShellPostalAddressLine2;
  line3?: CompanyShellPostalAddressLine3;
}

/**
 * Track 1 inline rows to validate / commit. Mutually exclusive with entity_record_ref. Capped at MAX_ROWS_PER_INVOCATION when set.
 */
export type CompanyShellProvisionInputRows = CompanyShellRow[] | null;

/**
 * Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with rows. Hydrated inside the workflow before the existing row-processing path runs.
 */
export type CompanyShellProvisionInputEntityRecordRef = EntityRecordRef | null;

/**
 * Optional client-supplied correlation id. Threaded through activity logs and used to build deterministic idempotency keys. When provided, must be 1–128 characters from [A-Za-z0-9._:-].
 */
export type CompanyShellProvisionInputCorrelationId = string | null;

/**
 * Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run; left unset for direct agent-invocation callers. Consumed at workflow completion (#899 Step 8) to look up and finalize the owning MigrationRun via get_migration_run(workload_id, run_id) — see workload_id below.
 */
export type CompanyShellProvisionInputRunId = string | null;

/**
 * Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a child of a canonical migration run, so the workflow's finalize step can resolve the owning MigrationRun document. Left unset for direct agent-invocation callers.
 */
export type CompanyShellProvisionInputWorkloadId = string | null;

/**
 * API-boundary payload for the ``CompanyShellProvisionWorkflow``.

The ``workflow`` field is the ``WorkflowPayload`` discriminator; keep the
Literal value in sync with the entry in
``ONBOARDING_TASK_QUEUE_WORKFLOWS`` inside ``workflow_registry.py``.

Authorisation is enforced at invocation time via the service account's
workspace ReBAC guards — not by comparing row ``company_group_name`` values
to Agentic org/workspace identifiers inside the workflow, and not by
constraining which ``mypay_environment`` target the caller may select
(see ``app/temporal/onboarding/AGENTS.md`` — Authorization & scope model).
 */
export interface CompanyShellProvisionInput {
  /** Workflow discriminator */
  workflow?: 'company_shell_provision';
  /** validate (dry-run) or commit (all-or-nothing at pre-flight) */
  mode: ProvisionMode;
  /** Published CompanyConfigVersion to apply as company options */
  template: CompanyTemplateRef;
  /** Track 1 inline rows to validate / commit. Mutually exclusive with entity_record_ref. Capped at MAX_ROWS_PER_INVOCATION when set. */
  rows?: CompanyShellProvisionInputRows;
  /** Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with rows. Hydrated inside the workflow before the existing row-processing path runs. */
  entity_record_ref?: CompanyShellProvisionInputEntityRecordRef;
  /** Optional client-supplied correlation id. Threaded through activity logs and used to build deterministic idempotency keys. When provided, must be 1–128 characters from [A-Za-z0-9._:-]. */
  correlation_id?: CompanyShellProvisionInputCorrelationId;
  /** Target Datapay environment; routes to the matching MyPay API deployment. */
  mypay_environment: MyPayEnvironment;
  /** Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run; left unset for direct agent-invocation callers. Consumed at workflow completion (#899 Step 8) to look up and finalize the owning MigrationRun via get_migration_run(workload_id, run_id) — see workload_id below. */
  run_id?: CompanyShellProvisionInputRunId;
  /** Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a child of a canonical migration run, so the workflow's finalize step can resolve the owning MigrationRun document. Left unset for direct agent-invocation callers. */
  workload_id?: CompanyShellProvisionInputWorkloadId;
  /** Destination-tier axis (LADR-039 Slice 4, #899) selecting which Christmas credential/endpoint tier (test vs. prod) to push to — orthogonal to mypay_environment's product routing. Defaults to TEST so existing agent-invocation callers keep resolving against today's single (test-tier) credential set unchanged. */
  target_environment?: TargetEnvironment;
}

/**
 * ISO country code — NZ (default) or AU
 */
export type CompanyShellRowCountryCode = typeof CompanyShellRowCountryCode[keyof typeof CompanyShellRowCountryCode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CompanyShellRowCountryCode = {
  NZ: 'NZ',
  AU: 'AU',
} as const;

/**
 * Company Group from the shell-upload CSV (C.1 row 5) — a DataPay group name validated via ``GET /v/1/onboarding/companygroups/{name}``. Optional; when omitted or blank, Christmas auto-creates a single-company group. When set, the canonical name from that GET is forwarded to Christmas as ``companyGroupName``. Not an Agentic workspace/org id; Pacific authorisation is enforced at invocation time via the service account ReBAC guards.
 */
export type CompanyShellRowCompanyGroupName = string | null;

/**
 * Bureau ID for the shell (Christmas-scoped)
 */
export type CompanyShellRowBureauId = string | null;

/**
 * Region name from the CSV — server resolves to regionId via the region-resolver port. Required when country_code == 'NZ' (Company Region is a NZ concept). When non-blank it must match the closed list of the 12 NZ regions (case-insensitive): Auckland, Taranaki, Hawke's Bay, Wellington, Marlborough, Nelson, Canterbury, Canterbury (South), Westland, Otago, Southland, Chatham Islands. AU rows leave this blank and carry their state via postal_state.
 */
export type CompanyShellRowRegionName = string | null;

/**
 * Datacom End Date (DD/MM/YYYY), if any
 */
export type CompanyShellRowEndDate = string | null;

/**
 * Company Annual Leave Anniversary Date (DD/MM/YYYY)
 */
export type CompanyShellRowAnnualLeaveAnniversaryDate = string | null;

export type CompanyShellRowTradingName = string | null;

/**
 * Company IRD Number (NZ)
 */
export type CompanyShellRowCompanyIrdNo = string | null;

/**
 * Parsed Sick Leave Stand Down (validator emits errors on parse failure)
 */
export type CompanyShellRowSickLeaveStandDown = SickLeaveStandDown | null;

/**
 * Australian Business Number (AU only). Required for AU rows; validated by ABN mod-89 checksum. WPN shape (8 digits) is also accepted — WPN checksum is deferred to Christmas validateOnly.
 */
export type CompanyShellRowCompanyAbnNumber = string | null;

/**
 * ABN Branch Number (AU only, optional). When present must be exactly 3 digits; max_length=3 enforced at the model boundary.
 */
export type CompanyShellRowCompanyAbnBranchNumber = string | null;

/**
 * State/territory code for the postal address (AU only)
 */
export type CompanyShellRowPostalState = string | null;

/**
 * Postcode for the postal address (AU only)
 */
export type CompanyShellRowPostalCode = string | null;

/**
 * State/territory code for the courier address (AU only)
 */
export type CompanyShellRowCourierState = string | null;

/**
 * Postcode for the courier address (AU only)
 */
export type CompanyShellRowCourierCode = string | null;

/**
 * Secondary contact block — conditionally required per the CSV legend
 */
export type CompanyShellRowSecondaryContact = CompanyContact | null;

/**
 * Delivery method (rule 20)
 */
export type CompanyShellRowDelivery = string | null;

/**
 * Delivery note (rule 21)
 */
export type CompanyShellRowDeliveryNote = string | null;

/**
 * EFiling Option — Christmas home unconfirmed
 */
export type CompanyShellRowEfilingOption = string | null;

/**
 * Large Company — closed list Small/Large (C.1 row 13)
 */
export type CompanyShellRowLargeCompany = string | null;

/**
 * ACR Product Type — Christmas home unconfirmed
 */
export type CompanyShellRowAcrProductType = string | null;

/**
 * KiwiSaver Option — Christmas home unconfirmed
 */
export type CompanyShellRowKiwisaverOption = string | null;

/**
 * ACC Classification Unit — Christmas home unconfirmed
 */
export type CompanyShellRowAccClassificationUnit = string | null;

/**
 * FTE Standard Hours/Week (rule 18) — Christmas home unconfirmed
 */
export type CompanyShellRowFteStandardHoursPerWeek = number | null;

/**
 * Week Start Day (rule 19) — Christmas home unconfirmed
 */
export type CompanyShellRowWeekStartDay = string | null;

/**
 * One company-shell row derived from the portal's CSV parse.

The portal maps the CSV **Company Group** column to ``companyGroupName``
on the JSON row (camelCase, matching the Christmas create payload).

Field homes annotated as *"unconfirmed"* correspond to CSV columns whose
Christmas patch destination the issue itself flags as unresolved
(EFiling Option, Large Company, ACR Product Type, KiwiSaver Option,
ACC Classification Unit, FTE Standard Hours/Week, Week Start Day).
These land here as free-form optional strings until the real Christmas
Phase-1 client resolves them; the placeholder adapter is content to
pass them through.

AUS-specific fields (``company_abn_number``, ``company_abn_branch_number``,
``postal_state``, ``postal_code``, ``courier_state``, ``courier_code``) are
present on every row but left ``None`` for NZ rows. Requiredness for AU rows
is enforced by the ``Validator`` class, not at the Pydantic boundary, so that
multi-error aggregation over the full row continues to work.
 */
export interface CompanyShellRow {
  /** Alphanumeric company code */
  company_code: string;
  /** Legal company name */
  company_name: string;
  /** ISO country code — NZ (default) or AU */
  country_code?: CompanyShellRowCountryCode;
  /** Company Group from the shell-upload CSV (C.1 row 5) — a DataPay group name validated via ``GET /v/1/onboarding/companygroups/{name}``. Optional; when omitted or blank, Christmas auto-creates a single-company group. When set, the canonical name from that GET is forwarded to Christmas as ``companyGroupName``. Not an Agentic workspace/org id; Pacific authorisation is enforced at invocation time via the service account ReBAC guards. */
  companyGroupName?: CompanyShellRowCompanyGroupName;
  /** Bureau ID for the shell (Christmas-scoped) */
  bureau_id?: CompanyShellRowBureauId;
  /** Region name from the CSV — server resolves to regionId via the region-resolver port. Required when country_code == 'NZ' (Company Region is a NZ concept). When non-blank it must match the closed list of the 12 NZ regions (case-insensitive): Auckland, Taranaki, Hawke's Bay, Wellington, Marlborough, Nelson, Canterbury, Canterbury (South), Westland, Otago, Southland, Chatham Islands. AU rows leave this blank and carry their state via postal_state. */
  region_name?: CompanyShellRowRegionName;
  /** Datacom Start Date (DD/MM/YYYY) */
  start_date: string;
  /** Datacom End Date (DD/MM/YYYY), if any */
  end_date?: CompanyShellRowEndDate;
  /** Company Annual Leave Anniversary Date (DD/MM/YYYY) */
  annual_leave_anniversary_date?: CompanyShellRowAnnualLeaveAnniversaryDate;
  trading_name?: CompanyShellRowTradingName;
  /** Company IRD Number (NZ) */
  company_ird_no?: CompanyShellRowCompanyIrdNo;
  /** Parsed Sick Leave Stand Down (validator emits errors on parse failure) */
  sick_leave_stand_down?: CompanyShellRowSickLeaveStandDown;
  /** Australian Business Number (AU only). Required for AU rows; validated by ABN mod-89 checksum. WPN shape (8 digits) is also accepted — WPN checksum is deferred to Christmas validateOnly. */
  company_abn_number?: CompanyShellRowCompanyAbnNumber;
  /** ABN Branch Number (AU only, optional). When present must be exactly 3 digits; max_length=3 enforced at the model boundary. */
  company_abn_branch_number?: CompanyShellRowCompanyAbnBranchNumber;
  /** State/territory code for the postal address (AU only) */
  postal_state?: CompanyShellRowPostalState;
  /** Postcode for the postal address (AU only) */
  postal_code?: CompanyShellRowPostalCode;
  /** State/territory code for the courier address (AU only) */
  courier_state?: CompanyShellRowCourierState;
  /** Postcode for the courier address (AU only) */
  courier_code?: CompanyShellRowCourierCode;
  /** Main contact block (name/phone/mobile/email required per rules 11-15) */
  main_contact?: CompanyContact;
  /** Secondary contact block — conditionally required per the CSV legend */
  secondary_contact?: CompanyShellRowSecondaryContact;
  /** Postal address (rules 16-17) */
  postal_address?: CompanyShellPostalAddress;
  /** Courier address (rules 16-17) */
  courier_address?: CompanyShellPostalAddress;
  /** Delivery method (rule 20) */
  delivery?: CompanyShellRowDelivery;
  /** Delivery note (rule 21) */
  delivery_note?: CompanyShellRowDeliveryNote;
  /** One entry per retained frequency; per-frequency PPE + pay-group start dates. Validator enforces at least one entry. */
  pay_schedules?: PayFrequencySchedule[];
  /** EFiling Option — Christmas home unconfirmed */
  efiling_option?: CompanyShellRowEfilingOption;
  /** Large Company — closed list Small/Large (C.1 row 13) */
  large_company?: CompanyShellRowLargeCompany;
  /** ACR Product Type — Christmas home unconfirmed */
  acr_product_type?: CompanyShellRowAcrProductType;
  /** KiwiSaver Option — Christmas home unconfirmed */
  kiwisaver_option?: CompanyShellRowKiwisaverOption;
  /** ACC Classification Unit — Christmas home unconfirmed */
  acc_classification_unit?: CompanyShellRowAccClassificationUnit;
  /** FTE Standard Hours/Week (rule 18) — Christmas home unconfirmed */
  fte_standard_hours_per_week?: CompanyShellRowFteStandardHoursPerWeek;
  /** Week Start Day (rule 19) — Christmas home unconfirmed */
  week_start_day?: CompanyShellRowWeekStartDay;
}

/**
 * Selector for a published ``CompanyConfigVersion``.

Resolved via ``CompanyConfigRepositoryPort.get_version(config_code, version)``
inside the ``resolve_template`` activity. Unknown or retired versions are
rejected with ``TemplateVersionNotFoundError`` / ``TemplateVersionRetiredError``.
 */
export interface CompanyTemplateRef {
  /** Company configuration code (e.g. 'nz-standard') */
  config_code: string;
  /**
   * Published version number
   * @minimum 1
   */
  version: number;
}

/**
 * Field-level FSC vs Pacific comparison for packet detail view.
 */
export interface ComparisonRow {
  /** Internal field key (e.g., paye, netPay) */
  field: string;
  /** Display label for packet detail UI */
  label: string;
  /** FSC field value */
  fsc_value: number;
  /** Pacific field value */
  pacific_value: number;
  /** Pacific - FSC difference */
  difference: number;
  /** Percent difference relative to FSC value */
  percentage_difference: number;
}

export interface CompletionTimeseriesResponse {
  /** Start of the queried range (ISO 8601) */
  period_start: string;
  /** End of the queried range (ISO 8601) */
  period_end: string;
  /** Granularity of each data point */
  period: Period;
  /** Time-series data points ordered chronologically */
  data_points: CompletionsDataPoint[];
  /** Sum of all completions across the entire range */
  total_completions: number;
}

export interface CompletionsDataPoint {
  /** Start of the bucket (ISO 8601 date, inclusive) */
  period_start: string;
  /** End of the bucket (ISO 8601 date, inclusive) */
  period_end: string;
  /** Total completion count in this bucket */
  completions: number;
}

export interface CompletionsPerUserResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  users: UserCompletionCount[];
  /** Sum of all completion requests */
  total_completions: number;
}

export type ComponentMappingDetailPacificComponentInvariantName = string | null;

export type ComponentMappingDetailPacificComponentId = string | null;

export type ComponentMappingDetailPacificComponentAssignmentId = string | null;

export type ComponentMappingDetailMappingReason = string | null;

/**
 * Link from FSC component to Pacific input component.
 */
export interface ComponentMappingDetail {
  fsc_code: string;
  fsc_name: string;
  pacific_component_invariant_name?: ComponentMappingDetailPacificComponentInvariantName;
  pacific_component_id?: ComponentMappingDetailPacificComponentId;
  pacific_component_assignment_id?: ComponentMappingDetailPacificComponentAssignmentId;
  mapped?: boolean;
  mapping_reason?: ComponentMappingDetailMappingReason;
}

/**
 * Element-relative stable parameter name (key for mapping storage). Null for no-bucket-storage slots (e.g. Bank Account Number, Employee Code) that are addressed by bucketTypeId only.
 */
export type ComponentParameterParameterQualifiedName = string | null;

/**
 * Value type: Money | Text | Boolean | Decimal
 */
export type ComponentParameterBucketValueType = typeof ComponentParameterBucketValueType[keyof typeof ComponentParameterBucketValueType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ComponentParameterBucketValueType = {
  Money: 'Money',
  Text: 'Text',
  Boolean: 'Boolean',
  Decimal: 'Decimal',
} as const;

export type ComponentParameterCompanyComponentId = string | null;

export type ComponentParameterIsAmbiguous = boolean | null;

export type ComponentParameterIsIndependentlyAssignable = boolean | null;

/**
 * A single parameter for a pay component.

Returned by the Christmas parameter discovery endpoint
(POST /app/v/1/onboarding/paycomponents/parameters).
Only input slots are returned (output slots are filtered out at the adapter
layer). ``parameterQualifiedName`` may be null for no-bucket-storage input
slots (e.g. Bank Account Number); those are mapped by ``displayName`` instead.

``isSealed`` follows LADR-038 visibility semantics: it is a visibility flag,
NOT a write ban. Sealed parameters are included and may be written at employee
scope; commit-time ``PAYCOMP_PARAMETER_NOT_WRITABLE`` handling (for values
genuinely held at a higher scope) is deferred to the HTTP/MyPay adapter phase.
 */
export interface ComponentParameter {
  /** Stable bucket reference; survives ComponentVersion upgrades */
  bucketTypeId: number;
  /** Element-relative stable parameter name (key for mapping storage). Null for no-bucket-storage slots (e.g. Bank Account Number, Employee Code) that are addressed by bucketTypeId only. */
  parameterQualifiedName?: ComponentParameterParameterQualifiedName;
  /** Element family identifier shared by all components on this element */
  elementQualifiedName: string;
  /** Human-readable label for portal rendering only */
  displayName: string;
  /** Value type: Money | Text | Boolean | Decimal */
  bucketValueType: ComponentParameterBucketValueType;
  /** LADR-038 visibility flag, NOT a write ban. Sealed parameters are included and writable at employee scope; commit-time PAYCOMP_PARAMETER_NOT_WRITABLE handling is deferred to the HTTP adapter phase. */
  isSealed: boolean;
  /** When True, the value must be supplied as a reference lookup (set ``referenceValue``); plain ``value`` is not accepted for this parameter. */
  requiresReferenceValue?: boolean;
  companyComponentId?: ComponentParameterCompanyComponentId;
  isAmbiguous?: ComponentParameterIsAmbiguous;
  isIndependentlyAssignable?: ComponentParameterIsIndependentlyAssignable;
}

/**
 * Inbound bulk discovery request for the Agentic API router.
 */
export interface ComponentsParametersRequest {
  /**
   * CompanyComponent.Name values; LADR-038 caps a request at 1000.
   * @maxItems 1000
   */
  componentNames?: string[];
}

export type CreateAnnotationRequestReviewerAssigned = string | null;

/**
 * Body for ``POST /eval/annotations``.

Carries the source ref and an optional reviewer assignment. Audit
fields and the annotation ID are stamped by the service so callers
cannot forge them.
 */
export interface CreateAnnotationRequest {
  source_ref: ThreadSourceRef;
  reviewer_assigned?: CreateAnnotationRequestReviewerAssigned;
}

export type CreateDocumentStoreIfNotExistsResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface CreateDocumentStoreIfNotExistsResult {
  step?: 'create_document_store_if_not_exists';
  result: CreateDocumentStoreIfNotExistsResultResult;
}

export type CreateOrgIfNotExistsResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface CreateOrgIfNotExistsResult {
  step?: 'create_org_if_not_exists';
  result: CreateOrgIfNotExistsResultResult;
}

export interface Dataset {
  label: string;
  data: number[];
}

/**
 * Entry for a failed packet in the dead-letter queue.

Logged to blob storage when a shadow calculation fails after exhausting retries.
Contains error context for debugging and manual retry capability.
 */
export interface DeadLetterEntry {
  /** Document ID of the failed packet */
  doc_id: string;
  /** Batch identifier this packet belongs to */
  shadow_run_id: string;
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Temporal workflow ID that failed */
  workflow_id: string;
  /** Human-readable error message */
  error_message: string;
  /**
   * Number of retry attempts made
   * @minimum 0
   */
  retry_count: number;
  /** Timestamp when the packet was sent to DLQ */
  failed_at: string;
  /** Error class name (e.g., 'PayPacketNotFoundError') for categorization */
  last_error_type: string;
}

/**
 * Response model for dead-letter delete operation.
 */
export interface DeleteDeadLetterResponse {
  /** Document ID targeted for deletion */
  doc_id: string;
  /** True if entry was deleted, False if not found */
  deleted: boolean;
}

export type DeleteDocumentStoreIfExistsResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface DeleteDocumentStoreIfExistsResult {
  step?: 'delete_document_store_if_exists';
  result: DeleteDocumentStoreIfExistsResultResult;
}

export type DeleteOrgChunksResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface DeleteOrgChunksResult {
  step?: 'delete_org_chunks';
  result: DeleteOrgChunksResultResult;
}

export type DeleteOrgDocumentsResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface DeleteOrgDocumentsResult {
  step?: 'delete_org_documents';
  result: DeleteOrgDocumentsResultResult;
}

export type DeleteOrgIfExistsResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface DeleteOrgIfExistsResult {
  step?: 'delete_org_if_exists';
  result: DeleteOrgIfExistsResultResult;
}

export type DeprovisionOrgRolesResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface DeprovisionOrgRolesResult {
  step?: 'deprovision_org_roles';
  result: DeprovisionOrgRolesResultResult;
}

export interface DepthBucket {
  /** Bucket label, e.g. '1', '2-5', '6-10', '11+' */
  label: string;
  /** Number of threads in this bucket */
  count: number;
}

/**
 * Migration target. Only ``christmas`` ships in this slice; ``smartly``
is a future destination named in the LADR.
 */
export type DestinationSystem = typeof DestinationSystem[keyof typeof DestinationSystem];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const DestinationSystem = {
  christmas: 'christmas',
} as const;

export type DocVersionStatus = typeof DocVersionStatus[keyof typeof DocVersionStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const DocVersionStatus = {
  draft: 'draft',
  pending_review: 'pending_review',
  activating: 'activating',
  active: 'active',
  superseded: 'superseded',
  rejected: 'rejected',
  failed: 'failed',
} as const;

export interface DocumentHealthSummary {
  /** Total org documents */
  total_documents: number;
  /** Documents with completed processing */
  completed: number;
  /** Documents pending processing */
  pending: number;
  /** Documents with failed processing */
  failed: number;
}

/**
 * Organization name if available
 */
export type DocumentSourceCountOrgName = string | null;

/**
 * Document name if available
 */
export type DocumentSourceCountDocName = string | null;

export interface DocumentSourceCount {
  /** Organization identifier */
  org_id: string;
  /** Organization category (public/internal/personal) */
  org_category: string;
  /** Organization name if available */
  org_name?: DocumentSourceCountOrgName;
  /** Document identifier */
  doc_id: string;
  /** Document name if available */
  doc_name?: DocumentSourceCountDocName;
  /** Number of threads citing this document */
  reference_count: number;
}

export interface DocumentSourcesResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Documents sorted by citation count descending */
  sources: DocumentSourceCount[];
}

export interface EmailCreateInvitationResponse {
  invitation_id: string;
  created_at: string;
  sent_to_email: string;
}

export type EntityRecordPatchCanonicalEntitiesItem = PatchCompanyShellRow | PatchWorkPatternRow | PatchImportRow;

/**
 * Request DTO — ``PATCH .../entities/{entity_type}`` body (Step-2 fix-up).

``canonical_entities`` is a **full-list replace**, not a partial patch —
the caller resubmits the corrected canonical rows in their entirety, and
``WorkloadsService.patch_entity_record`` swaps the entity record's whole
list before re-validating. Accepts ``PatchCompanyShellRow``,
``PatchWorkPatternRow``, or ``PatchImportRow`` (strict variants); the
service validates against the registered ``EntityValidatorPort`` for the
path's ``entity_type``.
 */
export interface EntityRecordPatch {
  /**
   * Full-list replace of canonical rows. Capped at 500 rows (same bound as Track-1 import validation) so oversized payloads fail fast with 422.
   * @maxItems 500
   */
  canonical_entities: EntityRecordPatchCanonicalEntitiesItem[];
}

export type EntityRecordRefRunId = string | null;

/**
 * Pointer to held canonical state in ``onboarding_records`` (same partition).

Track 2 provision inputs pass this reference instead of inlining
``canonical_entities`` (LADR-039 Entity record reference). ``run_id``
ties provision reads to the ``MigrationRun.pushed_versions`` snapshot
when set.
 */
export interface EntityRecordRef {
  workload_id: string;
  entity_type: EntityType;
  run_id?: EntityRecordRefRunId;
}

/**
 * LADR-039 entity-record state machine (Slice 3).

Transitions (see LADR-039 mermaid diagram):
``[*] -> DRAFT`` on ingest (``map_source`` upserts the entity);
``DRAFT -> VALIDATED`` / ``DRAFT -> VALIDATION_FAILED`` on
``POST .../validations`` depending on the report; ``VALIDATION_FAILED ->
DRAFT`` or ``-> VALIDATED`` per subsequent edit/validate; ``VALIDATED ->
VALIDATION_FAILED`` when a ``PATCH`` re-introduces a blocking error.
``PATCH`` alone never promotes ``DRAFT -> VALIDATED`` — only an explicit
``validate_entity`` call does.
 */
export type EntityRecordStatus = typeof EntityRecordStatus[keyof typeof EntityRecordStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EntityRecordStatus = {
  draft: 'draft',
  validated: 'validated',
  validation_failed: 'validation_failed',
} as const;

export type EntityRecordViewCanonicalEntitiesAnyOfItem = CompanyShellRow | WorkPatternRow | ImportRow;

export type EntityRecordViewCanonicalEntities = EntityRecordViewCanonicalEntitiesAnyOfItem[] | null;

export type EntityRecordViewValidation = ValidationReport | null;

/**
 * Read DTO — ``GET``/``PATCH .../entities/{entity_type}`` response.

Mirrors the persisted ``OnboardingEntityRecord`` but omits ``etag`` — the
optimistic-concurrency token is surfaced as the ``ETag`` response header
(LADR-039 contract) rather than in the response body, so a caller cannot
forget to carry it forward as an opaque header value on the next
``If-Match``.
 */
export interface EntityRecordView {
  id: string;
  workloadId: string;
  entity_type: EntityType;
  status: EntityRecordStatus;
  manual_edit_count: number;
  canonical_entities?: EntityRecordViewCanonicalEntities;
  validation?: EntityRecordViewValidation;
  source_metadata: SourceMetadata;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

/**
 * Tier 2 entity kind stored in the ``onboarding_records`` container.

Enum members for config + employee types are declared here so Slice 5/6
PRs can register adapters additively. Only ``company_shell`` has a
source-import / validator vertical today; unmapped types fail with a
clear "no importer/validator registered" error at the service boundary.

``pay_groups`` is intentionally absent — pay groups are provisioned
inside ``company_shell`` (LADR-039 Slice 5 decision). ``pay_run_details``
remains Slice 6+ (shape TBD).
 */
export type EntityType = typeof EntityType[keyof typeof EntityType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EntityType = {
  company_shell: 'company_shell',
  work_patterns: 'work_patterns',
  pay_components: 'pay_components',
  employees: 'employees',
  historic_leave_earnings: 'historic_leave_earnings',
} as const;

export interface Error {
  response_kind?: 'error';
  reason: string;
}

export type EvalAnnotationReviewerNotes = string | null;

export type EvalAnnotationReviewerAssigned = string | null;

export type EvalAnnotationSanitisedBy = string | null;

export type EvalAnnotationSanitisedAt = string | null;

export type EvalAnnotationSanitisationNotes = string | null;

export type EvalAnnotationExportedAt = string | null;

export type EvalAnnotationExportedToGoldenId = string | null;

/**
 * Steward-facing review annotation on a production artifact.

Stored in the shared CosmosDB ``evals`` container with
``type = "eval_annotation"`` and partitioned by
``source_ref.workspace_id`` (annotation cardinality follows tenants
reviewed). See ``app/eval/AGENTS.md`` Persistence section.

``annotation_id`` follows the ``ann_{ulid}`` shape documented in
``AGENTS.md``. Generation lives in the persistence adapter (plan
Step 6); the schema only enforces the prefix so the stored shape
stays consistent.

Sanitisation is the load-bearing privacy control. Setting
``sanitised = True`` requires both ``sanitised_by`` and
``sanitised_at`` so an audit trail always exists for any production-
derived row promoted toward the golden set. Export is gated on
``sanitised == True`` by the export service (plan Step 32); the
schema only enforces that ``exported`` and the ``EXPORTED`` status
are consistent.
 */
export interface EvalAnnotation {
  type?: 'eval_annotation';
  agent_type?: AgentType;
  annotation_id: string;
  source_ref: ThreadSourceRef;
  status?: EvalAnnotationStatus;
  failure_mode_tags?: EvalFailureModeTag[];
  reviewer_notes?: EvalAnnotationReviewerNotes;
  reviewer_assigned?: EvalAnnotationReviewerAssigned;
  sanitised?: boolean;
  sanitised_by?: EvalAnnotationSanitisedBy;
  sanitised_at?: EvalAnnotationSanitisedAt;
  sanitisation_notes?: EvalAnnotationSanitisationNotes;
  exported?: boolean;
  exported_at?: EvalAnnotationExportedAt;
  exported_to_golden_id?: EvalAnnotationExportedToGoldenId;
  created_at: string;
  updated_at: string;
}

/**
 * Opaque token for the next page; ``null`` when no further pages remain.
 */
export type EvalAnnotationListResponseContinuationToken = string | null;

/**
 * Paginated response for the annotation list endpoints.

Mirrors the platform-analytics ``FeedbackReviewListResponse`` envelope
so the admin UI can drive both surfaces with the same paging
primitive. Stable sort: ``created_at`` ascending, ties broken by
``annotation_id`` ascending — pinned at the repository so the
response order is independent of the underlying adapter.
 */
export interface EvalAnnotationListResponse {
  /** Annotation rows for the current page. */
  items: EvalAnnotation[];
  /** Opaque token for the next page; ``null`` when no further pages remain. */
  continuation_token?: EvalAnnotationListResponseContinuationToken;
}

/**
 * Lifecycle status of an annotation.

Allowed transitions (enforced at the service layer in plan Step 31):

- ``PENDING`` → ``IN_REVIEW`` when a reviewer claims the row.
- ``IN_REVIEW`` → ``APPROVED`` when the reviewer marks the row useful.
- ``IN_REVIEW`` → ``REJECTED`` when the reviewer marks it not useful.
- ``APPROVED`` → ``EXPORTED`` when the row has been exported as a
  draft golden candidate.

``REJECTED`` and ``EXPORTED`` are terminal. The schema only enforces
invariants between ``status`` and the ``exported`` flag; finer
transition rules belong to the service.
 */
export type EvalAnnotationStatus = typeof EvalAnnotationStatus[keyof typeof EvalAnnotationStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EvalAnnotationStatus = {
  pending: 'pending',
  in_review: 'in_review',
  approved: 'approved',
  rejected: 'rejected',
  exported: 'exported',
} as const;

/**
 * Reviewer-attached tag describing why a row is interesting.

Tags are advisory only — they do not drive scoring. The set is
intentionally small in v1; new tags can be added without a schema
version bump because consumers are tolerant of unknown enum values
on read paths only at their own discretion (Pydantic itself rejects
unknown values, which is the correct default for the steward UI).
 */
export type EvalFailureModeTag = typeof EvalFailureModeTag[keyof typeof EvalFailureModeTag];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EvalFailureModeTag = {
  wrong_doc_retrieved: 'wrong_doc_retrieved',
  insufficient_recall: 'insufficient_recall',
  wrong_jurisdiction: 'wrong_jurisdiction',
  hallucinated_citation: 'hallucinated_citation',
  missing_clarification: 'missing_clarification',
  incorrect_answer: 'incorrect_answer',
  ambiguous_question: 'ambiguous_question',
  other: 'other',
} as const;

/**
 * Evaluation state of a capability's implementation (LADR-030).
 */
export type EvalStatus = typeof EvalStatus[keyof typeof EvalStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EvalStatus = {
  unevaluated: 'unevaluated',
  passing: 'passing',
  failing: 'failing',
} as const;

/**
 * Document name if available
 */
export type FeedbackCitationSummaryDocName = string | null;

/**
 * Compact citation projection for the list row.

Only `doc_id` granularity is exposed because chunk_id is a content
hash that changes across re-ingest (see LADR-027 / Step 3 of #729).
 */
export interface FeedbackCitationSummary {
  /** Organization category (public/internal/personal) */
  org_category: string;
  /** Organization identifier */
  org_id: string;
  /** Document identifier (stable across chunk re-hashing) */
  doc_id: string;
  /** Document name if available */
  doc_name?: FeedbackCitationSummaryDocName;
}

/**
 * Stable reference to a single feedback event on an assistant message.

The same triple is reused as the source ref for eval annotations so
review decisions can be joined back to the underlying thread message
without extending the thread schema.
 */
export interface FeedbackItemRef {
  /** Workspace identifier */
  workspace_id: string;
  /** Thread identifier */
  thread_id: string;
  /**
   * Zero-based index of the assistant message within the thread that received the feedback
   * @minimum 0
   */
  assistant_message_index: number;
}

/**
 * Inclusive ISO 8601 lower bound on thread creation timestamp
 */
export type FeedbackReviewFilterPeriodStart = string | null;

/**
 * Inclusive ISO 8601 upper bound on thread creation timestamp
 */
export type FeedbackReviewFilterPeriodEnd = string | null;

/**
 * Optional case-insensitive substring filter applied to the question text
 */
export type FeedbackReviewFilterSearchText = string | null;

/**
 * Opaque pagination token returned by a prior page
 */
export type FeedbackReviewFilterContinuationToken = string | null;

/**
 * Filter / pagination inputs for the feedback review list.

All filters are optional and default to "no restriction" so a bare
request returns the most recent feedback rows across the platform.
 */
export interface FeedbackReviewFilter {
  /** Inclusive ISO 8601 lower bound on thread creation timestamp */
  period_start?: FeedbackReviewFilterPeriodStart;
  /** Inclusive ISO 8601 upper bound on thread creation timestamp */
  period_end?: FeedbackReviewFilterPeriodEnd;
  /** Feedback values to include; empty list means both thumbs_up and thumbs_down */
  feedback_values?: FeedbackValue[];
  /** Restrict to these workspace ids; empty list means all workspaces */
  workspace_ids?: string[];
  /** Restrict to threads referencing any of these knowledge orgs (KB filter); empty list means all */
  org_ids?: string[];
  /** Restrict to these jurisdictions (e.g. 'nz', 'au-federal', 'au-nsw'); empty list means all */
  jurisdictions?: string[];
  /** Optional case-insensitive substring filter applied to the question text */
  search_text?: FeedbackReviewFilterSearchText;
  /**
   * Maximum number of rows to return in a single page
   * @minimum 1
   * @maximum 500
   */
  page_size?: number;
  /** Opaque pagination token returned by a prior page */
  continuation_token?: FeedbackReviewFilterContinuationToken;
}

/**
 * Opaque token for the next page; None when no further pages remain
 */
export type FeedbackReviewListResponseContinuationToken = string | null;

/**
 * Paginated response for the feedback review list.
 */
export interface FeedbackReviewListResponse {
  /** Echo of the filter used to produce this page */
  filter: FeedbackReviewFilter;
  /** Feedback rows for the current page */
  rows: FeedbackReviewRow[];
  /** Opaque token for the next page; None when no further pages remain */
  continuation_token?: FeedbackReviewListResponseContinuationToken;
  /** Number of rows returned in this page */
  total_returned: number;
}

/**
 * Thread title
 */
export type FeedbackReviewRowThreadTitle = string | null;

/**
 * Timestamp of the user question (ISO 8601)
 */
export type FeedbackReviewRowUserTimestamp = string | null;

/**
 * Timestamp of the assistant answer (ISO 8601)
 */
export type FeedbackReviewRowAssistantTimestamp = string | null;

/**
 * Assistant answer text flattened to markdown (truncated for list display)
 */
export type FeedbackReviewRowAnswerPreview = string | null;

/**
 * Best-effort jurisdiction tag derived from knowledge_orgs (e.g. 'nz', 'au-federal'); None if undetermined
 */
export type FeedbackReviewRowJurisdiction = string | null;

/**
 * Original answer schema version for forward-compat handling
 */
export type FeedbackReviewRowAnswerSchemaVersion = number | null;

/**
 * Most recent eval annotation status for this item, if any (see EvalAnnotation schema)
 */
export type FeedbackReviewRowAnnotationStatus = string | null;

/**
 * A single feedback-bearing Q&A row for admin review.

Contains enough context for an internal admin to decide whether the
row is a useful golden candidate without immediately drilling into
the trace projection (Step 28). Sensitive fields (full answer text)
are intentionally truncated; full content lives behind the trace
endpoint where PII highlight spans are computed.
 */
export interface FeedbackReviewRow {
  /** Stable reference to this feedback event */
  item_ref: FeedbackItemRef;
  /** Feedback value recorded by the user */
  feedback_value: FeedbackValue;
  /** Workspace identifier */
  workspace_id: string;
  /** Thread identifier */
  thread_id: string;
  /** Thread title */
  thread_title?: FeedbackReviewRowThreadTitle;
  /** Thread creation timestamp (ISO 8601) */
  thread_created_at: string;
  /** Timestamp of the user question (ISO 8601) */
  user_timestamp?: FeedbackReviewRowUserTimestamp;
  /** Timestamp of the assistant answer (ISO 8601) */
  assistant_timestamp?: FeedbackReviewRowAssistantTimestamp;
  /** User question text (may be truncated for list display) */
  question_preview: string;
  /** Assistant answer text flattened to markdown (truncated for list display) */
  answer_preview?: FeedbackReviewRowAnswerPreview;
  /** Knowledge orgs attached to this thread at completion time */
  knowledge_orgs?: OrgKey[];
  /** Best-effort jurisdiction tag derived from knowledge_orgs (e.g. 'nz', 'au-federal'); None if undetermined */
  jurisdiction?: FeedbackReviewRowJurisdiction;
  /** Document-granularity citations on the assistant answer */
  citations?: FeedbackCitationSummary[];
  /** Original answer schema version for forward-compat handling */
  answer_schema_version?: FeedbackReviewRowAnswerSchemaVersion;
  /** Most recent eval annotation status for this item, if any (see EvalAnnotation schema) */
  annotation_status?: FeedbackReviewRowAnnotationStatus;
}

/**
 * Thumbs up / total, null if no feedback
 */
export type FeedbackSummaryResponsePositiveRatio = number | null;

export interface FeedbackSummaryResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Total positive feedback count */
  thumbs_up: number;
  /** Total negative feedback count */
  thumbs_down: number;
  /** Total messages that received feedback */
  total_with_feedback: number;
  /** Thumbs up / total, null if no feedback */
  positive_ratio?: FeedbackSummaryResponsePositiveRatio;
}

/**
 * Thread title
 */
export type FeedbackTraceResponseThreadTitle = string | null;

/**
 * Best-effort jurisdiction tag derived from knowledge_orgs; None if undetermined
 */
export type FeedbackTraceResponseJurisdiction = string | null;

/**
 * Most recent eval annotation summary for this item, if any
 */
export type FeedbackTraceResponseAnnotation = TraceAnnotationSummary | null;

/**
 * Original answer schema version for forward-compat handling
 */
export type FeedbackTraceResponseAnswerSchemaVersion = number | null;

/**
 * Targeted conversation trace projection for a single feedback item.

This is the response surface for `GET /platform-analytics/
feedback-items/{...}/trace` (Step 30). It carries enough context
for an admin reviewer to read the selected Q&A in full, see
citations and attached KBs, view annotation state, optionally drill
out to Honeycomb, and triage informational PII highlights — all
without leaving the admin surface in Pacific.Agentic.Web.

`selected_assistant_message_index` always points at an
assistant-role message in `messages`; the user prompt that produced
the answer immediately precedes it in conversation order. The
schema does not enforce that constraint structurally because the
underlying thread shape sometimes contains tool-only responses or
interleaved turns from streamed sessions; the service (Step 29)
validates the projection and raises before constructing this model
when the index is incoherent.
 */
export interface FeedbackTraceResponse {
  /** Stable reference to the feedback event being reviewed */
  item_ref: FeedbackItemRef;
  /** Feedback value recorded by the user */
  feedback_value: FeedbackValue;
  /** Workspace identifier */
  workspace_id: string;
  /** Thread identifier */
  thread_id: string;
  /** Thread title */
  thread_title?: FeedbackTraceResponseThreadTitle;
  /** Thread creation timestamp (ISO 8601) */
  thread_created_at: string;
  /** Knowledge orgs attached to this thread at completion time */
  knowledge_orgs?: OrgKey[];
  /** Best-effort jurisdiction tag derived from knowledge_orgs; None if undetermined */
  jurisdiction?: FeedbackTraceResponseJurisdiction;
  /** Ordered conversation messages projected from thread.messages */
  messages: TraceMessage[];
  /**
   * Index into `messages` of the assistant message that received the feedback
   * @minimum 0
   */
  selected_assistant_message_index: number;
  /** Most recent eval annotation summary for this item, if any */
  annotation?: FeedbackTraceResponseAnnotation;
  /** Optional deep links to infrastructure observability (Honeycomb) */
  observability?: TraceObservabilityLinks;
  /** Original answer schema version for forward-compat handling */
  answer_schema_version?: FeedbackTraceResponseAnswerSchemaVersion;
}

/**
 * Filterable feedback values for the review surface.
 */
export type FeedbackValue = typeof FeedbackValue[keyof typeof FeedbackValue];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const FeedbackValue = {
  thumbs_up: 'thumbs_up',
  thumbs_down: 'thumbs_down',
} as const;

export type FscComponentDetailStartDate = string | null;

export type FscComponentDetailEndDate = string | null;

export type FscComponentDetailInputAmount = number | null;

export type FscComponentDetailOutputGrossValue = number | null;

/**
 * FSC component detail row for paypacket-level comparisons.
 */
export interface FscComponentDetail {
  fsc_code: string;
  fsc_name: string;
  fsc_type: string;
  source: string;
  start_date?: FscComponentDetailStartDate;
  end_date?: FscComponentDetailEndDate;
  input_amount?: FscComponentDetailInputAmount;
  output_gross_value?: FscComponentDetailOutputGrossValue;
}

/**
 * Output containing Sankey diagram data.
 */
export interface GenerateSankeyOutput {
  /** Batch identifier */
  shadow_run_id: string;
  /** Nodes in the Sankey diagram */
  nodes?: SankeyNode[];
  /** Links (flows) in the Sankey diagram */
  links?: SankeyLink[];
  /** Human-readable summary of the cascade analysis */
  summary?: string;
  /**
   * Total variance packets included
   * @minimum 0
   */
  total_variance_packets?: number;
  /** Top source components for variances */
  top_variance_sources?: string[];
}

export type GenericPayloadWorkflow = string | null;

/**
 * Fallback for AgentWorkflow - allows any fields for generic execution.
 */
export interface GenericPayload {
  workflow?: GenericPayloadWorkflow;
  [key: string]: unknown;
 }

export interface GrowthDataPoint {
  /** Date bucket (ISO 8601 date) */
  date: string;
  /** Workspaces provisioned on this date */
  new_workspaces: number;
  /** Running total of workspaces */
  cumulative_workspaces: number;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

export type ImportAcceptedWorkflowId = string | null;

/**
 * Response DTO — ``202 Accepted`` body for a newly created import job.
 */
export interface ImportAccepted {
  import_id: string;
  workflow_id?: ImportAcceptedWorkflowId;
  status: JobStatus;
}

/**
 * Which branch of the workflow to run.
 */
export type ImportMode = typeof ImportMode[keyof typeof ImportMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ImportMode = {
  validate: 'validate',
  commit: 'commit',
} as const;

export type ImportResultStatus = typeof ImportResultStatus[keyof typeof ImportResultStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ImportResultStatus = {
  needs_mapping: 'needs_mapping',
  invalid: 'invalid',
  valid: 'valid',
  committed: 'committed',
} as const;

/**
 * Result of a pay component import operation.

status:
  - "needs_mapping"  — one or more elements have no saved company mapping;
                       the wizard should route to mapping setup (not an error)
  - "invalid"        — blocking pre-flight validation errors; no rows submitted
  - "valid"          — dry-run only: all pre-flight checks passed, nothing was
                       written; the wizard shows the review screen before commit
  - "committed"      — all pre-flight checks passed; rows submitted to Christmas
 */
export interface ImportResult {
  status: ImportResultStatus;
  committed: number;
  failed: number;
  errors: ImportValidationError[];
  unmapped: UnmappedElement[];
  mappingsSaved: number;
}

export type ImportRowCompanyComponentId = string | null;

export type ImportRowColumns = {[key: string]: string};

export type ImportRowStartDate = string | null;

export type ImportRowEndDate = string | null;

/**
 * A single parsed row from the import CSV (internal domain model).

This is the *internal* representation produced by the router from CSV data.
It carries raw ``columns`` (column → value mapping) which are resolved to
``parameterValues`` later by the service via the mapping index + discovery
data. It does **not** carry ``parameterValues`` directly.

Distinct from :class:`ImportPayComponentRow` (the wire format sent to
Christmas).
 */
export interface ImportRow {
  rowIndex: number;
  companyCode: string;
  employeeCode: string;
  componentName: string;
  companyComponentId?: ImportRowCompanyComponentId;
  columns: ImportRowColumns;
  startDate?: ImportRowStartDate;
  endDate?: ImportRowEndDate;
  rowMode?: RowMode;
}

/**
 * A blocking validation error for a single import row.
 */
export interface ImportValidationError {
  row: number;
  employeeCode: string;
  componentName: string;
  error: string;
}

export type ImportViewWorkflowId = string | null;

export type ImportViewOutcomeSummary = string | null;

export type ImportViewStartedAt = string | null;

export type ImportViewCompletedAt = string | null;

/**
 * Read DTO — ``GET .../imports/{import_id}`` response.

Mirrors the persisted ``Import`` job document but omits
``source_artifact_ref`` — an internal blob-storage detail that must never
reach an externally-facing surface (LADR-041/042 PII posture keeps
staging plumbing off the wire even though this endpoint is currently
Datacom-internal-only).
 */
export interface ImportView {
  import_id: string;
  workloadId: string;
  entity_type: EntityType;
  status: JobStatus;
  workflow_id?: ImportViewWorkflowId;
  source_metadata: SourceMetadata;
  outcome_summary?: ImportViewOutcomeSummary;
  started_at?: ImportViewStartedAt;
  completed_at?: ImportViewCompletedAt;
  started_by: string;
}

export type InternalOrgCreateJurisdiction = 'nz' | 'au-federal' | 'au-nsw' | 'au-vic' | 'au-qld' | 'au-wa' | 'au-sa' | 'au-tas' | 'au-act' | 'au-nt' | 'au-modern-awards' | 'nz' | 'au' | null;

export interface InternalOrgCreate {
  id: string;
  category?: 'internal';
  name: string;
  creator_user_id: string;
  jurisdiction?: InternalOrgCreateJurisdiction;
}

/**
 * Status of an agent invocation workflow execution.
 */
export type InvocationStatusEnum = typeof InvocationStatusEnum[keyof typeof InvocationStatusEnum];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const InvocationStatusEnum = {
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  TERMINATED: 'TERMINATED',
} as const;

/**
 * Lifecycle status of an ``Import`` / ``Validation`` job document.
 */
export type JobStatus = typeof JobStatus[keyof typeof JobStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const JobStatus = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

export interface LineGraph {
  element_kind?: 'linegraph';
  labels: string[];
  datasets: Dataset[];
}

export interface LiveHealthyResponse {
  status_kind?: 'healthy';
  version: string;
}

export interface LiveUnhealthyResponse {
  status_kind?: 'unhealthy';
  version: string;
  failures: string[];
}

export interface ManualCreateInvitationResponse {
  invitation_id: string;
  created_at: string;
  one_time_link: string;
}

export type MappingEntryComponentName = string | null;

export type MappingEntryParameterQualifiedName = string | null;

/**
 * A single column-to-parameter mapping submitted from the portal mapping UI.

When ``componentName`` is set the mapping applies only to that component.
When ``componentName`` is null the mapping is element-level (legacy cascade):
it applies to every component built on the same element until a
component-specific override is saved.

The parameter is identified by:
  - `parameterQualifiedName` for bucketed slots (element-relative, stable), or
  - `parameterDisplayName` for no-bucket-storage slots (Bank Account Number,
    Employee Particulars, Employee Reference, …) whose qualified name is null.
`parameterDisplayName` is always populated (it is what the UI shows and is the
fallback identifier when `parameterQualifiedName` is null).
 */
export interface MappingEntry {
  componentName?: MappingEntryComponentName;
  elementQualifiedName: string;
  parameterQualifiedName?: MappingEntryParameterQualifiedName;
  parameterDisplayName?: string;
  columnName: string;
}

export type MappingScopeKeyComponentName = string | null;

/**
 * Identifies a (componentName, element) scope whose mappings should be replaced.

Used by the Map Columns save payload when every column for a scope is skipped:
the scope must still be submitted so previously saved mappings are cleared.
 */
export interface MappingScopeKey {
  componentName?: MappingScopeKeyComponentName;
  elementQualifiedName: string;
}

/**
 * Body for the exported PATCH.

``exported_to_golden_id`` is the draft golden row's ID written by
the export pipeline. The service rejects empty strings so the
schema-level audit trail is always populated.
 */
export interface MarkExportedRequest {
  /** @minLength 1 */
  exported_to_golden_id: string;
}

export type MarkSanitisedRequestSanitisationNotes = string | null;

/**
 * Body for the sanitisation PATCH.

``sanitised_by`` carries the reviewer's identity for the audit
trail; the service rejects empty strings.
 */
export interface MarkSanitisedRequest {
  /** @minLength 1 */
  sanitised_by: string;
  sanitisation_notes?: MarkSanitisedRequestSanitisationNotes;
}

export interface Markdown {
  element_kind?: 'markdown';
  markdown: string;
}

/**
 * Match rate classification for shadow run list display.
 */
export type MatchRateStatus = typeof MatchRateStatus[keyof typeof MatchRateStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MatchRateStatus = {
  excellent: 'excellent',
  good: 'good',
  warning: 'warning',
  critical: 'critical',
} as const;

/**
 * A single time-series bucket for MCP interactions.
 */
export interface McpInteractionDataPoint {
  /** Start of the bucket (ISO 8601 date, inclusive) */
  period_start: string;
  /** End of the bucket (ISO 8601 date, inclusive) */
  period_end: string;
  /** Total MCP interaction count in this bucket */
  interactions: number;
}

/**
 * Time-bucketed MCP interaction counts.
 */
export interface McpInteractionTimeseriesResponse {
  /** Start of the queried range (ISO 8601) */
  period_start: string;
  /** End of the queried range (ISO 8601) */
  period_end: string;
  /** Granularity of each data point */
  period: Period;
  /** Time-series data points ordered chronologically */
  data_points: McpInteractionDataPoint[];
  /** Sum of all interactions across the entire range */
  total_interactions: number;
}

/**
 * MCP interaction counts grouped by platform user.
 */
export interface McpInteractionsPerUserResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Per-user interaction counts */
  users: McpUserInteractionCount[];
  /** Sum of all interactions across users */
  total_interactions: number;
}

/**
 * Summary statistics for MCP interactions.
 */
export interface McpOverviewResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Total MCP interactions in period */
  total_interactions: number;
  /** Unique platform users (via created_by) */
  unique_users: number;
  /** Average interactions per user */
  avg_interactions_per_user: number;
}

/**
 * Per-user MCP interaction count.
 */
export interface McpUserInteractionCount {
  /** Platform user_id who created the API key */
  user_id: string;
  /** Number of MCP interactions */
  interaction_count: number;
}

/**
 * A single hourly velocity bucket for MCP interactions.
 */
export interface McpVelocityDataPoint {
  /** Start of the bucket (ISO 8601 datetime) */
  period_start: string;
  /** End of the bucket (ISO 8601 datetime) */
  period_end: string;
  /** Interaction rate per hour in this bucket */
  interactions_per_hour: number;
}

/**
 * ISO 8601 datetime of highest interaction rate
 */
export type McpVelocityResponsePeakHour = string | null;

/**
 * MCP interaction velocity (interactions per hour) over time.
 */
export interface McpVelocityResponse {
  /** Start of the queried range (ISO 8601) */
  period_start: string;
  /** End of the queried range (ISO 8601) */
  period_end: string;
  /** Granularity of each data point */
  period: Period;
  /** Time-series velocity data points */
  data_points: McpVelocityDataPoint[];
  /** ISO 8601 datetime of highest interaction rate */
  peak_hour?: McpVelocityResponsePeakHour;
  /** Average interactions per hour across entire range */
  avg_per_hour: number;
}

export type MigrationRunCreateEntityTypes = EntityType[] | null;

/**
 * Product-routing target for the push (orthogonal to target_environment). Required for Slice-4 orchestration — omitting it fails with HTTP 422 (RunNotOrchestratableError) before the run is persisted.
 */
export type MigrationRunCreateMypayEnvironment = MyPayEnvironment | null;

/**
 * Request DTO — ``POST .../runs`` body (Tier 3 migration-run push).

``entity_types`` is optional per the LADR-039 API table: omitting it
selects the "full orchestration profile" —
``WorkloadsService.create_run`` resolves it to every entity type that
currently has an ingested ``OnboardingEntityRecord`` for the workload,
rather than a hardcoded single value, so this stays correct as
``EntityType`` grows beyond ``company_shell``.
 */
export interface MigrationRunCreate {
  target_environment: TargetEnvironment;
  entity_types?: MigrationRunCreateEntityTypes;
  /** Product-routing target for the push (orthogonal to target_environment). Required for Slice-4 orchestration — omitting it fails with HTTP 422 (RunNotOrchestratableError) before the run is persisted. */
  mypay_environment?: MigrationRunCreateMypayEnvironment;
}

/**
 * Lifecycle status of a `MigrationRun` (LADR-039 migration-run state machine).

``SUCCEEDED`` and ``FAILED`` are terminal. A non-terminal run (``QUEUED``
or ``RUNNING``) triggers the write freeze on sibling mutation endpoints
(``PATCH .../entities``, ``POST .../imports``, ``POST .../validations``)
and the at-most-one-non-terminal-run invariant on ``POST .../runs``.
 */
export type MigrationRunStatus = typeof MigrationRunStatus[keyof typeof MigrationRunStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MigrationRunStatus = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

/**
 * Public discovery shape — environment slug and display name only.
 */
export interface MyPayEndpointSummary {
  environment: MyPayEnvironment;
  name: string;
}

/**
 * Datapay environment slug for MyPay API deployment routing.
 */
export type MyPayEnvironment = typeof MyPayEnvironment[keyof typeof MyPayEnvironment];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MyPayEnvironment = {
  'nz-easipay': 'nz-easipay',
  'nz-datapay': 'nz-datapay',
  'nz-enterprise': 'nz-enterprise',
} as const;

export interface NotReadyResponse {
  status_kind?: 'not_ready';
  version: string;
  dependencies: string[];
}

/**
 * Who the notification is targeted at.

`org_admin` — admins of the owning `Org` (resolved via `UserOrgRole`).
`platform_admin` — platform admins, used for `public/*` sources whose
audience is the platform team rather than a tenant org.
 */
export type NotificationAudience = typeof NotificationAudience[keyof typeof NotificationAudience];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NotificationAudience = {
  org_admin: 'org_admin',
  platform_admin: 'platform_admin',
} as const;

export type NotificationEventOrgCategory = typeof NotificationEventOrgCategory[keyof typeof NotificationEventOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NotificationEventOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type NotificationEventSourceId = string | null;

export type NotificationEventBaseDocId = string | null;

export type NotificationEventVersion = string | null;

export type NotificationEventCorrelationId = string | null;

export type NotificationEventDetailsAnyOf = {[key: string]: string};

export type NotificationEventDetails = NotificationEventDetailsAnyOf | null;

/**
 * Immutable HOTL notification record.

Persisted to the `notifications` container partitioned by `org_id`.
Per-user read state lives separately in `NotificationRead`.
 */
export interface NotificationEvent {
  notification_id: string;
  timestamp: string;
  event_type: NotificationEventType;
  severity: NotificationSeverity;
  audience: NotificationAudience;
  org_id: string;
  org_category: NotificationEventOrgCategory;
  source_id?: NotificationEventSourceId;
  base_doc_id?: NotificationEventBaseDocId;
  version?: NotificationEventVersion;
  correlation_id?: NotificationEventCorrelationId;
  details?: NotificationEventDetails;
}

/**
 * Notification event types per LADR-026 §Change 4.

Six event types span source-registry actions and document-version
transitions. Mirrors the audit trail's append-only event catalogue.
 */
export type NotificationEventType = typeof NotificationEventType[keyof typeof NotificationEventType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NotificationEventType = {
  versionauto_approved: 'version.auto_approved',
  versionpending_review: 'version.pending_review',
  versionauto_approval_failed: 'version.auto_approval_failed',
  sourceerror: 'source.error',
  sourcecreated: 'source.created',
  sourceauto_approve_changed: 'source.auto_approve_changed',
} as const;

/**
 * Severity hints for UI rendering.

`informational` events report something that happened (e.g. auto-approved).
`actionable` events flag something an admin must look at (e.g. pending review).
 */
export type NotificationSeverity = typeof NotificationSeverity[keyof typeof NotificationSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NotificationSeverity = {
  informational: 'informational',
  actionable: 'actionable',
} as const;

/**
 * Worker UUID if the instruction targets a specific worker
 */
export type OmniResolvedIdsWorkerId = string | null;

/**
 * Pay period ID if the instruction targets a specific period
 */
export type OmniResolvedIdsPayPeriodId = string | null;

/**
 * Entity IDs pre-resolved by Omni before invoking the validation workflow.

Omni validates these IDs exist in the source system (Pacific.Pay /
Pacific.Maintenance) before calling the Agentic API. The scope-assertion
activity verifies `company_id == OmniValidationInput.org_id` to prevent
cross-tenant data access.
 */
export interface OmniResolvedIds {
  /** Company UUID — must equal OmniValidationInput.org_id */
  company_id: string;
  /** Worker UUID if the instruction targets a specific worker */
  worker_id?: OmniResolvedIdsWorkerId;
  /** Pay period ID if the instruction targets a specific period */
  pay_period_id?: OmniResolvedIdsPayPeriodId;
}

/**
 * Input for the OmniRequestValidation Workflow.

Triggers AI-powered validation of a natural-language payroll instruction
against real Pacific.Pay / Pacific.Leave / Pacific.Maintenance data and
NZ Holidays Act knowledge. The workflow never mutates; it validates and
optionally proposes a single write-tool call for human approval.
 */
export interface OmniValidationInput {
  /** Workflow discriminator */
  workflow?: 'omni_request_validation';
  /** Natural-language payroll instruction to validate */
  instruction: string;
  /** Company UUID — used for SA-org scope assertion */
  org_id: string;
  /** Entity IDs pre-resolved by Omni before invoking the workflow */
  resolved_ids: OmniResolvedIds;
}

export type OnboardingWorkloadCompanyCode = string | null;

export type OnboardingWorkloadSourceSystem = SourceSystem | null;

/**
 * Tier 1 header document — one per customer company migration project.

One per ``/workloadId`` partition, stored with ``id: "workload"``; the
project UUID is always ``workload_id`` (the partition key), never ``id``.
``workload_id`` serialises to the partition-key field ``workloadId`` so the
CosmosDB adapter can persist/query this model directly.

``aggregate_status`` is deliberately absent — it is computed on read and
belongs on the API ``WorkloadView`` only (LADR-039 Slice 5), never stored here.
 */
export interface OnboardingWorkload {
  id?: 'workload';
  workloadId: string;
  workspace_id: string;
  company_code?: OnboardingWorkloadCompanyCode;
  source_system?: OnboardingWorkloadSourceSystem;
  destination_system: DestinationSystem;
  config_snapshot?: null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export type OnboardingWorkloadCreateSourceSystem = SourceSystem | null;

export type OnboardingWorkloadCreateCompanyCode = string | null;

/**
 * Request DTO — create a new onboarding workload (Tier 1 header).
 */
export interface OnboardingWorkloadCreate {
  workspace_id: string;
  destination_system: DestinationSystem;
  source_system?: OnboardingWorkloadCreateSourceSystem;
  company_code?: OnboardingWorkloadCreateCompanyCode;
}

export type OrgCategory = typeof OrgCategory[keyof typeof OrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type OrgJurisdiction = 'nz' | 'au-federal' | 'au-nsw' | 'au-vic' | 'au-qld' | 'au-wa' | 'au-sa' | 'au-tas' | 'au-act' | 'au-nt' | 'au-modern-awards' | 'nz' | 'au';

export type OrgCataloguedAt = string | null;

export type OrgFoundingAdminRoleCreatedAt = string | null;

export type OrgDocumentStoreCreatedAt = string | null;

export type OrgOnboarding = 'pending' | 'in_progress' | 'review' | 'completed' | null;

export interface Org {
  id: string;
  category: OrgCategory;
  name: string;
  creator_user_id: string;
  jurisdiction: OrgJurisdiction;
  catalogued_at?: OrgCataloguedAt;
  founding_admin_role_created_at?: OrgFoundingAdminRoleCreatedAt;
  document_store_created_at?: OrgDocumentStoreCreatedAt;
  onboarding?: OrgOnboarding;
}

/**
 * Organization name if available
 */
export type OrgAdoptionEntryOrgName = string | null;

export interface OrgAdoptionEntry {
  /** Organization identifier */
  org_id: string;
  /** Organization category */
  org_category: string;
  /** Organization name if available */
  org_name?: OrgAdoptionEntryOrgName;
  /** Number of admin roles */
  admin_count: number;
  /** Number of member roles */
  member_count: number;
  /** Total roles (admin + member) */
  total_members: number;
}

export interface OrgAdoptionResponse {
  /** Orgs sorted by total members descending */
  orgs: OrgAdoptionEntry[];
}

export type OrgDocumentOrgCategory = typeof OrgDocumentOrgCategory[keyof typeof OrgDocumentOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgDocumentOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type OrgDocumentDocLastCataloguedAt = string | null;

export type OrgDocumentDocLastMarkdownCreatedAt = string | null;

export type OrgDocumentDocLastProcessedAt = string | null;

export type OrgDocumentDocLastIndexedAt = string | null;

export type OrgDocumentProcessingError = string | null;

export type OrgDocumentSourceId = string | null;

export type OrgDocumentSourceVersion = string | null;

export type OrgDocumentSourceEffectiveDate = string | null;

export type OrgDocumentSourceExpiryDate = string | null;

export type OrgDocumentEmbeddingModel = string | null;

export type OrgDocumentEmbeddingDimensions = number | null;

export interface OrgDocument {
  org_category: OrgDocumentOrgCategory;
  org_id: string;
  doc_id: string;
  doc_name: string;
  doc_source: string;
  doc_tags: string[];
  doc_last_catalogued_at?: OrgDocumentDocLastCataloguedAt;
  doc_last_markdown_created_at?: OrgDocumentDocLastMarkdownCreatedAt;
  doc_last_processed_at?: OrgDocumentDocLastProcessedAt;
  doc_last_indexed_at?: OrgDocumentDocLastIndexedAt;
  processing_status?: ProcessingStatus;
  processing_error?: OrgDocumentProcessingError;
  source_id?: OrgDocumentSourceId;
  source_version?: OrgDocumentSourceVersion;
  source_effective_date?: OrgDocumentSourceEffectiveDate;
  source_expiry_date?: OrgDocumentSourceExpiryDate;
  embedding_model?: OrgDocumentEmbeddingModel;
  embedding_dimensions?: OrgDocumentEmbeddingDimensions;
  doc_version?: number;
  doc_version_status?: DocVersionStatus;
}

export type OrgInvitationOrgCategory = typeof OrgInvitationOrgCategory[keyof typeof OrgInvitationOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type OrgInvitationRole = typeof OrgInvitationRole[keyof typeof OrgInvitationRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationRole = {
  admin: 'admin',
  member: 'member',
} as const;

export type OrgInvitationRecipientEmail = string | null;

export type OrgInvitationRecipientMobile = string | null;

export type OrgInvitationRecipientName = string | null;

export type OrgInvitationDeliveryMethod = typeof OrgInvitationDeliveryMethod[keyof typeof OrgInvitationDeliveryMethod];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationDeliveryMethod = {
  email: 'email',
  sms: 'sms',
  manual: 'manual',
  none: 'none',
} as const;

export interface OrgInvitation {
  org_id: string;
  org_category: OrgInvitationOrgCategory;
  invitation_id: string;
  role: OrgInvitationRole;
  recipient_email?: OrgInvitationRecipientEmail;
  recipient_mobile?: OrgInvitationRecipientMobile;
  recipient_name?: OrgInvitationRecipientName;
  delivery_method: OrgInvitationDeliveryMethod;
  created_at: string;
}

export type OrgInvitationCreateOrgCategory = typeof OrgInvitationCreateOrgCategory[keyof typeof OrgInvitationCreateOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationCreateOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type OrgInvitationCreateRole = typeof OrgInvitationCreateRole[keyof typeof OrgInvitationCreateRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationCreateRole = {
  admin: 'admin',
  member: 'member',
} as const;

export type OrgInvitationCreateRecipientEmail = string | null;

export type OrgInvitationCreateRecipientMobile = string | null;

export type OrgInvitationCreateRecipientName = string | null;

export type OrgInvitationCreateDeliveryMethod = typeof OrgInvitationCreateDeliveryMethod[keyof typeof OrgInvitationCreateDeliveryMethod];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgInvitationCreateDeliveryMethod = {
  email: 'email',
  sms: 'sms',
  manual: 'manual',
  none: 'none',
} as const;

export interface OrgInvitationCreate {
  org_id: string;
  org_category: OrgInvitationCreateOrgCategory;
  invitation_id: string;
  role: OrgInvitationCreateRole;
  recipient_email?: OrgInvitationCreateRecipientEmail;
  recipient_mobile?: OrgInvitationCreateRecipientMobile;
  recipient_name?: OrgInvitationCreateRecipientName;
  delivery_method: OrgInvitationCreateDeliveryMethod;
}

export type OrgKeyCategory = typeof OrgKeyCategory[keyof typeof OrgKeyCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const OrgKeyCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export interface OrgKey {
  id: string;
  category: OrgKeyCategory;
}

export interface OverviewResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Total workspaces on the platform */
  total_workspaces: number;
  /** Total threads in period */
  total_threads: number;
  /** Total organizations */
  total_orgs: number;
  /** Unique users who created at least one thread in the period */
  active_users: number;
  /** Monthly returning users (retention signal) */
  mru: number;
  /** Monthly returning orgs (org stickiness) */
  mro: number;
  document_health: DocumentHealthSummary;
  /** Total MCP interactions in period */
  total_mcp_interactions?: number;
  /** Unique platform users with MCP interactions in period */
  mcp_active_users?: number;
}

/**
 * An informational PII highlight on a single message's text.

Offsets are character offsets into the message's flattened text
(the same `text` field surfaced on `TraceMessage`) using
Python-style ``[start, end)`` half-open semantics. The schema does
not attempt to align with grapheme clusters — the admin UI is
expected to render highlights against the same flattened text the
service produced. Length-zero or negative spans are rejected so a
consumer cannot accidentally render an empty highlight.
 */
export interface PIIHighlightSpan {
  /** Pattern that produced the highlight */
  label: PIIPatternLabel;
  /**
   * Inclusive character offset into the message text
   * @minimum 0
   */
  start: number;
  /**
   * Exclusive character offset into the message text
   * @minimum 1
   */
  end: number;
}

/**
 * Allowlisted PII pattern labels surfaced as informational highlights.

The set is intentionally small. Patterns are documented at the
service layer (Step 29) where the actual regexes live; this enum is
the wire-shape contract so the admin UI can render consistent
pattern badges. Add a new label here only when the corresponding
regex is also added to the service pattern allowlist.
 */
export type PIIPatternLabel = typeof PIIPatternLabel[keyof typeof PIIPatternLabel];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PIIPatternLabel = {
  email: 'email',
  phone: 'phone',
  ird: 'ird',
  tfn: 'tfn',
  abn: 'abn',
  nzbn: 'nzbn',
} as const;

export type PacificInputComponentDetailSourceFscName = string | null;

export type PacificInputComponentDetailComponentId = string | null;

export type PacificInputComponentDetailComponentAssignmentId = string | null;

export type PacificInputComponentDetailFormula = string | null;

/**
 * Pacific input component and parameters used for calculation.
 */
export interface PacificInputComponentDetail {
  source_fsc_name?: PacificInputComponentDetailSourceFscName;
  component_invariant_name: string;
  component_id?: PacificInputComponentDetailComponentId;
  component_assignment_id?: PacificInputComponentDetailComponentAssignmentId;
  formula?: PacificInputComponentDetailFormula;
  parameters?: PacificInputParameterDetail[];
}

export type PacificInputParameterDetailValue = number | null;

export type PacificInputParameterDetailUnit = string | null;

/**
 * Pacific input parameter used for calculation.
 */
export interface PacificInputParameterDetail {
  name: string;
  value?: PacificInputParameterDetailValue;
  unit?: PacificInputParameterDetailUnit;
}

export type PacificOutputComponentDetailComponentId = string | null;

/**
 * Pacific output component/result entry.
 */
export interface PacificOutputComponentDetail {
  section: string;
  component_name: string;
  component_id?: PacificOutputComponentDetailComponentId;
  value: number;
}

/**
 * Pay group short code (e.g. 'MID')
 */
export type PacketDetailPayGroupCode = string | null;

/**
 * Pay group display name (e.g. 'Midland')
 */
export type PacketDetailPayGroupName = string | null;

/**
 * Pay period start date (ISO format)
 */
export type PacketDetailPayPeriodStart = string | null;

/**
 * Pay period end date (ISO format)
 */
export type PacketDetailPayPeriodEnd = string | null;

/**
 * AI narrative analysis (deferred in this phase)
 */
export type PacketDetailAiAnalysis = string | null;

/**
 * Detailed packet response for packet detail screen.
 */
export interface PacketDetail {
  /** Unique document/packet identifier */
  packet_id: string;
  /** Parent batch ID */
  batch_id: string;
  /** Overall severity classification for this packet */
  severity_tier: PacketSeverityTier;
  /** FSC gross pay amount (NZD) */
  fsc_gross: number;
  /** FSC PAYE amount (NZD) */
  fsc_paye: number;
  /** FSC net pay amount (NZD) */
  fsc_net: number;
  /** Pacific gross pay amount (NZD) */
  pacific_gross: number;
  /** Pacific PAYE amount (NZD) */
  pacific_paye: number;
  /** Pacific net pay amount (NZD) */
  pacific_net: number;
  /**
   * Count of fields where FSC and Pacific values differ
   * @minimum 0
   */
  variance_field_count: number;
  /** Pay group short code (e.g. 'MID') */
  pay_group_code?: PacketDetailPayGroupCode;
  /** Pay group display name (e.g. 'Midland') */
  pay_group_name?: PacketDetailPayGroupName;
  /** Pay period start date (ISO format) */
  pay_period_start?: PacketDetailPayPeriodStart;
  /** Pay period end date (ISO format) */
  pay_period_end?: PacketDetailPayPeriodEnd;
  /** Full FSC vs Pacific field-level comparisons */
  comparison_rows?: ComparisonRow[];
  /** Variance rows including expected variance flags */
  variance_details?: PacketVarianceDetail[];
  /** FSC identified components */
  fsc_components?: FscComponentDetail[];
  /** FSC to Pacific component mapping links */
  component_mappings?: ComponentMappingDetail[];
  /** Pacific mapped input components and parameters */
  pacific_input_components?: PacificInputComponentDetail[];
  /** Pacific calculation output components/results */
  pacific_output_components?: PacificOutputComponentDetail[];
  /** AI narrative analysis (deferred in this phase) */
  ai_analysis?: PacketDetailAiAnalysis;
}

/**
 * Flags indicating which graph details are available in this response.
 */
export interface PacketGraphDataCompleteness {
  /** Whether graph data exists for the packet */
  has_graph_data?: boolean;
  /** Whether at least one node includes per-day calculated amounts */
  has_per_day_amounts?: boolean;
  /** Whether execution order data is available */
  has_execution_order?: boolean;
}

/**
 * First root node (node with no incoming dependencies), if any
 */
export type PacketGraphDetailRootNodeId = string | null;

/**
 * Detailed packet graph response for DAG rendering.
 */
export interface PacketGraphDetail {
  /** Parent batch ID */
  batch_id: string;
  /** Packet identifier */
  packet_id: string;
  /** First root node (node with no incoming dependencies), if any */
  root_node_id?: PacketGraphDetailRootNodeId;
  /** All root node IDs (nodes with no incoming dependencies) */
  root_node_ids?: string[];
  /** All leaf node IDs (nodes with no outgoing edges) */
  leaf_node_ids?: string[];
  /** All nodes in the packet graph */
  nodes?: PacketGraphNode[];
  /** All directed edges in the packet graph */
  edges?: PacketGraphEdge[];
  /** Node IDs in execution order when available */
  execution_ordered_node_ids?: string[];
  /** Availability flags for optional graph details */
  data_completeness?: PacketGraphDataCompleteness;
}

/**
 * Directed edge detail for packet graph visualization.
 */
export interface PacketGraphEdge {
  /** Source node ID */
  source_node_id: string;
  /** Target node ID */
  target_node_id: string;
}

/**
 * Variance amount
 */
export type PacketGraphNodeVarianceAmount = number | null;

/**
 * Optional per-day calculated amounts keyed by YYYY-MM-DD
 */
export type PacketGraphNodePerDayCalculatedAmount = {[key: string]: number};

/**
 * Optional execution order index for this node
 */
export type PacketGraphNodeExecutionOrderIndex = number | null;

/**
 * Node detail for packet graph visualization.
 */
export interface PacketGraphNode {
  /** Unique node identifier */
  node_id: string;
  /** Display label for the node */
  label: string;
  /** Node classification */
  node_class: string;
  /** Node type */
  node_type: string;
  /** Calculated amount for this node */
  calculated_amount: number;
  /** Whether node has a calculated value */
  has_value?: boolean;
  /** Calculation description */
  calculation_description?: string;
  /** Whether this node has variance */
  has_variance?: boolean;
  /** Variance amount */
  variance_amount?: PacketGraphNodeVarianceAmount;
  /** Incoming dependency node IDs for this node */
  dependencies?: string[];
  /** Optional per-day calculated amounts keyed by YYYY-MM-DD */
  per_day_calculated_amount?: PacketGraphNodePerDayCalculatedAmount;
  /** Optional execution order index for this node */
  execution_order_index?: PacketGraphNodeExecutionOrderIndex;
}

/**
 * Pay group short code (e.g. 'MID')
 */
export type PacketResultPayGroupCode = string | null;

/**
 * Pay group display name (e.g. 'Midland')
 */
export type PacketResultPayGroupName = string | null;

/**
 * Pay period start date (ISO format)
 */
export type PacketResultPayPeriodStart = string | null;

/**
 * Pay period end date (ISO format)
 */
export type PacketResultPayPeriodEnd = string | null;

/**
 * Packet-level comparison result for list view.
 */
export interface PacketResult {
  /** Unique document/packet identifier */
  packet_id: string;
  /** Parent batch ID */
  batch_id: string;
  /** Overall severity classification for this packet */
  severity_tier: PacketSeverityTier;
  /** FSC gross pay amount (NZD) */
  fsc_gross: number;
  /** FSC PAYE amount (NZD) */
  fsc_paye: number;
  /** FSC net pay amount (NZD) */
  fsc_net: number;
  /** Pacific gross pay amount (NZD) */
  pacific_gross: number;
  /** Pacific PAYE amount (NZD) */
  pacific_paye: number;
  /** Pacific net pay amount (NZD) */
  pacific_net: number;
  /**
   * Count of fields where FSC and Pacific values differ
   * @minimum 0
   */
  variance_field_count: number;
  /** Pay group short code (e.g. 'MID') */
  pay_group_code?: PacketResultPayGroupCode;
  /** Pay group display name (e.g. 'Midland') */
  pay_group_name?: PacketResultPayGroupName;
  /** Pay period start date (ISO format) */
  pay_period_start?: PacketResultPayPeriodStart;
  /** Pay period end date (ISO format) */
  pay_period_end?: PacketResultPayPeriodEnd;
}

/**
 * Severity classification for packet-level comparison results.
 */
export type PacketSeverityTier = typeof PacketSeverityTier[keyof typeof PacketSeverityTier];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PacketSeverityTier = {
  MATCH: 'MATCH',
  ACCEPTABLE: 'ACCEPTABLE',
  MINOR: 'MINOR',
  VARIANCE: 'VARIANCE',
  CRITICAL: 'CRITICAL',
  ERROR: 'ERROR',
  SKIPPED_ZERO: 'SKIPPED_ZERO',
  SKIPPED_REVERSAL: 'SKIPPED_REVERSAL',
  SKIPPED_REVERSAL_TYPE: 'SKIPPED_REVERSAL_TYPE',
  SKIPPED_INTERVENTION: 'SKIPPED_INTERVENTION',
  SKIPPED_PRE_CONFIG: 'SKIPPED_PRE_CONFIG',
} as const;

/**
 * Reason variance is expected, when applicable
 */
export type PacketVarianceDetailExpectedReason = string | null;

/**
 * Variance detail row enriched with expected variance markers.
 */
export interface PacketVarianceDetail {
  /** Field key with variance */
  field: string;
  /** Display label for UI */
  label: string;
  /** FSC field value */
  fsc_value: number;
  /** Pacific field value */
  pacific_value: number;
  /** Pacific - FSC difference */
  difference: number;
  /** Percent difference relative to FSC value */
  percentage_difference: number;
  /** True when variance is expected due to known limitations */
  is_expected?: boolean;
  /** Reason variance is expected, when applicable */
  expected_reason?: PacketVarianceDetailExpectedReason;
}

/**
 * Paginated response for dead-letter queue entries.
 */
export interface PaginatedDeadLetterResponse {
  /** Dead-letter entries for current page */
  entries: DeadLetterEntry[];
  /**
   * Total number of entries matching filters
   * @minimum 0
   */
  total_count: number;
  /**
   * Maximum entries per page
   * @minimum 1
   * @maximum 1000
   */
  limit: number;
  /**
   * Number of entries skipped
   * @minimum 0
   */
  offset: number;
  /** True if more entries exist beyond this page */
  has_more: boolean;
}

/**
 * Available pay groups and pay periods for filter dropdowns
 */
export type PaginatedPacketResponseFacets = BatchFacets | null;

/**
 * Paginated packet list response for a batch.
 */
export interface PaginatedPacketResponse {
  /** Packet results for the current page */
  items?: PacketResult[];
  /**
   * Total number of matching packets
   * @minimum 0
   */
  total: number;
  /**
   * Current page number (1-indexed)
   * @minimum 1
   */
  page: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  page_size: number;
  /**
   * Total pages for the current filter
   * @minimum 0
   */
  total_pages: number;
  /** Available pay groups and pay periods for filter dropdowns */
  facets?: PaginatedPacketResponseFacets;
}

/**
 * ISO country code — NZ (default) or AU
 */
export type PatchCompanyShellRowCountryCode = typeof PatchCompanyShellRowCountryCode[keyof typeof PatchCompanyShellRowCountryCode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PatchCompanyShellRowCountryCode = {
  NZ: 'NZ',
  AU: 'AU',
} as const;

/**
 * Company Group from the shell-upload CSV (C.1 row 5) — a DataPay group name validated via ``GET /v/1/onboarding/companygroups/{name}``. Optional; when omitted or blank, Christmas auto-creates a single-company group. When set, the canonical name from that GET is forwarded to Christmas as ``companyGroupName``. Not an Agentic workspace/org id; Pacific authorisation is enforced at invocation time via the service account ReBAC guards.
 */
export type PatchCompanyShellRowCompanyGroupName = string | null;

/**
 * Bureau ID for the shell (Christmas-scoped)
 */
export type PatchCompanyShellRowBureauId = string | null;

/**
 * Region name from the CSV — server resolves to regionId via the region-resolver port. Required when country_code == 'NZ' (Company Region is a NZ concept). When non-blank it must match the closed list of the 12 NZ regions (case-insensitive): Auckland, Taranaki, Hawke's Bay, Wellington, Marlborough, Nelson, Canterbury, Canterbury (South), Westland, Otago, Southland, Chatham Islands. AU rows leave this blank and carry their state via postal_state.
 */
export type PatchCompanyShellRowRegionName = string | null;

/**
 * Datacom End Date (DD/MM/YYYY), if any
 */
export type PatchCompanyShellRowEndDate = string | null;

/**
 * Company Annual Leave Anniversary Date (DD/MM/YYYY)
 */
export type PatchCompanyShellRowAnnualLeaveAnniversaryDate = string | null;

export type PatchCompanyShellRowTradingName = string | null;

/**
 * Company IRD Number (NZ)
 */
export type PatchCompanyShellRowCompanyIrdNo = string | null;

/**
 * Parsed Sick Leave Stand Down (validator emits errors on parse failure)
 */
export type PatchCompanyShellRowSickLeaveStandDown = SickLeaveStandDown | null;

/**
 * Australian Business Number (AU only). Required for AU rows; validated by ABN mod-89 checksum. WPN shape (8 digits) is also accepted — WPN checksum is deferred to Christmas validateOnly.
 */
export type PatchCompanyShellRowCompanyAbnNumber = string | null;

/**
 * ABN Branch Number (AU only, optional). When present must be exactly 3 digits; max_length=3 enforced at the model boundary.
 */
export type PatchCompanyShellRowCompanyAbnBranchNumber = string | null;

/**
 * State/territory code for the postal address (AU only)
 */
export type PatchCompanyShellRowPostalState = string | null;

/**
 * Postcode for the postal address (AU only)
 */
export type PatchCompanyShellRowPostalCode = string | null;

/**
 * State/territory code for the courier address (AU only)
 */
export type PatchCompanyShellRowCourierState = string | null;

/**
 * Postcode for the courier address (AU only)
 */
export type PatchCompanyShellRowCourierCode = string | null;

/**
 * Secondary contact block — conditionally required per the CSV legend
 */
export type PatchCompanyShellRowSecondaryContact = CompanyContact | null;

/**
 * Delivery method (rule 20)
 */
export type PatchCompanyShellRowDelivery = string | null;

/**
 * Delivery note (rule 21)
 */
export type PatchCompanyShellRowDeliveryNote = string | null;

/**
 * EFiling Option — Christmas home unconfirmed
 */
export type PatchCompanyShellRowEfilingOption = string | null;

/**
 * Large Company — closed list Small/Large (C.1 row 13)
 */
export type PatchCompanyShellRowLargeCompany = string | null;

/**
 * ACR Product Type — Christmas home unconfirmed
 */
export type PatchCompanyShellRowAcrProductType = string | null;

/**
 * KiwiSaver Option — Christmas home unconfirmed
 */
export type PatchCompanyShellRowKiwisaverOption = string | null;

/**
 * ACC Classification Unit — Christmas home unconfirmed
 */
export type PatchCompanyShellRowAccClassificationUnit = string | null;

/**
 * FTE Standard Hours/Week (rule 18) — Christmas home unconfirmed
 */
export type PatchCompanyShellRowFteStandardHoursPerWeek = number | null;

/**
 * Week Start Day (rule 19) — Christmas home unconfirmed
 */
export type PatchCompanyShellRowWeekStartDay = string | null;

/**
 * PATCH-only strict variant of ``CompanyShellRow`` (#898 PR feedback).

``EntityRecordPatch``'s own ``extra="forbid"`` only guards *its* keys
(i.e. that the body has nothing besides ``canonical_entities``) — it does
not cascade into the row model, and ``CompanyShellRow`` itself (like its
nested value objects ``SickLeaveStandDown``/``CompanyContact``/
``CompanyShellPostalAddress``/``PayFrequencySchedule``) has no
``model_config`` of its own, so it defaults to Pydantic's
``extra="ignore"``: an unknown/non-canonical field anywhere in a PATCH
row would previously be silently stripped and still return success.

``CompanyShellRow`` is shared with the Track-1 import-parsing path
(``CompanyShellSourceImportAdapter`` / ``Validator``), so changing its
global ``extra`` behaviour, or the nested models' behaviour, is out of
scope for this PATCH-only fix. Redeclaring the nested fields with
strict-subclass types would also make this class's mutable attributes
covariant overrides of the base class's (a Liskov/pyright
``reportIncompatibleVariableOverride`` violation), so instead this
subclass — used only for ``EntityRecordPatch.canonical_entities`` — sets
``extra="forbid"`` on itself (covers unknown top-level row keys) and
manually rejects unknown keys in the raw (pre-validation) nested dicts
via a ``mode="before"`` validator, so a PATCH row carrying an unexpected
field anywhere is rejected with 422 instead of silently accepted.
 */
export interface PatchCompanyShellRow {
  /** Alphanumeric company code */
  company_code: string;
  /** Legal company name */
  company_name: string;
  /** ISO country code — NZ (default) or AU */
  country_code?: PatchCompanyShellRowCountryCode;
  /** Company Group from the shell-upload CSV (C.1 row 5) — a DataPay group name validated via ``GET /v/1/onboarding/companygroups/{name}``. Optional; when omitted or blank, Christmas auto-creates a single-company group. When set, the canonical name from that GET is forwarded to Christmas as ``companyGroupName``. Not an Agentic workspace/org id; Pacific authorisation is enforced at invocation time via the service account ReBAC guards. */
  companyGroupName?: PatchCompanyShellRowCompanyGroupName;
  /** Bureau ID for the shell (Christmas-scoped) */
  bureau_id?: PatchCompanyShellRowBureauId;
  /** Region name from the CSV — server resolves to regionId via the region-resolver port. Required when country_code == 'NZ' (Company Region is a NZ concept). When non-blank it must match the closed list of the 12 NZ regions (case-insensitive): Auckland, Taranaki, Hawke's Bay, Wellington, Marlborough, Nelson, Canterbury, Canterbury (South), Westland, Otago, Southland, Chatham Islands. AU rows leave this blank and carry their state via postal_state. */
  region_name?: PatchCompanyShellRowRegionName;
  /** Datacom Start Date (DD/MM/YYYY) */
  start_date: string;
  /** Datacom End Date (DD/MM/YYYY), if any */
  end_date?: PatchCompanyShellRowEndDate;
  /** Company Annual Leave Anniversary Date (DD/MM/YYYY) */
  annual_leave_anniversary_date?: PatchCompanyShellRowAnnualLeaveAnniversaryDate;
  trading_name?: PatchCompanyShellRowTradingName;
  /** Company IRD Number (NZ) */
  company_ird_no?: PatchCompanyShellRowCompanyIrdNo;
  /** Parsed Sick Leave Stand Down (validator emits errors on parse failure) */
  sick_leave_stand_down?: PatchCompanyShellRowSickLeaveStandDown;
  /** Australian Business Number (AU only). Required for AU rows; validated by ABN mod-89 checksum. WPN shape (8 digits) is also accepted — WPN checksum is deferred to Christmas validateOnly. */
  company_abn_number?: PatchCompanyShellRowCompanyAbnNumber;
  /** ABN Branch Number (AU only, optional). When present must be exactly 3 digits; max_length=3 enforced at the model boundary. */
  company_abn_branch_number?: PatchCompanyShellRowCompanyAbnBranchNumber;
  /** State/territory code for the postal address (AU only) */
  postal_state?: PatchCompanyShellRowPostalState;
  /** Postcode for the postal address (AU only) */
  postal_code?: PatchCompanyShellRowPostalCode;
  /** State/territory code for the courier address (AU only) */
  courier_state?: PatchCompanyShellRowCourierState;
  /** Postcode for the courier address (AU only) */
  courier_code?: PatchCompanyShellRowCourierCode;
  /** Main contact block (name/phone/mobile/email required per rules 11-15) */
  main_contact?: CompanyContact;
  /** Secondary contact block — conditionally required per the CSV legend */
  secondary_contact?: PatchCompanyShellRowSecondaryContact;
  /** Postal address (rules 16-17) */
  postal_address?: CompanyShellPostalAddress;
  /** Courier address (rules 16-17) */
  courier_address?: CompanyShellPostalAddress;
  /** Delivery method (rule 20) */
  delivery?: PatchCompanyShellRowDelivery;
  /** Delivery note (rule 21) */
  delivery_note?: PatchCompanyShellRowDeliveryNote;
  /** One entry per retained frequency; per-frequency PPE + pay-group start dates. Validator enforces at least one entry. */
  pay_schedules?: PayFrequencySchedule[];
  /** EFiling Option — Christmas home unconfirmed */
  efiling_option?: PatchCompanyShellRowEfilingOption;
  /** Large Company — closed list Small/Large (C.1 row 13) */
  large_company?: PatchCompanyShellRowLargeCompany;
  /** ACR Product Type — Christmas home unconfirmed */
  acr_product_type?: PatchCompanyShellRowAcrProductType;
  /** KiwiSaver Option — Christmas home unconfirmed */
  kiwisaver_option?: PatchCompanyShellRowKiwisaverOption;
  /** ACC Classification Unit — Christmas home unconfirmed */
  acc_classification_unit?: PatchCompanyShellRowAccClassificationUnit;
  /** FTE Standard Hours/Week (rule 18) — Christmas home unconfirmed */
  fte_standard_hours_per_week?: PatchCompanyShellRowFteStandardHoursPerWeek;
  /** Week Start Day (rule 19) — Christmas home unconfirmed */
  week_start_day?: PatchCompanyShellRowWeekStartDay;
}

export type PatchImportRowCompanyComponentId = string | null;

export type PatchImportRowColumns = {[key: string]: string};

export type PatchImportRowStartDate = string | null;

export type PatchImportRowEndDate = string | null;

/**
 * PATCH-only strict variant of ``ImportRow`` (Slice 5 PR 3).

``ImportRow`` is shared with Track 1 import parsing, so its global
``extra`` behaviour stays permissive. This subclass — used only for
``EntityRecordPatch.canonical_entities`` — sets ``extra="forbid"`` so a
PATCH row carrying an unexpected field is rejected with 422.
 */
export interface PatchImportRow {
  rowIndex: number;
  companyCode: string;
  employeeCode: string;
  componentName: string;
  companyComponentId?: PatchImportRowCompanyComponentId;
  columns: PatchImportRowColumns;
  startDate?: PatchImportRowStartDate;
  endDate?: PatchImportRowEndDate;
  rowMode?: RowMode;
}

export type PatchWorkPatternRowEmployeeCode = string | null;

export type PatchWorkPatternRowWorkPatternCode = string | null;

/**
 * PATCH-only strict variant of ``WorkPatternRow`` (Slice 5 PR 2).

``WorkPatternRow`` is shared with Track 1 import parsing, so its global
``extra`` behaviour stays permissive. This subclass — used only for
``EntityRecordPatch.canonical_entities`` — sets ``extra="forbid"`` so a
PATCH row carrying an unexpected field is rejected with 422.
 */
export interface PatchWorkPatternRow {
  /** @maxLength 255 */
  workPatternName: string;
  employeeCode?: PatchWorkPatternRowEmployeeCode;
  workPatternCode?: PatchWorkPatternRowWorkPatternCode;
  cycleDays: number;
  startDate: string;
  /** @maxLength 10 */
  payPublicHolidays: string;
  standardHours: number;
  standardDays: number;
  dayHours: number[];
}

/**
 * Optional provider template id (e.g. 'xero') for mapping coverage
 */
export type PayComponentImportInputProviderId = string | null;

/**
 * Track 1 inline pay-component rows to validate / commit. Mutually exclusive with entity_record_ref; when set must be non-empty. Capped at MAX_ROWS_PER_INVOCATION at the model boundary.
 */
export type PayComponentImportInputRows = ImportRow[] | null;

/**
 * Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with rows. Hydrated inside the workflow before the existing row-processing path runs.
 */
export type PayComponentImportInputEntityRecordRef = EntityRecordRef | null;

/**
 * Optional client-supplied correlation id. Threaded through activity logs and used to build deterministic idempotency keys. When provided, must be 1-128 characters from [A-Za-z0-9._:-].
 */
export type PayComponentImportInputCorrelationId = string | null;

/**
 * Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run.
 */
export type PayComponentImportInputRunId = string | null;

/**
 * Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a migration-run child.
 */
export type PayComponentImportInputWorkloadId = string | null;

/**
 * Destination-tier axis (LADR-039) selecting which Christmas credential/endpoint tier (test vs. prod) to push to. Set by the orchestrator for Track 2 children; optional for Track 1 callers.
 */
export type PayComponentImportInputTargetEnvironment = TargetEnvironment | null;

/**
 * API-boundary payload for the ``PayComponentImportWorkflow``.

The ``workflow`` field is the ``WorkflowPayload`` discriminator; keep the
Literal value in sync with the entry in ``ONBOARDING_TASK_QUEUE_WORKFLOWS``
inside ``workflow_registry.py``.
 */
export interface PayComponentImportInput {
  /** Workflow discriminator */
  workflow?: 'pay_component_import';
  /** validate (dry-run, zero writes) or commit (submit rows) */
  mode: ImportMode;
  /** Pacific company code (used as company_id) */
  company_code: string;
  /** Optional provider template id (e.g. 'xero') for mapping coverage */
  provider_id?: PayComponentImportInputProviderId;
  /** Track 1 inline pay-component rows to validate / commit. Mutually exclusive with entity_record_ref; when set must be non-empty. Capped at MAX_ROWS_PER_INVOCATION at the model boundary. */
  rows?: PayComponentImportInputRows;
  /** Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with rows. Hydrated inside the workflow before the existing row-processing path runs. */
  entity_record_ref?: PayComponentImportInputEntityRecordRef;
  /** Confirmed company column→parameter mappings, passed INLINE so the commit workflow needs no shared mapping store (the onboarding worker runs as a separate, stateless process). Company mappings take precedence over provider templates. Mirrors how CompanyShellProvisionWorkflow carries its rows inline. */
  mappings?: MappingEntry[];
  /** Optional client-supplied correlation id. Threaded through activity logs and used to build deterministic idempotency keys. When provided, must be 1-128 characters from [A-Za-z0-9._:-]. */
  correlation_id?: PayComponentImportInputCorrelationId;
  /** Target Datapay environment; routes to the matching MyPay API deployment. */
  mypay_environment: MyPayEnvironment;
  /** Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run. */
  run_id?: PayComponentImportInputRunId;
  /** Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a migration-run child. */
  workload_id?: PayComponentImportInputWorkloadId;
  /** Destination-tier axis (LADR-039) selecting which Christmas credential/endpoint tier (test vs. prod) to push to. Set by the orchestrator for Track 2 children; optional for Track 1 callers. */
  target_environment?: PayComponentImportInputTargetEnvironment;
}

/**
 * First PPE date (DD/MM/YYYY) for this frequency
 */
export type PayFrequencyScheduleFirstPpeDate = string | null;

/**
 * Pay group start date (DD/MM/YYYY) for this frequency
 */
export type PayFrequencySchedulePayGroupStartDate = string | null;

/**
 * First-PPE + pay-group start date for one retained frequency.

The CSV carries per-frequency dates for Weekly / Fortnightly / Monthly;
the frontend collapses these into a list of ``PayFrequencySchedule``
entries — one per frequency the company retains.

Conditional requiredness (per the CSV legend) is enforced by the
validator: at least one schedule is required, and its dates must both
be present.
 */
export interface PayFrequencySchedule {
  /** Pay frequency (weekly | fortnightly | monthly) */
  frequency: string;
  /** First PPE date (DD/MM/YYYY) for this frequency */
  first_ppe_date?: PayFrequencyScheduleFirstPpeDate;
  /** Pay group start date (DD/MM/YYYY) for this frequency */
  pay_group_start_date?: PayFrequencySchedulePayGroupStartDate;
}

/**
 * A distinct pay group available in a batch.
 */
export interface PayGroupFacet {
  /** Pay group short code (e.g. 'MID') */
  code: string;
  /** Pay group display name (e.g. 'Midland') */
  name: string;
}

export type PayGroupOptionsProfilePayrollConsultant = PayrollConsultantRef | null;

export type PayGroupOptionsProfileFinalReportDistributionProcessTime = number | null;

export type PayGroupOptionsProfileNewPayRunCreationWaitTime = number | null;

export type PayGroupOptionsProfileDaysToFinaliseAfterDCDate = number | null;

export type PayGroupOptionsProfileMinNumOfPayRunAuthorisers = number | null;

export type PayGroupOptionsProfileDaysAfterTerminationDate = number | null;

/**
 * The Christmas *Pay Group Options* screen — 18 fields (12 booleans + 6
value fields).

Value-field types/aliases follow the Christmas **C# contract** (property
names, not UI labels): C# ``byte?``/``short?``/``int?`` are modelled as
Python ``int`` with range constraints (Python has no byte/short). Boolean
aliases are UI-derived and provisional (reconcile at LADR-034). Field
*order* here is irrelevant — Web sorts A→Z. Every boolean defaults
``False`` (incl. the greyed-but-editable *Automatically Generate
Timesheet*); the user selects values, we never seed a default-True.

NOTE: *Enable Annual Leave in Weeks Calculations* is deliberately absent —
it lives on the **PayGroup** entity, not on the options profile.

Conditional validation (``validate_conditional_days``):
    - ``automatically_finalise_main_pay_run`` → ``days_to_finalise_after_dc_date`` must be present and > 0
    - ``enable_pay_run_authorisation`` → ``min_num_of_pay_run_authorisers`` must be present and > 0
    - ``auto_end_date_user_after_termination`` → ``days_after_termination_date`` must be present and > 0
 */
export interface PayGroupOptionsProfile {
  PayrollConsultant?: PayGroupOptionsProfilePayrollConsultant;
  FinalReportDistributionProcessTime?: PayGroupOptionsProfileFinalReportDistributionProcessTime;
  NewPayRunCreationWaitTime?: PayGroupOptionsProfileNewPayRunCreationWaitTime;
  AutomaticallyFinaliseMainPayRun?: boolean;
  DaysToFinaliseAfterDCDate?: PayGroupOptionsProfileDaysToFinaliseAfterDCDate;
  AllowManualCreationOfEmptyPendingRuns?: boolean;
  AutoCreateEmptyPendingRuns?: boolean;
  AutomaticallyGenerateTimesheet?: boolean;
  StopAutomaticTimesheetGeneration?: boolean;
  EnablePayRunAuthorisation?: boolean;
  MinNumOfPayRunAuthorisers?: PayGroupOptionsProfileMinNumOfPayRunAuthorisers;
  EmailUserOnTimesheetImportJobCompletion?: boolean;
  EmailAllPayrollAdminsForAPIOrThirdPartyImportedTimesheets?: boolean;
  LoadTop50PrioritisedPayPackets?: boolean;
  AutoEndDateUserAfterTermination?: boolean;
  DaysAfterTerminationDate?: PayGroupOptionsProfileDaysAfterTerminationDate;
  UseDirectAccessURLForEmailNotification?: boolean;
  DisablePayRunsPublicHolidayDCDateShift?: boolean;
}

/**
 * PayGroup-entity screen flags from the Christmas Pay Group Options screen.

These flags persist on the Christmas `PayGroup` entity itself (NOT on the
pay-group *options* object mirrored by `PayGroupOptionsProfile`), so they
form their own profile section — the same one-C#-entity-per-sub-profile split
as `CompanySettingsProfile` (Company entity). Aliases match the exact
`PayGroup.cs` C# property names.

`enable_liw` mirrors the *Enable Annual Leave in Weeks Calculations* checkbox
on the Pay Group Options screen (`PayGroup.EnableLIW`). Note the Christmas
coupling: at runtime this is OR-ed with the company-wide flag
(`Company.EnableTrialComponentVersions`, exposed as
`CompanySettingsProfile.enable_trial_component_versions`) — the same label
also appears on the Company Options screen — and Christmas only persists
`EnableLIW` when the box is ticked *and* the company flag is off (a
per-pay-group override). That cross-entity save-time reconciliation belongs to
the LADR-034 apply-path, not this storage schema, so this section stores the
flag as a plain bool with no cross-section validator.
 */
export interface PayGroupSettingsProfile {
  EnableLIW?: boolean;
}

/**
 * A distinct pay period available in a batch.
 */
export interface PayPeriodFacet {
  /** Period start date (ISO format) */
  start: string;
  /** Period end date (ISO format) */
  end: string;
}

/**
 * Optional custom system prompt (uses default if not provided)
 */
export type PayrollAnalystWorkflowInputSystemPrompt = string | null;

/**
 * Input for the PayrollAnalyst Agent Workflow.

Triggers an AI-powered analysis of a shadow batch run, using analysis tools
to investigate variances, trace root causes through calculation graphs,
and produce structured findings.
 */
export interface PayrollAnalystWorkflowInput {
  /** Workflow discriminator */
  workflow?: 'payroll_analyst';
  /** Batch identifier to analyze (from ShadowRunWorkflow) */
  shadow_run_id: string;
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Optional custom system prompt (uses default if not provided) */
  system_prompt?: PayrollAnalystWorkflowInputSystemPrompt;
  /** Optional focus areas for analysis (e.g., ['paye', 'kiwiSaver']) */
  analysis_focus?: string[];
  /**
   * Maximum agent loop iterations before forced completion
   * @minimum 1
   * @maximum 50
   */
  max_iterations?: number;
}

/**
 * Payroll-consultant *User* reference (nullable). Minimal shape — reconcile
the exact fields with the Christmas ``User`` contract at LADR-034.
 */
export interface PayrollConsultantRef {
  /** @minLength 1 */
  UserId: string;
}

export type Period = typeof Period[keyof typeof Period];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Period = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
} as const;

/**
 * Document IDs for a single pay period's data sources.
 */
export interface PeriodDocIds {
  /** PayPacketFinalised blob doc_id */
  payDocId: string;
  /** LeaveAdjusted blob doc_id */
  leaveDocId: string;
}

export type PersonalOrgCreateJurisdiction = 'nz' | 'au-federal' | 'au-nsw' | 'au-vic' | 'au-qld' | 'au-wa' | 'au-sa' | 'au-tas' | 'au-act' | 'au-nt' | 'au-modern-awards' | 'nz' | 'au' | null;

export interface PersonalOrgCreate {
  id: string;
  category?: 'personal';
  name: string;
  creator_user_id: string;
  jurisdiction?: PersonalOrgCreateJurisdiction;
}

export type ProcessingStatus = typeof ProcessingStatus[keyof typeof ProcessingStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ProcessingStatus = {
  pending: 'pending',
  processing: 'processing',
  completed: 'completed',
  failed: 'failed',
  deleting: 'deleting',
  cleaning: 'cleaning',
} as const;

export type ProvisionAdminRoleResultResult = StepFailed | StepSkipped | StepSucceeded;

export interface ProvisionAdminRoleResult {
  step?: 'provision_admin_role';
  result: ProvisionAdminRoleResultResult;
}

/**
 * Which branch of the workflow to run.
 */
export type ProvisionMode = typeof ProvisionMode[keyof typeof ProvisionMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ProvisionMode = {
  validate: 'validate',
  commit: 'commit',
} as const;

export type PublicOrgCreateJurisdiction = 'nz' | 'au-federal' | 'au-nsw' | 'au-vic' | 'au-qld' | 'au-wa' | 'au-sa' | 'au-tas' | 'au-act' | 'au-nt' | 'au-modern-awards' | 'nz' | 'au' | null;

export interface PublicOrgCreate {
  id: string;
  category?: 'public';
  name: string;
  creator_user_id: string;
  jurisdiction?: PublicOrgCreateJurisdiction;
}

export type PushedRecordVersionEmployeeKey = string | null;

/**
 * Payload provenance for one entity/employee record read at push time.

One entry per record in the run's scope. ``etag`` is the
optimistic-concurrency token read from the source
``OnboardingEntityRecord`` (or, in a later employee-tier slice, the
per-employee record) at the moment this run captured its snapshot — never
re-read afterwards, so the run's provenance stays a fixed point-in-time
reference even if the underlying record is edited later.
 */
export interface PushedRecordVersion {
  entity_type: EntityType;
  employee_key?: PushedRecordVersionEmployeeKey;
  etag: string;
}

export interface ReadyResponse {
  status_kind?: 'ready';
  version: string;
}

export type RecipientName = string | null;

export type RecipientRole = typeof RecipientRole[keyof typeof RecipientRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RecipientRole = {
  admin: 'admin',
  member: 'member',
} as const;

export interface Recipient {
  email: string;
  name?: RecipientName;
  role: RecipientRole;
}

export interface Refusal {
  response_kind?: 'refusal';
  reason: string;
}

/**
 * Response model for dead-letter retry operation.
 */
export interface RetryDeadLetterResponse {
  /** Document ID being retried */
  doc_id: string;
  /** New Temporal workflow ID */
  workflow_id: string;
  /** New Temporal run ID */
  run_id: string;
  /** Status of the retry workflow (always RUNNING on creation) */
  status?: 'RUNNING';
}

/**
 * MRO / prior period active orgs, null if no prior activity
 */
export type ReturningOrgsResponseRetentionRate = number | null;

export interface ReturningOrgsResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Orgs with active users in the current period */
  active_orgs: number;
  /** Monthly returning orgs (active in both current and prior period) */
  mro: number;
  /** Orgs active in current period but not in prior period */
  new_orgs: number;
  /** MRO / prior period active orgs, null if no prior activity */
  retention_rate?: ReturningOrgsResponseRetentionRate;
}

/**
 * MRU / prior period active users, null if no prior activity
 */
export type ReturningUsersResponseRetentionRate = number | null;

export interface ReturningUsersResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Monthly active users in the current period */
  mau: number;
  /** Monthly returning users (active in both current and prior period) */
  mru: number;
  /** Users active in current period but not in prior period */
  new_users: number;
  /** MRU / prior period active users, null if no prior activity */
  retention_rate?: ReturningUsersResponseRetentionRate;
}

/**
 * Row mode for the LADR-038 bulk import endpoint.
 */
export type RowMode = typeof RowMode[keyof typeof RowMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RowMode = {
  Create: 'Create',
  Update: 'Update',
} as const;

/**
 * Severity of a per-row validation error.

Matches the shared frontend contract (Pacific.Agentic.Web#323). Only
``ERROR`` is currently emitted by Pacific's pre-flight; ``WARNING`` is
reserved for future non-blocking findings surfaced by ``validateOnly``.
 */
export type RowSeverity = typeof RowSeverity[keyof typeof RowSeverity];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RowSeverity = {
  error: 'error',
  warning: 'warning',
} as const;

/**
 * One per-row / per-column validation error.

Matches the shared FE/BE contract in Pacific.Agentic.Web#323 exactly:
``{ row_index, column, message, severity }``. Column ``"*"`` is reserved
for row-level errors that do not attach to a single column.
 */
export interface RowValidationError {
  /**
   * Zero-based index into the rows array
   * @minimum 0
   */
  row_index: number;
  /** CanonicalRow field name that produced the error. '*' when the error is row-level (e.g. saga failure, cross-field rule). */
  column: string;
  /** Human-readable error message */
  message: string;
  /** Error severity — ERROR is blocking, WARNING is informational */
  severity?: RowSeverity;
}

export type RunAcceptedOrchestrationWorkflowId = string | null;

/**
 * Response DTO — ``202 Accepted`` body for a newly created migration run.
 */
export interface RunAccepted {
  run_id: string;
  orchestration_workflow_id?: RunAcceptedOrchestrationWorkflowId;
  status: MigrationRunStatus;
}

export type RunViewMypayEnvironment = MyPayEnvironment | null;

export type RunViewOrchestrationWorkflowId = string | null;

export type RunViewCompletedAt = string | null;

/**
 * Read DTO — ``GET .../runs`` / ``GET .../runs/{run_id}`` response.

Mirrors the persisted ``MigrationRun`` but omits ``etag`` — the
optimistic-concurrency token stays a storage-only detail, matching
``ImportView``/``EntityRecordView``/``ValidationView``'s convention.
 */
export interface RunView {
  run_id: string;
  workloadId: string;
  target_environment: TargetEnvironment;
  entity_types: EntityType[];
  status: MigrationRunStatus;
  mypay_environment?: RunViewMypayEnvironment;
  pushed_versions: PushedRecordVersion[];
  orchestration_workflow_id?: RunViewOrchestrationWorkflowId;
  child_workflow_refs: ChildWorkflowRef[];
  correlation_id: string;
  started_at: string;
  completed_at?: RunViewCompletedAt;
  started_by: string;
}

/**
 * Type of flow for styling
 */
export type SankeyLinkVarianceType = typeof SankeyLinkVarianceType[keyof typeof SankeyLinkVarianceType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SankeyLinkVarianceType = {
  match: 'match',
  variance: 'variance',
  error: 'error',
} as const;

/**
 * A link (flow) in the Sankey diagram.
 */
export interface SankeyLink {
  /** Source node ID */
  source: string;
  /** Target node ID */
  target: string;
  /** Flow value (packet count or dollar amount) */
  value: number;
  /**
   * Number of packets in this flow
   * @minimum 0
   */
  packet_count: number;
  /** Type of flow for styling */
  variance_type: SankeyLinkVarianceType;
}

/**
 * A node in the Sankey diagram.
 */
export interface SankeyNode {
  /** Unique node identifier */
  id: string;
  /** Display label for the node */
  label: string;
  /** Node classification for styling */
  node_class: string;
  /**
   * Layer in the diagram (0=source, higher=downstream)
   * @minimum 0
   */
  layer: number;
  /** Total value flowing through this node */
  total_value?: number;
}

/**
 * PUT body for persisting company mappings from the Map Columns screen.
 */
export interface SaveMappingsRequest {
  entries?: MappingEntry[];
  replaceScopes?: MappingScopeKey[];
}

/**
 * Input for the Shadow Calculator Workflow.
 */
export interface ShadowCalculationWorkflowInput {
  /** Workflow discriminator */
  workflow: 'shadow_calculator';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Document ID containing FSC PayPacket JSON */
  doc_id: string;
}

/**
 * Input for the Shadow Calculator Child Workflow.

Wraps a single shadow calculation with parent workflow signaling capability.
 */
export interface ShadowCalculatorChildInput {
  /** Workflow discriminator */
  workflow?: 'shadow_calculator_child';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Document ID containing FSC PayPacket JSON */
  doc_id: string;
  /** Batch identifier for grouping */
  shadow_run_id: string;
  /** Parent workflow ID for signaling completion */
  parent_workflow_id: string;
  /** Task queue name for shadow calculator workflows (environment-specific) */
  task_queue?: string;
}

/**
 * Input for the Shadow Integration Workflow.
 */
export interface ShadowIntegrationInput {
  /** Workflow discriminator */
  workflow?: 'shadow_integration';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** FSC Employee ID */
  employee_id: string;
  /** Document IDs of FSC PayPackets in period order */
  doc_ids: string[];
  /** Synthetic ClientId for this test run */
  client_id?: string;
  /** Synthetic PayeeId */
  payee_id?: string;
}

/**
 * Input for the Shadow Leave Integration Workflow.
 */
export interface ShadowLeaveIntegrationInput {
  /** Workflow discriminator */
  workflow: 'shadow_leave_integration';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Leave holder UUID (= payee_id) */
  leave_holder_id: string;
  /** FSC employee identifier */
  employee_id: string;
  /** Per-period document IDs */
  period_docs: PeriodDocIds[];
}

/**
 * Optional batch identifier for tracking (auto-generated if not provided)
 */
export type ShadowRunInputShadowRunId = string | null;

/**
 * Optional pre-listed document IDs (for shard mode). If provided, skips directory listing.
 */
export type ShadowRunInputDocIds = string[] | null;

/**
 * Parent orchestrator workflow ID for shard signaling (shard mode only)
 */
export type ShadowRunInputParentOrchestratorId = string | null;

/**
 * Optional custom system prompt for the PayrollAnalystWorkflow (uses default if not provided)
 */
export type ShadowRunInputAnalysisSystemPrompt = string | null;

/**
 * Input for the Shadow Run Workflow.

Orchestrates shadow calculation across multiple pay packets in a directory,
using a signal-based coordination pattern for scalability.

Can operate in two modes:
1. Directory mode: Lists documents from `directory_path` (default)
2. Shard mode: Uses pre-listed `doc_ids` (for hierarchical orchestration)
 */
export interface ShadowRunInput {
  /** Workflow discriminator */
  workflow?: 'shadow_run';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Blob storage directory path (e.g., 'paypackets/2026-01/') */
  directory_path: string;
  /** Optional batch identifier for tracking (auto-generated if not provided) */
  shadow_run_id?: ShadowRunInputShadowRunId;
  /** Optional pre-listed document IDs (for shard mode). If provided, skips directory listing. */
  doc_ids?: ShadowRunInputDocIds;
  /** Timeout in minutes for batch completion (default: 4 hours) */
  timeout_minutes?: number;
  /** Enable CosmosDB shadow run summary persistence (requires BATCH_SUMMARY_ENABLED=true on worker) */
  persist_shadow_run_summary?: boolean;
  /** Parent orchestrator workflow ID for shard signaling (shard mode only) */
  parent_orchestrator_id?: ShadowRunInputParentOrchestratorId;
  /**
   * Error rate threshold (0.0-1.0) to trip circuit breaker. Default 0.5 = abort if >50% failures.
   * @minimum 0
   * @maximum 1
   */
  circuit_breaker_threshold?: number;
  /**
   * Minimum completed packets before circuit breaker can trip. Default 100.
   * @minimum 10
   */
  circuit_breaker_sample_size?: number;
  /** Spawn PayrollAnalystWorkflow after batch completion to analyze results */
  trigger_analysis?: boolean;
  /** Optional custom system prompt for the PayrollAnalystWorkflow (uses default if not provided) */
  analysis_system_prompt?: ShadowRunInputAnalysisSystemPrompt;
  /** Task queue name for shadow calculator workflows (environment-specific) */
  task_queue?: string;
}

/**
 * Shadow-run summary ID linking to shadow batch results; this is NOT the invocation detail key (use `id` for that). Null if not available
 */
export type ShadowRunItemBatchId = string | null;

/**
 * ISO 8601 completion time; null if not completed
 */
export type ShadowRunItemCompletedAt = string | null;

/**
 * Single shadow run item for list view (camelCase for API contract).
 */
export interface ShadowRunItem {
  /** Temporal workflow ID (stable invocation identifier); use as the {workflow_id} path parameter for the invocation detail endpoint */
  id: string;
  /** Shadow-run summary ID linking to shadow batch results; this is NOT the invocation detail key (use `id` for that). Null if not available */
  batchId?: ShadowRunItemBatchId;
  /** Run status: completed, running, failed, etc. */
  status: string;
  /** ISO 8601 completion time; null if not completed */
  completedAt?: ShadowRunItemCompletedAt;
  /** Total packets in run */
  totalPackets?: number;
  /** Processing duration */
  durationInSeconds?: number;
  /** Match rate percentage 0-100 */
  matchRate?: number;
  /** Classification of match rate for UI */
  matchRateStatus?: MatchRateStatus;
}

/**
 * Optional batch identifier for tracking (auto-generated if not provided)
 */
export type ShadowRunOrchestratorInputShadowRunId = string | null;

/**
 * Optional limit on concurrent shards (None = unlimited)
 */
export type ShadowRunOrchestratorInputMaxParallelShards = number | null;

/**
 * Optional custom system prompt for the PayrollAnalystWorkflow (uses default if not provided)
 */
export type ShadowRunOrchestratorInputAnalysisSystemPrompt = string | null;

/**
 * Input for the Shadow Run Orchestrator Workflow (enterprise scale).

Orchestrates hierarchical sharding for 10k-100k+ pay packets by spawning
sub-orchestrators (ShadowRunWorkflow) for each shard.

See: LADR-006, .plans/298-enterprise-batch-sharding.md
 */
export interface ShadowRunOrchestratorInput {
  /** Workflow discriminator */
  workflow?: 'shadow_run_orchestrator';
  /** Organization category */
  org_category: string;
  /** Organization ID */
  org_id: string;
  /** Blob storage directory path (e.g., 'paypackets/2026-01/') */
  directory_path: string;
  /** Optional batch identifier for tracking (auto-generated if not provided) */
  shadow_run_id?: ShadowRunOrchestratorInputShadowRunId;
  /**
   * Number of packets per shard (default: 5000)
   * @minimum 100
   * @maximum 10000
   */
  shard_size?: number;
  /** Optional limit on concurrent shards (None = unlimited) */
  max_parallel_shards?: ShadowRunOrchestratorInputMaxParallelShards;
  /**
   * Timeout in minutes for orchestrator completion (default: 8 hours)
   * @minimum 1
   */
  timeout_minutes?: number;
  /**
   * Error rate threshold (0.0-1.0) to trip circuit breaker. Default 0.5 = abort if >50% shard failures.
   * @minimum 0
   * @maximum 1
   */
  circuit_breaker_threshold?: number;
  /**
   * Minimum completed shards before circuit breaker can trip. Default 5.
   * @minimum 1
   */
  circuit_breaker_sample_size?: number;
  /** Spawn PayrollAnalystWorkflow after orchestrator completion to analyze results */
  trigger_analysis?: boolean;
  /** Optional custom system prompt for the PayrollAnalystWorkflow (uses default if not provided) */
  analysis_system_prompt?: ShadowRunOrchestratorInputAnalysisSystemPrompt;
  /** Skip spawning child workflows (for testing signal handling only) */
  skip_child_workflows?: boolean;
  /** Task queue name for shadow calculator workflows (environment-specific) */
  task_queue?: string;
}

/**
 * Shadow run processing status values.
 */
export type ShadowRunStatus = typeof ShadowRunStatus[keyof typeof ShadowRunStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ShadowRunStatus = {
  queued: 'queued',
  processing: 'processing',
  completed: 'completed',
  failed: 'failed',
  timeout: 'timeout',
  aborted: 'aborted',
} as const;

/**
 * Organization category
 */
export type ShadowRunSummaryOrgCategory = typeof ShadowRunSummaryOrgCategory[keyof typeof ShadowRunSummaryOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ShadowRunSummaryOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

/**
 * Match rate percentage (0-100)
 */
export type ShadowRunSummaryMatchRate = number | null;

/**
 * ISO timestamp when processing started
 */
export type ShadowRunSummaryStartedAt = string | null;

/**
 * ISO timestamp when shadow run completed
 */
export type ShadowRunSummaryCompletedAt = string | null;

/**
 * ISO timestamp when record expires
 */
export type ShadowRunSummaryExpiresAt = string | null;

/**
 * Shadow run summary for operational status queries.

Stored in CosmosDB with 7-day TTL for quick status lookups.
Partition key: shadowRunId
 */
export interface ShadowRunSummary {
  /** Unique shadow run identifier */
  shadow_run_id: string;
  /** Organization category */
  org_category: ShadowRunSummaryOrgCategory;
  /** Organization ID */
  org_id: string;
  /** Directory path processed */
  directory_path: string;
  /** Current shadow run status */
  status: ShadowRunStatus;
  /** Total packets in batch */
  total_packets: number;
  /** Packets completed */
  completed_count?: number;
  /** Packets with matches */
  match_count?: number;
  /** Packets with variances */
  variance_count?: number;
  /** Packets with errors */
  error_count?: number;
  /** Match rate percentage (0-100) */
  match_rate?: ShadowRunSummaryMatchRate;
  /** ISO timestamp when shadow run created */
  created_at: string;
  /** ISO timestamp when processing started */
  started_at?: ShadowRunSummaryStartedAt;
  /** ISO timestamp when shadow run completed */
  completed_at?: ShadowRunSummaryCompletedAt;
  /** Temporal workflow ID */
  workflow_id: string;
  /** ISO timestamp when record expires */
  expires_at?: ShadowRunSummaryExpiresAt;
  /** Sample of failed document IDs (from live workflow) */
  failed_docs?: string[];
  /** Count of persistence failures (from live workflow) */
  persistence_failures?: number;
  /** Sample of documents with persistence failures (from live workflow) */
  persistence_failed_docs?: string[];
}

/**
 * Opaque cursor for next page; null if last page
 */
export type ShadowRunsListResponseNextPageCursor = string | null;

/**
 * Paginated shadow runs list response.
 */
export interface ShadowRunsListResponse {
  /** Page of shadow run items */
  data: ShadowRunItem[];
  /** Opaque cursor for next page; null if last page */
  nextPageCursor?: ShadowRunsListResponseNextPageCursor;
}

/**
 * Parsed representation of the NZ Sick Leave Stand Down control.

Rule 10 from the business-rules doc: the raw CSV field is parsed to
``{amount, periodType}`` (e.g. ``{"amount": 6, "period_type": "months"}``).
Parsing lives in the validator; this model is the on-the-wire shape.
 */
export interface SickLeaveStandDown {
  /**
   * Numeric stand-down amount
   * @minimum 0
   */
  amount: number;
  /** Period unit (e.g. 'months', 'weeks') */
  period_type: string;
}

export interface SmsCreateInvitationResponse {
  invitation_id: string;
  created_at: string;
  sent_to_phone_number: string;
}

/**
 * Reference to a staged source-upload blob — never the raw bytes.

LADR-041/042 PII posture: raw upload bytes are staged to blob storage and
only this reference crosses the Temporal workflow boundary (a
``SourceMappingInput`` field, later slice). ``blob_path`` follows the
convention ``onboarding-staging/{workload_id}/imports/{import_id}/{filename}``.
 */
export interface SourceArtifactRef {
  blob_path: string;
  content_type: string;
  /** @minimum 0 */
  byte_length: number;
  checksum_sha256: string;
}

/**
 * Storage etag observed on the Tier 2 entity record at the moment WorkloadsService.create_import passed the if_match_source_version re-import check. None on a first import (no prior record to race against). Carried through so map_source can gate its upsert_entity_record call on this etag, closing the window where two concurrent re-imports both pass the API-layer check.
 */
export type SourceMappingInputExpectedEntityEtag = string | null;

/**
 * Ref-only payload for the ``SourceMappingWorkflow``.

The ``workflow`` field is the ``WorkflowPayload`` discriminator; keep the
Literal value in sync with the entry in ``ONBOARDING_TASK_QUEUE_WORKFLOWS``
inside ``workflow_registry.py``.

Started from ``WorkloadsService`` via the shared ``WorkflowStarter``
(LADR-039 Slice 4, #899) — a semantic-202 starter, not routed through
``AgentInvocationService`` — so this model carries only what the
source-mapping activity needs to locate and process the staged upload.

``source_artifact_ref`` is the **only** pointer to the uploaded file; raw
bytes never cross the workflow boundary (LADR-041/042).
 */
export interface SourceMappingInput {
  /** Workflow discriminator */
  workflow?: 'source_mapping';
  /**
   * Owning onboarding workload id
   * @minLength 1
   */
  workload_id: string;
  /**
   * Import job document id
   * @minLength 1
   */
  import_id: string;
  /** Tier 2 entity type being imported */
  entity_type: EntityType;
  /** Reference to the staged upload blob — never the raw bytes */
  source_artifact_ref: SourceArtifactRef;
  /** Storage etag observed on the Tier 2 entity record at the moment WorkloadsService.create_import passed the if_match_source_version re-import check. None on a first import (no prior record to race against). Carried through so map_source can gate its upsert_entity_record call on this etag, closing the window where two concurrent re-imports both pass the API-layer check. */
  expected_entity_etag?: SourceMappingInputExpectedEntityEtag;
}

export type SourceMetadataSourceSystem = SourceSystem | null;

export type SourceMetadataConfigurationId = string | null;

export type SourceMetadataSelectionFields = {[key: string]: string};

/**
 * Upload provenance and the PRD R-S1-2 configuration selection.

Captured once at import time and carried on both ``Import.source_metadata``
(job provenance) and ``OnboardingEntityRecord.source_metadata``
(denormalised onto the entity slice so a later re-import's
``if_match_source_version`` precondition check does not require a join
back to the originating ``Import`` document).
 */
export interface SourceMetadata {
  original_filename: string;
  source_system?: SourceMetadataSourceSystem;
  configuration_id?: SourceMetadataConfigurationId;
  selection_fields?: SourceMetadataSelectionFields;
  source_version: string;
}

/**
 * Declared legacy payroll source for a workload. Per-import provenance
remains on the Tier 2 entity record's ``source_metadata`` (later slice).
 */
export type SourceSystem = typeof SourceSystem[keyof typeof SourceSystem];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SourceSystem = {
  ps_template: 'ps_template',
  chris21: 'chris21',
  xero: 'xero',
} as const;

/**
 * validate (dry-run) or commit (persist rows)
 */
export type SplitCostingImportInputMode = typeof SplitCostingImportInputMode[keyof typeof SplitCostingImportInputMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SplitCostingImportInputMode = {
  validate: 'validate',
  commit: 'commit',
} as const;

/**
 * Optional caller-supplied key for replay safety.
 */
export type SplitCostingImportInputIdempotencyKey = string | null;

/**
 * Optional client-supplied correlation id. Threaded through activity logs and forwarded to the downstream adapter.
 */
export type SplitCostingImportInputCorrelationId = string | null;

/**
 * API-boundary payload for the ``SplitCostingImportWorkflow``.

The ``workflow`` field is the ``WorkflowPayload`` discriminator; keep the
Literal value in sync with the entry in ``ONBOARDING_TASK_QUEUE_WORKFLOWS``
inside ``workflow_registry.py``.

``company_code`` comes from the bound session (the API layer populates it)
— it is never read from the uploaded file. This is the sole tenant
discriminator on the write.

``file_identifier`` is required and is carried purely for the import audit
record (R-GOV-3) — it is never placed on the Christmas wire body.
 */
export interface SplitCostingImportInput {
  /** Workflow discriminator */
  workflow?: 'split_costing_import';
  /** validate (dry-run) or commit (persist rows) */
  mode: SplitCostingImportInputMode;
  /**
   * Company code bound to the session. NOTE: sourced from the session, never from the uploaded file.
   * @minLength 1
   * @maxLength 64
   * @pattern ^[A-Za-z0-9._-]+$
   */
  companyCode: string;
  /**
   * Split costing rows to validate / commit. Capped at 500 at the model boundary.
   * @maxItems 500
   */
  rows: SplitCostingRow[];
  /** Optional caller-supplied key for replay safety. */
  idempotencyKey?: SplitCostingImportInputIdempotencyKey;
  /** Optional client-supplied correlation id. Threaded through activity logs and forwarded to the downstream adapter. */
  correlationId?: SplitCostingImportInputCorrelationId;
  /** Target Datapay environment; routes to the matching MyPay API deployment. */
  mypayEnvironment: MyPayEnvironment;
  /**
   * Identifier for the uploaded file (e.g. its filename), carried through for the import audit record (R-GOV-3 / AC-APP-02). Pacific-internal only — never forwarded to Christmas.
   * @minLength 1
   * @maxLength 256
   */
  fileIdentifier: string;
}

/**
 * One segment in an ordered division→leaf cost centre path.

``level`` identifies which tier of the cost structure hierarchy this segment
occupies (1 = top, increasing toward the leaf). ``code`` is the cost centre
code at that tier.

Both fields use their natural names on the wire.
 */
export interface SplitCostingPathSegment {
  /**
   * Hierarchy level (1 = top-level division)
   * @minimum 1
   */
  level: number;
  /**
   * Cost centre code at this level
   * @minLength 1
   * @maxLength 64
   */
  code: string;
}

/**
 * Apportioned percentage as a decimal (e.g. 50 for 50%). None means the field was absent — the validator blocks this case.
 */
export type SplitCostingRowApportionedPercentage = number | string | null;

/**
 * Effective start date (yyyy-MM-dd). Blank start does not block when a default can be resolved (AC-APP-08).
 */
export type SplitCostingRowStartDate = string | null;

/**
 * Raw end-date value from the client payload, carried purely so the Pacific validator can block on a non-blank value (R-APP-10). Never forwarded to Christmas.
 */
export type SplitCostingRowEndDateRaw = string | null;

/**
 * One split costing row from the portal-parsed JSON payload.

All fields use camelCase aliases to match the Christmas wire contract.
The model uses ``populate_by_name=True`` so internal code can reference
snake_case names without the alias.

``end_date_raw`` is not on the Christmas wire — it is carried here only so
the Pacific validator can block on a non-blank value (R-APP-10 / AC-ERR-07).
The field name ``endDateRaw`` signals that this is the raw client value;
errors reference the logical name ``"endDate"`` (not ``"endDateRaw"``).

``apportioned_percentage`` is ``Decimal | None`` — ``None`` means absent
from the payload (the validator blocks that case). No 0–100 range check is
enforced here — R-API-1 excludes range enforcement from MyPay; the portal
blocks out-of-range before the API (AC-ERR-05).
 */
export interface SplitCostingRow {
  /**
   * Employee code for this split costing row
   * @minLength 1
   * @maxLength 64
   */
  employeeCode: string;
  /** Apportioned percentage as a decimal (e.g. 50 for 50%). None means the field was absent — the validator blocks this case. */
  apportionedPercentage?: SplitCostingRowApportionedPercentage;
  /** Effective start date (yyyy-MM-dd). Blank start does not block when a default can be resolved (AC-APP-08). */
  startDate?: SplitCostingRowStartDate;
  /** Raw end-date value from the client payload, carried purely so the Pacific validator can block on a non-blank value (R-APP-10). Never forwarded to Christmas. */
  endDateRaw?: SplitCostingRowEndDateRaw;
  /**
   * Ordered division→leaf path identifying the target cost centre
   * @minItems 1
   */
  path: SplitCostingPathSegment[];
}

export interface StepFailed {
  status?: 'failed';
  error: string;
}

export const StepSkippedValue = {
  status: 'skipped',
} as const;
export type StepSkipped = typeof StepSkippedValue;

export const StepSucceededValue = {
  status: 'succeeded',
} as const;
export type StepSucceeded = typeof StepSucceededValue;

export interface Table {
  element_kind?: 'table';
  headers: string[];
  rows: string[][];
}

/**
 * Destination Christmas environment for a migration-run push.

Orthogonal to the product routing axis (``MyPayEnvironment``, a
Temporal-layer concept — see ``app/temporal/onboarding``): this picks the
settings-routed connector (test vs. prod base URL/credentials), not the
customer's MyPay product line. Only ``TEST`` is a live push target this
slice; ``PROD`` credentials are wired but guarded/deferred.
 */
export type TargetEnvironment = typeof TargetEnvironment[keyof typeof TargetEnvironment];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TargetEnvironment = {
  test: 'test',
  prod: 'prod',
} as const;

/**
 * Thread title if available
 */
export type ThreadClusterPointTitle = string | null;

/**
 * Human-readable cluster label
 */
export type ThreadClusterPointClusterLabel = string | null;

/**
 * A single thread plotted in 2D cluster space.
 */
export interface ThreadClusterPoint {
  /** Workspace identifier */
  workspace_id: string;
  /** Thread identifier */
  thread_id: string;
  /** Thread title if available */
  title?: ThreadClusterPointTitle;
  /** First user message text */
  question: string;
  /** Thread creation timestamp (ISO 8601) */
  created_at: string;
  /** UMAP 2D x-coordinate */
  x: number;
  /** UMAP 2D y-coordinate */
  y: number;
  /** Cluster assignment (-1 = noise/unclustered) */
  cluster_id: number;
  /** Human-readable cluster label */
  cluster_label?: ThreadClusterPointClusterLabel;
  /** Origin of the question: 'thread' or 'mcp' */
  source?: string;
}

export interface ThreadCreate {
  workspace_id: string;
  thread_id: string;
  title: string;
  knowledge_from_orgs: OrgKey[];
}

export type ThreadCreateMessageUserTimezone = string | null;

export interface ThreadCreateMessage {
  /** @maxLength 32000 */
  content: string;
  user_timezone?: ThreadCreateMessageUserTimezone;
}

export interface ThreadDepthResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Total threads in period */
  total_threads: number;
  /** Mean message count per thread */
  average_depth: number;
  /** Maximum message count in a single thread */
  max_depth: number;
  /** Thread count by depth bucket */
  distribution: DepthBucket[];
}

export interface ThreadKnowledgeUpdate {
  knowledge_from_orgs: OrgKey[];
}

export type ThreadMessageFeedbackRequestFeedback = typeof ThreadMessageFeedbackRequestFeedback[keyof typeof ThreadMessageFeedbackRequestFeedback];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ThreadMessageFeedbackRequestFeedback = {
  NUMBER_1: 1,
  NUMBER_0: 0,
  NUMBER_MINUS_1: -1,
} as const;

export interface ThreadMessageFeedbackRequest {
  feedback: ThreadMessageFeedbackRequestFeedback;
}

/**
 * A thread-typed annotation source reference.

v1 source refs are assistant messages on a thread —
``workspace_id`` + ``thread_id`` + ``assistant_message_index``. The
``type`` field is the discriminator tag for the
:data:`EvalAnnotationSourceRef` discriminated union below, so adding
``McpInteractionSourceRef`` (or any other source type) later does not
require renaming this model or breaking persisted records.
 */
export interface ThreadSourceRef {
  type?: 'thread';
  workspace_id: string;
  thread_id: string;
  /**
   * Zero-based index of the assistant message in the thread.
   * @minimum 0
   */
  assistant_message_index: number;
}

/**
 * Base model containing thread metadata without messages
 */
export interface ThreadSummary {
  workspace_id: string;
  thread_id: string;
  title: string;
  created_at: string;
  knowledge_from_orgs: OrgKey[];
}

export interface ThreadTitleUpdate {
  title: string;
}

/**
 * 2D scatter data with cluster assignments for thread topic visualisation.
 */
export interface TopicClusterResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Per-thread 2D coordinates and cluster assignments */
  threads: ThreadClusterPoint[];
  /** Per-cluster summary statistics */
  cluster_summary: ClusterSummary[];
  /** Total threads processed */
  total_threads: number;
  /** Number of distinct clusters (excluding noise) */
  total_clusters: number;
}

/**
 * Reviewer currently assigned, if any
 */
export type TraceAnnotationSummaryReviewerAssigned = string | null;

/**
 * Summary of the most recent eval annotation for the selected item.

The full annotation CRUD boundary lives under the eval module
(Step 31). The trace endpoint only surfaces the latest annotation's
headline state so the admin UI can render review status without a
second round-trip. `annotation_id` is included so the UI can deep
link into the annotation editor when one exists.
 */
export interface TraceAnnotationSummary {
  /** ULID-style annotation identifier */
  annotation_id: string;
  /** Latest EvalAnnotationStatus value */
  status: string;
  /** Whether the annotation has passed the sanitisation gate */
  sanitised: boolean;
  /** Whether the annotation has been exported as a draft golden */
  exported: boolean;
  /** Reviewer currently assigned, if any */
  reviewer_assigned?: TraceAnnotationSummaryReviewerAssigned;
  /** ISO 8601 timestamp of the most recent annotation update */
  updated_at: string;
}

/**
 * Document name if available
 */
export type TraceCitationDocName = string | null;

/**
 * Original chunk index from the assistant response (UI hint only)
 */
export type TraceCitationChunkIndex = number | null;

/**
 * Most recent processing status from the org document catalog if resolvable
 */
export type TraceCitationDocProcessingStatus = string | null;

/**
 * A citation surfaced on an assistant message in the trace.

Carries `doc_id` granularity (chunk_id is intentionally omitted to
stay stable across re-ingest, matching the rest of the harness —
see `FeedbackCitationSummary` and `ExpectedCitation`). `chunk_index`
is preserved as a UI hint only and is not used for join logic.
`doc_processing_status` is populated when the cited document can be
resolved against the org document catalog (e.g. `completed`,
`failed`); when the document is no longer available the field is
`None` and the UI should render the citation as unresolved.
 */
export interface TraceCitation {
  /** Organization category (public/internal/personal) */
  org_category: string;
  /** Organization identifier */
  org_id: string;
  /** Document identifier (stable across re-ingest) */
  doc_id: string;
  /** Document name if available */
  doc_name?: TraceCitationDocName;
  /** Original chunk index from the assistant response (UI hint only) */
  chunk_index?: TraceCitationChunkIndex;
  /** Most recent processing status from the org document catalog if resolvable */
  doc_processing_status?: TraceCitationDocProcessingStatus;
}

/**
 * ISO 8601 message timestamp if available
 */
export type TraceMessageTimestamp = string | null;

/**
 * Discriminator from UIStructuredOutput for assistant messages (answer/clarification_request/refusal/error)
 */
export type TraceMessageResponseKind = string | null;

/**
 * One message in the projected conversation trace.

Both user and assistant messages share this shape so the UI can
render them in a single ordered list. Citations are populated only
on assistant messages (and may be empty); user messages always
carry an empty `citations` list. `pii_highlights` is computed by
the service (Step 29) over the flattened `text` field for every
message regardless of role — reviewers should be able to see
obvious PII tokens in their own users' questions just as easily as
in the assistant's answer.
 */
export interface TraceMessage {
  /** Message role */
  role: TraceMessageRole;
  /** ISO 8601 message timestamp if available */
  timestamp?: TraceMessageTimestamp;
  /** Flattened message text (markdown elements joined for assistant messages) */
  text: string;
  /** Document-granularity citations on the assistant answer (always empty for user messages) */
  citations?: TraceCitation[];
  /** Discriminator from UIStructuredOutput for assistant messages (answer/clarification_request/refusal/error) */
  response_kind?: TraceMessageResponseKind;
  /** Informational PII highlight spans computed over `text`; never used to redact */
  pii_highlights?: PIIHighlightSpan[];
}

/**
 * Role of a single message in the projected trace.
 */
export type TraceMessageRole = typeof TraceMessageRole[keyof typeof TraceMessageRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TraceMessageRole = {
  user: 'user',
  assistant: 'assistant',
} as const;

/**
 * Deep link to the Honeycomb trace covering this thread completion, if resolvable
 */
export type TraceObservabilityLinksHoneycombTraceUrl = string | null;

/**
 * Deep link to the specific Honeycomb span for the selected assistant message, if resolvable
 */
export type TraceObservabilityLinksHoneycombSpanUrl = string | null;

/**
 * Optional infrastructure observability deep links.

Honeycomb links are best-effort: they are populated when a trace ID
can be resolved from thread metadata, and omitted otherwise. The
trace endpoint never depends on Honeycomb being reachable — these
are presentation hints for reviewers who want to drill into
request-level infrastructure detail.
 */
export interface TraceObservabilityLinks {
  /** Deep link to the Honeycomb trace covering this thread completion, if resolvable */
  honeycomb_trace_url?: TraceObservabilityLinksHoneycombTraceUrl;
  /** Deep link to the specific Honeycomb span for the selected assistant message, if resolvable */
  honeycomb_span_url?: TraceObservabilityLinksHoneycombSpanUrl;
}

export interface UIAssistantMessage {
  role: 'assistant';
  content: UIStructuredOutput;
  timestamp: string;
}

export type UIStructuredOutputResponse = Answer | ClarificationRequest | Refusal | Error;

export interface UIStructuredOutput {
  response: UIStructuredOutputResponse;
}

export type UIThreadMessagesItem = UIUserMessage | UIAssistantMessage;

export type UIThreadFeedback = {[key: string]: 1 | 0 | -1};

/**
 * UI representation of a thread with transformed messages
 */
export interface UIThread {
  workspace_id: string;
  thread_id: string;
  title: string;
  created_at: string;
  knowledge_from_orgs: OrgKey[];
  messages: UIThreadMessagesItem[];
  feedback: UIThreadFeedback;
}

export interface UIUserMessage {
  role: 'user';
  content: string;
  timestamp: string;
}

/**
 * An element in the import file that has no saved company mapping.

Returned in the ImportResult when status == "needs_mapping" so the Web
wizard can drive the user to the mapping setup screen.
 */
export interface UnmappedElement {
  elementQualifiedName: string;
  componentName: string;
  parameters: ComponentParameter[];
}

export type UpdateReviewRequestStatus = typeof UpdateReviewRequestStatus[keyof typeof UpdateReviewRequestStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const UpdateReviewRequestStatus = {
  in_review: 'in_review',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type UpdateReviewRequestReviewerNotes = string | null;

export type UpdateReviewRequestFailureModeTags = EvalFailureModeTag[] | null;

export type UpdateReviewRequestReviewerAssigned = string | null;

/**
 * Body for the review-lifecycle PATCH.

``status`` is narrowed to :data:`EvalAnnotationReviewRequestStatus` —
only ``"in_review"``, ``"approved"``, and ``"rejected"`` are valid.
``"pending"`` is the create-side initial state (callers cannot move
rows back to pending) and ``"exported"`` is gated on sanitisation via
the dedicated exported endpoint so its sanitisation pre-condition is
enforced in one place. Pydantic rejects any other value at request
validation time (HTTP 422) rather than letting the service surface a
later error.
 */
export interface UpdateReviewRequest {
  status: UpdateReviewRequestStatus;
  reviewer_notes?: UpdateReviewRequestReviewerNotes;
  failure_mode_tags?: UpdateReviewRequestFailureModeTags;
  reviewer_assigned?: UpdateReviewRequestReviewerAssigned;
}

/**
 * User email (from profile)
 */
export type UserCompletionCountEmail = string | null;

/**
 * User display name (from profile)
 */
export type UserCompletionCountName = string | null;

export interface UserCompletionCount {
  /** User identifier (workspace creator) */
  user_id: string;
  /** Workspace identifier */
  workspace_id: string;
  /** Number of completion requests */
  completion_count: number;
  /** User email (from profile) */
  email?: UserCompletionCountEmail;
  /** User display name (from profile) */
  name?: UserCompletionCountName;
}

export interface UserGrowthResponse {
  /** Start of the queried period (ISO 8601) */
  period_start: string;
  /** End of the queried period (ISO 8601) */
  period_end: string;
  /** Daily growth data */
  data_points: GrowthDataPoint[];
  /** Total new workspaces in period */
  total_new_workspaces: number;
}

export type UserOrgRoleOrgCategory = typeof UserOrgRoleOrgCategory[keyof typeof UserOrgRoleOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const UserOrgRoleOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type UserOrgRoleRole = typeof UserOrgRoleRole[keyof typeof UserOrgRoleRole];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const UserOrgRoleRole = {
  admin: 'admin',
  member: 'member',
} as const;

export type UserOrgRoleUserEmail = string | null;

export type UserOrgRoleUserMobile = string | null;

export type UserOrgRoleUserName = string | null;

export interface UserOrgRole {
  user_id: string;
  org_id: string;
  org_category: UserOrgRoleOrgCategory;
  role: UserOrgRoleRole;
  user_email?: UserOrgRoleUserEmail;
  user_mobile?: UserOrgRoleUserMobile;
  user_name?: UserOrgRoleUserName;
}

export type UserProfileEmail = string | null;

export type UserProfileName = string | null;

export type UserProfileStatusChangedAt = string | null;

export type UserProfileStatusChangedBy = string | null;

export interface UserProfile {
  user_id: string;
  email?: UserProfileEmail;
  name?: UserProfileName;
  first_seen_at: string;
  last_login_at: string;
  account_status?: AccountStatus;
  status_changed_at?: UserProfileStatusChangedAt;
  status_changed_by?: UserProfileStatusChangedBy;
}

export type UserProfileSummaryEmail = string | null;

export type UserProfileSummaryName = string | null;

/**
 * Lightweight projection for batch lookups (analytics).
 */
export interface UserProfileSummary {
  user_id: string;
  email?: UserProfileSummaryEmail;
  name?: UserProfileSummaryName;
}

/**
 * Which validation layers ran for a Step 2 / push validation (PRD D-4).

LADR-039 validation contract: Pydantic + Pacific rules are required;
destination Christmas ``validateOnly`` is optional per profile.
 */
export interface ValidateProfile {
  pydantic_schema?: boolean;
  pacific_cross_field?: boolean;
  destination_dry_run?: boolean;
}

export type ValidationAcceptedWorkflowId = string | null;

/**
 * Response DTO — ``202 Accepted`` body for ``POST .../validations``.

Mirrors ``ImportAccepted``'s shape even though validation is terminal
(``SUCCEEDED``/``FAILED``) by the time this responds this slice —
``workflow_id`` stays ``None`` for now so the shape is forward-compatible
if a later slice promotes validation to an async
``RevalidateEntityWorkflow``.
 */
export interface ValidationAccepted {
  validation_id: string;
  workflow_id?: ValidationAcceptedWorkflowId;
  status: JobStatus;
}

/**
 * When true, run Christmas validateOnly after Pacific validation (requires ONBOARDING_MIGRATION_RUNS_ENABLED). When false or omitted, Pacific-only.
 */
export type ValidationCreateDestinationDryRun = boolean | null;

/**
 * Target Datapay environment for destination dry-run. Defaults to nz-datapay when destination_dry_run is true for company_shell, work_patterns, or pay_components.
 */
export type ValidationCreateMypayEnvironment = MyPayEnvironment | null;

/**
 * Request DTO — optional body for ``POST .../entities/{type}/validations``.

PRD D-4 / LADR-039 ``ValidateProfile.destination_dry_run``. Omitting the
body (or leaving ``destination_dry_run`` null) uses Pacific-only
validation. Christmas ``validateOnly`` is opt-in
(``destination_dry_run=true``) and gated by
``ONBOARDING_MIGRATION_RUNS_ENABLED``.
 */
export interface ValidationCreate {
  /** When true, run Christmas validateOnly after Pacific validation (requires ONBOARDING_MIGRATION_RUNS_ENABLED). When false or omitted, Pacific-only. */
  destination_dry_run?: ValidationCreateDestinationDryRun;
  /** Target Datapay environment for destination dry-run. Defaults to nz-datapay when destination_dry_run is true for company_shell, work_patterns, or pay_components. */
  mypay_environment?: ValidationCreateMypayEnvironment;
  /** Credential tier for Christmas dry-run. Slice 5 accepts TEST only; non-TEST values are rejected. */
  target_environment?: TargetEnvironment;
}

export type ValidationErrorLocItem = string | number;

export type ValidationErrorCtx = { [key: string]: unknown };

export interface ValidationError {
  loc: ValidationErrorLocItem[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: ValidationErrorCtx;
}

export type ValidationReportValidatedAt = string | null;

/**
 * Aggregated per-row validation outcome for an entity-slice import.

Per the LADR-039 validation contract, ``errors`` (``RowSeverity.ERROR``)
block promotion of the entity record; ``warnings`` (``RowSeverity.WARNING``)
are informational only and never block.
 */
export interface ValidationReport {
  errors?: RowValidationError[];
  warnings?: RowValidationError[];
  validated_at?: ValidationReportValidatedAt;
  profile?: ValidateProfile;
}

export type ValidationViewReport = ValidationReport | null;

export type ValidationViewStartedAt = string | null;

export type ValidationViewCompletedAt = string | null;

/**
 * Read DTO — ``GET .../validations/{validation_id}`` response.

Mirrors the persisted ``Validation`` job document. Served from a
**workload-scoped** route (not nested under `entities/{entity_type}`) per
the LADR-039 API table — validation ids are unique within the
`/workloadId` partition, so one poll route serves entity and future
employee validations alike.
 */
export interface ValidationView {
  validation_id: string;
  workloadId: string;
  entity_type: EntityType;
  status: JobStatus;
  report?: ValidationViewReport;
  started_at?: ValidationViewStartedAt;
  completed_at?: ValidationViewCompletedAt;
  started_by: string;
}

/**
 * validate (dry-run) or commit (persist patterns)
 */
export type WorkPatternImportInputMode = typeof WorkPatternImportInputMode[keyof typeof WorkPatternImportInputMode];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WorkPatternImportInputMode = {
  validate: 'validate',
  commit: 'commit',
} as const;

/**
 * Batch-level scope type. 'Employee' routes to the employees endpoint with employeeCode per row; 'Employee Group' routes to the employeegroups endpoint with workPatternCode per row.
 */
export type WorkPatternImportInputScopeType = typeof WorkPatternImportInputScopeType[keyof typeof WorkPatternImportInputScopeType];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WorkPatternImportInputScopeType = {
  Employee: 'Employee',
  Employee_Group: 'Employee Group',
} as const;

/**
 * Track 1 inline work patterns to validate / commit. Mutually exclusive with entity_record_ref; when set must be non-empty. Capped at 500 at the model boundary.
 */
export type WorkPatternImportInputPatterns = WorkPatternRow[] | null;

/**
 * Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with patterns. Hydrated inside the workflow before the existing pattern-processing path runs.
 */
export type WorkPatternImportInputEntityRecordRef = EntityRecordRef | null;

/**
 * Optional caller-supplied key for replay safety.
 */
export type WorkPatternImportInputIdempotencyKey = string | null;

/**
 * Optional client-supplied correlation id. Threaded through activity logs and forwarded to the downstream adapter.
 */
export type WorkPatternImportInputCorrelationId = string | null;

/**
 * Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run.
 */
export type WorkPatternImportInputRunId = string | null;

/**
 * Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a migration-run child.
 */
export type WorkPatternImportInputWorkloadId = string | null;

/**
 * Destination-tier axis (LADR-039) selecting which Christmas credential/endpoint tier (test vs. prod) to push to. Set by the orchestrator for Track 2 children; optional for Track 1 callers.
 */
export type WorkPatternImportInputTargetEnvironment = TargetEnvironment | null;

/**
 * API-boundary payload for the ``WorkPatternImportWorkflow``.

The ``workflow`` field is the ``WorkflowPayload`` discriminator; keep the
Literal value in sync with the entry in ``ONBOARDING_TASK_QUEUE_WORKFLOWS``
inside ``workflow_registry.py``.

``scope_type`` is a batch-level field: every pattern in a single invocation
shares the same scope type, matching the page-level dropdown in the Web UI.
It controls which Christmas endpoint the adapter routes to and which
scope-code field (``employeeCode`` vs ``workPatternCode``) is expected.
 */
export interface WorkPatternImportInput {
  /** Workflow discriminator */
  workflow?: 'work_pattern_import';
  /** validate (dry-run) or commit (persist patterns) */
  mode: WorkPatternImportInputMode;
  /** Batch-level scope type. 'Employee' routes to the employees endpoint with employeeCode per row; 'Employee Group' routes to the employeegroups endpoint with workPatternCode per row. */
  scopeType: WorkPatternImportInputScopeType;
  /**
   * Company code to import patterns for. NOTE: this is the sole tenant discriminator on the write and is not yet scoped to the caller — see the accepted-gap note in app/temporal/onboarding/AGENTS.md (#843). The length/pattern bounds here are defence-in-depth, not authorization. Required for both Track 1 (inline patterns) and Track 2 (entity_record_ref) inputs — the orchestrator fills this from source_metadata.selection_fields (or workload.company_code fallback).
   * @minLength 1
   * @maxLength 64
   * @pattern ^[A-Za-z0-9._-]+$
   */
  company_code: string;
  /** Track 1 inline work patterns to validate / commit. Mutually exclusive with entity_record_ref; when set must be non-empty. Capped at 500 at the model boundary. */
  patterns?: WorkPatternImportInputPatterns;
  /** Track 2 pointer to a validated OnboardingEntityRecord in the same workload partition. Mutually exclusive with patterns. Hydrated inside the workflow before the existing pattern-processing path runs. */
  entity_record_ref?: WorkPatternImportInputEntityRecordRef;
  /** Optional caller-supplied key for replay safety. */
  idempotency_key?: WorkPatternImportInputIdempotencyKey;
  /** Optional client-supplied correlation id. Threaded through activity logs and forwarded to the downstream adapter. */
  correlation_id?: WorkPatternImportInputCorrelationId;
  /** Target Datapay environment; routes to the matching MyPay API deployment. */
  mypay_environment: MyPayEnvironment;
  /** Optional LADR-039 migration run identifier (ref-only — no PII). Set when this workflow is a child of a canonical migration run. */
  run_id?: WorkPatternImportInputRunId;
  /** Optional LADR-039 Tier 1 workload identifier (ref-only — no PII). Set alongside run_id when this workflow is a migration-run child. */
  workload_id?: WorkPatternImportInputWorkloadId;
  /** Destination-tier axis (LADR-039) selecting which Christmas credential/endpoint tier (test vs. prod) to push to. Set by the orchestrator for Track 2 children; optional for Track 1 callers. */
  target_environment?: WorkPatternImportInputTargetEnvironment;
}

export type WorkPatternRowEmployeeCode = string | null;

export type WorkPatternRowWorkPatternCode = string | null;

/**
 * One work pattern derived from the portal's CSV parse.

All fields use camelCase aliases to match the LADR036 JSON contract.
The model uses ``populate_by_name=True`` so internal code can reference
snake_case names without the alias.

``scope_type`` has been lifted to the batch level (``WorkPatternImportInput``)
and is no longer a per-row field.  The scope-code field is either
``employee_code`` (Employee scope) or ``work_pattern_code`` (Employee Group
scope); both are optional here because only one is required per batch —
mutual exclusivity is enforced by ``validator.py``.
 */
export interface WorkPatternRow {
  /** @maxLength 255 */
  workPatternName: string;
  employeeCode?: WorkPatternRowEmployeeCode;
  workPatternCode?: WorkPatternRowWorkPatternCode;
  cycleDays: number;
  startDate: string;
  /** @maxLength 10 */
  payPublicHolidays: string;
  standardHours: number;
  standardDays: number;
  dayHours: number[];
}

/**
 * Computed-on-read workload status (LADR-039). Never persisted.
 */
export type WorkloadAggregateStatus = typeof WorkloadAggregateStatus[keyof typeof WorkloadAggregateStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WorkloadAggregateStatus = {
  draft: 'draft',
  validation_failed: 'validation_failed',
  validated: 'validated',
  provisioning: 'provisioning',
  completed: 'completed',
  failed: 'failed',
} as const;

export type WorkloadViewCompanyCode = string | null;

export type WorkloadViewSourceSystem = SourceSystem | null;

/**
 * Read DTO — ``GET`` / list workload responses with computed aggregate status.

``aggregate_status`` is never persisted (LADR-039); it is resolved on read
from entity records + latest migration run.
 */
export interface WorkloadView {
  workloadId: string;
  workspace_id: string;
  company_code?: WorkloadViewCompanyCode;
  source_system?: WorkloadViewSourceSystem;
  destination_system: DestinationSystem;
  config_snapshot?: null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  aggregate_status: WorkloadAggregateStatus;
}

export type WorkspaceName = string | null;

export type WorkspaceProvisionedAt = string | null;

export interface Workspace {
  workspace_id: string;
  creator_user_id: string;
  name: WorkspaceName;
  provisioned_at: WorkspaceProvisionedAt;
}

export type GetLiveness200 = LiveHealthyResponse | LiveUnhealthyResponse;

export type GetReadiness200 = ReadyResponse | NotReadyResponse;

export type ListAgentsParams = {
include_inactive?: boolean;
};

export type ListInvocationsParams = {
/**
 * Max items per page
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * Opaque cursor for next page
 */
cursor?: string | null;
/**
 * Filter by creator user ID
 */
created_by?: string | null;
/**
 * Filter by run status (comma-separated: completed, running, failed, terminated)
 */
status?: string | null;
/**
 * Filter runs completed on or after (ISO 8601)
 */
dateFrom?: string | null;
/**
 * Filter runs completed on or before (ISO 8601)
 */
dateTo?: string | null;
/**
 * Sort field: completedAt, matchRate, durationInSeconds
 */
sortBy?: string;
/**
 * Sort direction: asc, desc
 */
sortDirection?: string;
};

export type ProvisionOrgBody = PublicOrgCreate | InternalOrgCreate | PersonalOrgCreate;

export type ProvisionOrg200Item = CreateOrgIfNotExistsResult | ProvisionAdminRoleResult | CreateDocumentStoreIfNotExistsResult;

export type DeprovisionOrgParams = {
org_name_or_id_verification: string;
};

export type DeprovisionOrg200Item = DeleteOrgIfExistsResult | DeprovisionOrgRolesResult | DeleteDocumentStoreIfExistsResult | DeleteOrgDocumentsResult | DeleteOrgChunksResult;

export type ListOrgsByUserParams = {
user_id: string;
};

export type UpdateOrgNameParams = {
name: string;
};

export type UpdateOrgOnboardingStatusParams = {
status: UpdateOrgOnboardingStatusStatus;
};

export type UpdateOrgOnboardingStatusStatus = typeof UpdateOrgOnboardingStatusStatus[keyof typeof UpdateOrgOnboardingStatusStatus];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const UpdateOrgOnboardingStatusStatus = {
  pending: 'pending',
  in_progress: 'in_progress',
  review: 'review',
  completed: 'completed',
} as const;

export type CreateInvitation200 = ManualCreateInvitationResponse | EmailCreateInvitationResponse | SmsCreateInvitationResponse;

export type RespondToInvitationByTokenParams = {
token: string;
accept: boolean;
};

export type RespondToInvitationByIdParams = {
org_id: string;
category: RespondToInvitationByIdCategory;
invitation_id: string;
accept: boolean;
};

export type RespondToInvitationByIdCategory = typeof RespondToInvitationByIdCategory[keyof typeof RespondToInvitationByIdCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RespondToInvitationByIdCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type GetOnboardingCompanyParams = {
/**
 * Target Datapay environment; routes company lookup to the matching MyPay API deployment.
 */
mypay_environment: MyPayEnvironment;
};

export type PostComponentsParametersParams = {
/**
 * Target Datapay environment; routes to the matching MyPay API deployment.
 */
mypay_environment: MyPayEnvironment;
};

export type PostComponentsParameters200 = {[key: string]: ComponentParameter[]};

export type GetComponentParametersParams = {
/**
 * Target Datapay environment; routes to the matching MyPay API deployment.
 */
mypay_environment: MyPayEnvironment;
};

export type ImportPayComponentsParams = {
/**
 * Target Datapay environment; routes to the matching MyPay API deployment.
 */
mypay_environment: MyPayEnvironment;
};

export type GetBatchSankeyParams = {
/**
 * Minimum packets for a flow to appear
 * @minimum 1
 * @maximum 100
 */
min_packet_threshold?: number;
/**
 * Maximum nodes in diagram
 * @minimum 5
 * @maximum 200
 */
max_nodes?: number;
};

export type ListBatchPacketsParams = {
/**
 * Page number (1-indexed)
 * @minimum 1
 */
page?: number;
/**
 * Items per page
 * @minimum 1
 * @maximum 100
 */
page_size?: number;
/**
 * Optional comma-separated severity tiers filter (e.g., MATCH,CRITICAL)
 */
severity_tiers?: string | null;
/**
 * Optional component filter (exact field match)
 */
component?: string | null;
/**
 * Optional pay group code filters (repeat query param, e.g. pay_group=MID&pay_group=HO)
 */
pay_group?: string[] | null;
/**
 * Optional pay period start date filter (ISO format)
 */
pay_period_start?: string | null;
/**
 * Optional pay period end date filters (repeat query param, ISO format)
 */
pay_period_end?: string[] | null;
};

export type ListDeadLettersParams = {
/**
 * Filter by batch ID
 */
batch_id?: string | null;
/**
 * Maximum entries per page
 * @minimum 1
 * @maximum 1000
 */
limit?: number;
/**
 * Number of entries to skip
 * @minimum 0
 */
offset?: number;
};

export type GetCompletionWithUserMessage200Item = UIUserMessage | UIAssistantMessage;

export type ExportAllThreadsCsvParams = {
/**
 * Number of threads per page to fetch while exporting
 * @minimum 1
 * @maximum 10000
 */
page_size?: number;
/**
 * Filter threads created after this datetime (ISO 8601 format)
 */
from_created_at?: string | null;
/**
 * Filter threads created before this datetime (ISO 8601 format)
 */
to_created_at?: string | null;
};

export type GetOwnUserProfile200 = UserProfile | null;

export type GetUserProfile200 = UserProfile | null;

export type GetOwnWorkspace200 = Workspace | null;

export type ListCapabilitiesParams = {
include_inactive?: boolean;
};

export type ListCompanyConfigsParams = {
status?: CompanyConfigStatus | null;
include_retired?: boolean;
};

export type ListEvalAnnotationsBySourceRefParams = {
/**
 * Source thread workspace ID
 */
workspace_id: string;
/**
 * Source thread ID
 */
thread_id: string;
/**
 * Zero-based index of the assistant message in the thread
 * @minimum 0
 */
assistant_message_index: number;
/**
 * Maximum number of rows to return per page
 * @minimum 1
 * @maximum 500
 */
page_size?: number;
/**
 * Opaque token returned by the prior page
 */
continuation_token?: string | null;
};

export type ListEvalAnnotationExportCandidatesParams = {
/**
 * Optional source workspace ID partition filter
 */
workspace_id?: string | null;
/**
 * Maximum number of rows to return per page
 * @minimum 1
 * @maximum 500
 */
page_size?: number;
/**
 * Opaque token returned by the prior page
 */
continuation_token?: string | null;
};

export type ListNotificationsParams = {
/**
 * @minLength 1
 */
org_id: string;
org_category: ListNotificationsOrgCategory;
unread_only?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListNotificationsOrgCategory = typeof ListNotificationsOrgCategory[keyof typeof ListNotificationsOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ListNotificationsOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type MarkAllNotificationsReadParams = {
/**
 * @minLength 1
 */
org_id: string;
org_category: MarkAllNotificationsReadOrgCategory;
};

export type MarkAllNotificationsReadOrgCategory = typeof MarkAllNotificationsReadOrgCategory[keyof typeof MarkAllNotificationsReadOrgCategory];


// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MarkAllNotificationsReadOrgCategory = {
  public: 'public',
  internal: 'internal',
  personal: 'personal',
} as const;

export type ListOnboardingWorkloadsParams = {
workspace_id: string;
};

export type CreateOnboardingEntityValidationBody = ValidationCreate | null;

export type ListOnboardingWorkloadAuditEventsParams = {
action?: AuditAction | null;
from_timestamp?: string | null;
to_timestamp?: string | null;
cursor?: string | null;
/**
 * @minimum 1
 * @maximum 999
 */
limit?: number;
};

export type GetActiveUsersParams = {
/**
 * Granularity: daily, weekly (ISO Mon-Sun), or monthly
 */
period?: Period;
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetCompletionsPerUserParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetCompletionTimeseriesParams = {
/**
 * Granularity: daily, weekly (ISO Mon-Sun), or monthly
 */
period?: Period;
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetThreadDepthParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetDocumentSourcesParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetFeedbackSummaryParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetUserGrowthParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetReturningUsersParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetReturningOrgsParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetOverviewParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetTopicClustersParams = {
/**
 * Include MCP interaction questions in clustering
 */
include_mcp?: boolean;
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type ListFeedbackItemsParams = {
/**
 * Inclusive ISO 8601 lower bound on thread creation timestamp
 */
period_start?: string | null;
/**
 * Inclusive ISO 8601 upper bound on thread creation timestamp
 */
period_end?: string | null;
/**
 * Feedback values to include; omit for both
 */
feedback_values?: FeedbackValue[] | null;
/**
 * Restrict to these workspace ids
 */
workspace_ids?: string[] | null;
/**
 * Restrict to threads referencing any of these knowledge orgs
 */
org_ids?: string[] | null;
/**
 * Restrict to these jurisdictions (e.g. 'nz', 'au-federal')
 */
jurisdictions?: string[] | null;
/**
 * Case-insensitive substring filter on the question text
 */
search_text?: string | null;
/**
 * Maximum number of rows to return
 * @minimum 1
 * @maximum 500
 */
page_size?: number;
/**
 * Opaque pagination token returned by a prior page
 */
continuation_token?: string | null;
};

export type GetMcpInteractionsPerUserParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetMcpInteractionTimeseriesParams = {
/**
 * Granularity: daily, weekly (ISO Mon-Sun), or monthly
 */
period?: Period;
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetMcpOverviewParams = {
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

export type GetMcpVelocityParams = {
/**
 * Granularity: daily, weekly (ISO Mon-Sun), or monthly
 */
period?: Period;
/**
 * Start date (ISO 8601, inclusive)
 */
from_date: string;
/**
 * End date (ISO 8601, inclusive)
 */
to_date: string;
};

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  export const getPayrollAgenticAPI = () => {
/**
 * Liveness probe - checks if the application is running.
Rate limited to prevent DoS attacks.
 * @summary Liveness
 */
const getLiveness = (
    
 options?: SecondParameter<typeof customCoreInstance<GetLiveness200>>,) => {
      return customCoreInstance<GetLiveness200>(
      {url: `/api/v1/health/live`, method: 'GET'
    },
      options);
    }
  
/**
 * Readiness probe - checks if the application can handle requests.
Rate limited to prevent DoS attacks.
 * @summary Readiness
 */
const getReadiness = (
    
 options?: SecondParameter<typeof customCoreInstance<GetReadiness200>>,) => {
      return customCoreInstance<GetReadiness200>(
      {url: `/api/v1/health/ready`, method: 'GET'
    },
      options);
    }
  
/**
 * Create a new Agent.
 * @summary Create Agent
 */
const createAgent = (
    agentCreate: AgentCreate,
 options?: SecondParameter<typeof customCoreInstance<Agent>>,) => {
      return customCoreInstance<Agent>(
      {url: `/api/v1/agents`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: agentCreate
    },
      options);
    }
  
/**
 * List all Agents.
 * @summary List Agents
 */
const listAgents = (
    params?: ListAgentsParams,
 options?: SecondParameter<typeof customCoreInstance<Agent[]>>,) => {
      return customCoreInstance<Agent[]>(
      {url: `/api/v1/agents`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Get an Agent by ID.
 * @summary Get Agent
 */
const getAgent = (
    agentId: string,
 options?: SecondParameter<typeof customCoreInstance<Agent>>,) => {
      return customCoreInstance<Agent>(
      {url: `/api/v1/agents/${agentId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Update an Agent's blueprint.
 * @summary Update Agent Blueprint
 */
const updateAgentBlueprint = (
    agentId: string,
    agentUpdate: AgentUpdate,
 options?: SecondParameter<typeof customCoreInstance<Agent>>,) => {
      return customCoreInstance<Agent>(
      {url: `/api/v1/agents/${agentId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: agentUpdate
    },
      options);
    }
  
/**
 * Soft-delete an Agent. Requires provisioner privileges.
 * @summary Delete Agent
 */
const deleteAgent = (
    agentId: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/agents/${agentId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Create a Service Account for an Agent.
 * @summary Create Service Account
 */
const createServiceAccount = (
    agentId: string,
    agentServiceAccountCreate: AgentServiceAccountCreate,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: agentServiceAccountCreate
    },
      options);
    }
  
/**
 * List Service Accounts for an Agent.
 * @summary List Service Accounts
 */
const listServiceAccounts = (
    agentId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount[]>>,) => {
      return customCoreInstance<AgentServiceAccount[]>(
      {url: `/api/v1/agents/${agentId}/service-accounts`, method: 'GET'
    },
      options);
    }
  
/**
 * Get a Service Account by ID.
 * @summary Get Service Account
 */
const getServiceAccount = (
    agentId: string,
    serviceAccountId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Soft-delete a Service Account.
 * @summary Delete Service Account
 */
const deleteServiceAccount = (
    agentId: string,
    serviceAccountId: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Grant a capability to a Service Account.
 * @summary Grant Capability
 */
const grantCapability = (
    agentId: string,
    serviceAccountId: string,
    capabilityId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}/capabilities/${capabilityId}`, method: 'POST'
    },
      options);
    }
  
/**
 * Revoke a capability from a Service Account.
 * @summary Revoke Capability
 */
const revokeCapability = (
    agentId: string,
    serviceAccountId: string,
    capabilityId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}/capabilities/${capabilityId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Sync a capability snapshot to the latest version.
 * @summary Sync Capability
 */
const syncCapability = (
    agentId: string,
    serviceAccountId: string,
    capabilityId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}/capabilities/${capabilityId}`, method: 'PUT'
    },
      options);
    }
  
/**
 * Sync all capability snapshots to their latest versions.
 * @summary Sync All Capability Snapshots
 */
const syncAllCapabilitySnapshots = (
    agentId: string,
    serviceAccountId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentServiceAccount>>,) => {
      return customCoreInstance<AgentServiceAccount>(
      {url: `/api/v1/agents/${agentId}/service-accounts/${serviceAccountId}/capabilities`, method: 'PUT'
    },
      options);
    }
  
/**
 * Invoke an agent workflow. Returns 202 immediately by default. When wait_for_completion is true, returns 200 with the terminal status if the workflow finishes within the server wait timeout.
 * @summary Invoke Agent
 */
const invokeAgent = (
    agentId: string,
    agentInvocationRequest: AgentInvocationRequest,
 options?: SecondParameter<typeof customCoreInstance<AgentInvocationResponse | AgentInvocationResponse>>,) => {
      return customCoreInstance<AgentInvocationResponse | AgentInvocationResponse>(
      {url: `/api/v1/agents/${agentId}/invocations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: agentInvocationRequest
    },
      options);
    }
  
/**
 * List shadow runs for an agent with cursor pagination, filtering, and sorting. Each item's `id` is the Temporal workflow ID, usable as the `{workflow_id}` path parameter for the invocation detail endpoint; `batchId` is the shadow-run summary ID for the batch results.
 * @summary List Invocations
 */
const listInvocations = (
    agentId: string,
    params?: ListInvocationsParams,
 options?: SecondParameter<typeof customCoreInstance<ShadowRunsListResponse>>,) => {
      return customCoreInstance<ShadowRunsListResponse>(
      {url: `/api/v1/agents/${agentId}/invocations`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Get the status of an agent invocation by workflow_id.
 * @summary Get Invocation Status
 */
const getInvocationStatus = (
    agentId: string,
    workflowId: string,
 options?: SecondParameter<typeof customCoreInstance<AgentInvocationStatus>>,) => {
      return customCoreInstance<AgentInvocationStatus>(
      {url: `/api/v1/agents/${agentId}/invocations/${workflowId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary List Documents By Org
 */
const listDocumentsByOrg = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
 options?: SecondParameter<typeof customCoreInstance<OrgDocument[]>>,) => {
      return customCoreInstance<OrgDocument[]>(
      {url: `/api/v1/org_document/list/${orgCategory}/${orgId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Get Document Download Url
 */
const getDocumentDownloadUrl = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    docId: string,
 options?: SecondParameter<typeof customCoreInstance<string>>,) => {
      return customCoreInstance<string>(
      {url: `/api/v1/org_document/download_url/${orgCategory}/${orgId}/${docId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Get Chunk
 */
const getChunk = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    docId: string,
    chunkId: string,
 options?: SecondParameter<typeof customCoreInstance<ChunkRead>>,) => {
      return customCoreInstance<ChunkRead>(
      {url: `/api/v1/org_document/chunk/${orgCategory}/${orgId}/${docId}/${chunkId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Provision Org
 */
const provisionOrg = (
    provisionOrgBody: ProvisionOrgBody,
 options?: SecondParameter<typeof customCoreInstance<ProvisionOrg200Item[]>>,) => {
      return customCoreInstance<ProvisionOrg200Item[]>(
      {url: `/api/v1/org_provisioner/provision_org`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: provisionOrgBody
    },
      options);
    }
  
/**
 * @summary List Orgs By Category
 */
const listOrgsByCategory = (
    orgCategory: 'public' | 'internal' | 'personal',
 options?: SecondParameter<typeof customCoreInstance<Org[]>>,) => {
      return customCoreInstance<Org[]>(
      {url: `/api/v1/org_provisioner/category/${orgCategory}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Deprovision Org
 */
const deprovisionOrg = (
    orgKey: OrgKey,
    params: DeprovisionOrgParams,
 options?: SecondParameter<typeof customCoreInstance<DeprovisionOrg200Item[]>>,) => {
      return customCoreInstance<DeprovisionOrg200Item[]>(
      {url: `/api/v1/org_provisioner/deprovision_org`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: orgKey,
        params
    },
      options);
    }
  
/**
 * @summary List Orgs By User
 */
const listOrgsByUser = (
    params: ListOrgsByUserParams,
 options?: SecondParameter<typeof customCoreInstance<Org[]>>,) => {
      return customCoreInstance<Org[]>(
      {url: `/api/v1/org`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Get Org
 */
const getOrg = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
 options?: SecondParameter<typeof customCoreInstance<Org>>,) => {
      return customCoreInstance<Org>(
      {url: `/api/v1/org/${category}/${orgId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Update Org Name
 */
const updateOrgName = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
    params: UpdateOrgNameParams,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org/${category}/${orgId}/name`, method: 'PATCH',
        params
    },
      options);
    }
  
/**
 * @summary Update Org Onboarding Status
 */
const updateOrgOnboardingStatus = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
    params: UpdateOrgOnboardingStatusParams,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org/${category}/${orgId}/onboarding`, method: 'PATCH',
        params
    },
      options);
    }
  
/**
 * @summary List Org Roles By User
 */
const listOrgRolesByUser = (
    userId: string,
 options?: SecondParameter<typeof customCoreInstance<UserOrgRole[]>>,) => {
      return customCoreInstance<UserOrgRole[]>(
      {url: `/api/v1/org-role/user/${userId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary List Org Roles By Org
 */
const listOrgRolesByOrg = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
 options?: SecondParameter<typeof customCoreInstance<UserOrgRole[]>>,) => {
      return customCoreInstance<UserOrgRole[]>(
      {url: `/api/v1/org-role/org/${category}/${orgId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Add Org Role
 */
const addOrgRole = (
    userOrgRole: UserOrgRole,
 options?: SecondParameter<typeof customCoreInstance<UserOrgRole>>,) => {
      return customCoreInstance<UserOrgRole>(
      {url: `/api/v1/org-role`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: userOrgRole
    },
      options);
    }
  
/**
 * @summary Remove Org Role
 */
const removeOrgRole = (
    userOrgRole: UserOrgRole,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org-role`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: userOrgRole
    },
      options);
    }
  
/**
 * @summary Create Invitation
 */
const createInvitation = (
    orgInvitationCreate: OrgInvitationCreate,
 options?: SecondParameter<typeof customCoreInstance<CreateInvitation200>>,) => {
      return customCoreInstance<CreateInvitation200>(
      {url: `/api/v1/org-invitation`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: orgInvitationCreate
    },
      options);
    }
  
/**
 * @summary Respond To Invitation By Token
 */
const respondToInvitationByToken = (
    params: RespondToInvitationByTokenParams,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org-invitation/respond-by-token`, method: 'POST',
        params
    },
      options);
    }
  
/**
 * @summary Respond To Invitation By Id
 */
const respondToInvitationById = (
    params: RespondToInvitationByIdParams,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org-invitation/respond-by-id`, method: 'POST',
        params
    },
      options);
    }
  
/**
 * @summary Create Bulk Invitations
 */
const createBulkInvitations = (
    bulkInvitationCreate: BulkInvitationCreate,
 options?: SecondParameter<typeof customCoreInstance<BulkInvitationResponse>>,) => {
      return customCoreInstance<BulkInvitationResponse>(
      {url: `/api/v1/org-invitation/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: bulkInvitationCreate
    },
      options);
    }
  
/**
 * @summary List Invitations By Self
 */
const listInvitationsBySelf = (
    
 options?: SecondParameter<typeof customCoreInstance<OrgInvitation[]>>,) => {
      return customCoreInstance<OrgInvitation[]>(
      {url: `/api/v1/org-invitation/self`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary List Invitations By Org
 */
const listInvitationsByOrg = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
 options?: SecondParameter<typeof customCoreInstance<OrgInvitation[]>>,) => {
      return customCoreInstance<OrgInvitation[]>(
      {url: `/api/v1/org-invitation/org/${category}/${orgId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Rescind Invitation
 */
const rescindInvitation = (
    category: 'public' | 'internal' | 'personal',
    orgId: string,
    invitationId: string,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/org-invitation/${category}/${orgId}/${invitationId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Resolve a company code to its name.

Returns 200 with ``{ companyCode, companyName }`` when the code resolves
and the onboarding service account has access; 404 when it does not.
 * @summary Get Onboarding Company
 */
const getOnboardingCompany = (
    companyCode: string,
    params: GetOnboardingCompanyParams,
 options?: SecondParameter<typeof customCoreInstance<CompanyLookupResult>>,) => {
      return customCoreInstance<CompanyLookupResult>(
      {url: `/api/v1/onboarding/companies/${companyCode}`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * List configured MyPay API environments for the onboarding selector.
 * @summary List Mypay Environments
 */
const listMyPayEnvironments = (
    
 options?: SecondParameter<typeof customCoreInstance<MyPayEndpointSummary[]>>,) => {
      return customCoreInstance<MyPayEndpointSummary[]>(
      {url: `/api/v1/onboarding/mypay-environments`, method: 'GET'
    },
      options);
    }
  
/**
 * Return selectable parameters for one or more pay components, keyed by
their display name (the "Component Name" / "Deduction Name" from the CSV).

Powers the mapping-setup screen: pass the distinct component names in
``componentNames``; each maps to its parameter list. Unknown names are
omitted from the response.
 * @summary Post Components Parameters
 */
const postComponentsParameters = (
    companyCode: string,
    componentsParametersRequest: ComponentsParametersRequest,
    params: PostComponentsParametersParams,
 options?: SecondParameter<typeof customCoreInstance<PostComponentsParameters200>>,) => {
      return customCoreInstance<PostComponentsParameters200>(
      {url: `/api/v1/christmas/companies/${companyCode}/components/parameters`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: componentsParametersRequest,
        params
    },
      options);
    }
  
/**
 * Return the selectable parameters for a single pay component.
 * @summary Get Component Parameters
 */
const getComponentParameters = (
    companyCode: string,
    componentCode: string,
    params: GetComponentParametersParams,
 options?: SecondParameter<typeof customCoreInstance<ComponentParameter[]>>,) => {
      return customCoreInstance<ComponentParameter[]>(
      {url: `/api/v1/christmas/companies/${companyCode}/components/${componentCode}/parameters`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Retrieve all saved column-to-parameter mappings for the company.
 * @summary Get Pay Component Mappings
 */
const getPayComponentMappings = (
    companyCode: string,
 options?: SecondParameter<typeof customCoreInstance<CompanyElementMapping[]>>,) => {
      return customCoreInstance<CompanyElementMapping[]>(
      {url: `/api/v1/christmas/companies/${companyCode}/pay-components/mappings`, method: 'GET'
    },
      options);
    }
  
/**
 * Save/update column-to-parameter mappings for the company.

Persists the supplied entries and returns the full updated mapping list.
``replaceScopes`` marks element scopes shown on the Map Columns screen
whose mappings should be fully replaced — including when every column
for that scope is skipped.
 * @summary Save Pay Component Mappings
 */
const savePayComponentMappings = (
    companyCode: string,
    saveMappingsRequest: SaveMappingsRequest,
 options?: SecondParameter<typeof customCoreInstance<CompanyElementMapping[]>>,) => {
      return customCoreInstance<CompanyElementMapping[]>(
      {url: `/api/v1/christmas/companies/${companyCode}/pay-components/mappings`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: saveMappingsRequest
    },
      options);
    }
  
/**
 * Remove all saved column-to-parameter mappings for the company.
 * @summary Clear Pay Component Mappings
 */
const clearPayComponentMappings = (
    companyCode: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/christmas/companies/${companyCode}/pay-components/mappings`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Upload a CSV and run the pre-flight (mapping check + validation).

This endpoint validates only — it never commits. A clean file returns
status "valid" (powers the review-before-commit screen); a file with
unmapped elements returns "needs_mapping"; blocking validation errors
return "invalid" (HTTP 422). To COMMIT, invoke the
``PayComponentImportWorkflow`` via the agent-invocation surface with
``mode: "commit"``.

Parses the CSV using DictReader (first row is the header). Reserved
columns (case-insensitive): "Employee Code" → employee scope code and
"Component Name" → component (resolved by display name; "Deduction Name"
is a legacy alias). The `component_column` form field overrides which
column identifies the component. "Employee Name" and "End Date" are
informational. "Start Date" is parsed per row into ``ImportRow.startDate``
(ISO ``YYYY-MM-DD``, optional); invalid formats are collected as
``ImportResult.errors``. All other columns are candidate parameter value
columns.

Returns 200 for "needs_mapping" and "valid"; 422 for "invalid".
 * @summary Import Pay Components
 */
const importPayComponents = (
    companyCode: string,
    bodyImportPayComponents: BodyImportPayComponents,
    params: ImportPayComponentsParams,
 options?: SecondParameter<typeof customCoreInstance<ImportResult>>,) => {const formData = new FormData();
formData.append(`file`, bodyImportPayComponents.file)
if(bodyImportPayComponents.provider_id !== undefined && bodyImportPayComponents.provider_id !== null) {
 formData.append(`provider_id`, bodyImportPayComponents.provider_id)
 }
if(bodyImportPayComponents.component_column !== undefined && bodyImportPayComponents.component_column !== null) {
 formData.append(`component_column`, bodyImportPayComponents.component_column)
 }

      return customCoreInstance<ImportResult>(
      {url: `/api/v1/christmas/companies/${companyCode}/pay-components/import`, method: 'POST',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData,
        params
    },
      options);
    }
  
/**
 * Get shadow run summary from CosmosDB.

Args:
    org_category: Organization category
    org_id: Organization ID
    batch_id: Unique shadow run identifier (path param kept for API compatibility)
    token: Authentication token

Returns:
    Shadow run summary

Raises:
    HTTPException: 404 if shadow run not found, 403 if unauthorized
 * @summary Get Shadow Run Summary
 */
const getShadowRunSummary = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    batchId: string,
 options?: SecondParameter<typeof customCoreInstance<ShadowRunSummary>>,) => {
      return customCoreInstance<ShadowRunSummary>(
      {url: `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Get shadow run summary by batch_id, resolving org context from stored data.

Simplified endpoint for frontend navigation where only batch_id is known.
Returns 404 for both not-found and unauthorized to prevent shadow run enumeration.

Args:
    batch_id: Unique shadow run identifier (path param kept for API compatibility)
    token: Authentication token

Returns:
    Shadow run summary

Raises:
    HTTPException: 404 if shadow run not found or unauthorized
 * @summary Get Shadow Run Summary By Id
 */
const getShadowRunSummaryById = (
    batchId: string,
 options?: SecondParameter<typeof customCoreInstance<ShadowRunSummary>>,) => {
      return customCoreInstance<ShadowRunSummary>(
      {url: `/api/v1/shadow/batches/${batchId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Get Sankey diagram data for variance cascade visualization.

Generates aggregated variance cascade data suitable for rendering
with D3.js, Plotly, or SankeyMATIC.

Args:
    org_category: Organization category
    org_id: Organization ID
    batch_id: Unique batch identifier
    token: Authentication token
    min_packet_threshold: Minimum packets for a flow to appear (1-100)
    max_nodes: Maximum nodes to include (5-200)

Returns:
    Sankey diagram data with nodes and links

Raises:
    HTTPException: 404 if batch not found, 403 if unauthorized
 * @summary Get Batch Sankey
 */
const getBatchSankey = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    batchId: string,
    params?: GetBatchSankeyParams,
 options?: SecondParameter<typeof customCoreInstance<GenerateSankeyOutput>>,) => {
      return customCoreInstance<GenerateSankeyOutput>(
      {url: `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}/sankey`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * List packet-level comparison results for a batch.

Supports filtering by severity tiers, variance component, pay group,
and pay period with page/page_size pagination. Response includes
facets for filter dropdown population.

Args:
    org_category: Organization category
    org_id: Organization ID
    batch_id: Unique batch identifier
    token: Authentication token
    page: Page number (1-indexed)
    page_size: Items per page
    severity_tiers: Optional comma-separated severity tiers
    component: Optional component/field with variance
    pay_group: Optional pay group code filters
    pay_period_start: Optional pay period start date filter (ISO)
    pay_period_end: Optional pay period end date filters (ISO)

Returns:
    Paginated packet response with facets

Raises:
    HTTPException: 404 if batch not found, 403 if unauthorized,
        422 for invalid filter values
 * @summary List Batch Packets
 */
const listBatchPackets = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    batchId: string,
    params?: ListBatchPacketsParams,
 options?: SecondParameter<typeof customCoreInstance<PaginatedPacketResponse>>,) => {
      return customCoreInstance<PaginatedPacketResponse>(
      {url: `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}/packets`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Get detailed packet comparison data for packet detail screen.

Includes summary totals, comparison rows, variance details, and
component-level mapping/input/output sections. For older runs that
do not include component traces, component sections are returned empty.
 * @summary Get Batch Packet Detail
 */
const getBatchPacketDetail = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    batchId: string,
    packetId: string,
 options?: SecondParameter<typeof customCoreInstance<PacketDetail>>,) => {
      return customCoreInstance<PacketDetail>(
      {url: `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}/packets/${packetId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Get full packet DAG data for frontend graph rendering.

Includes node details, directed edges, root/leaf node IDs, optional
execution order, and completeness flags for optional graph attributes.
 * @summary Get Batch Packet Graph
 */
const getBatchPacketGraph = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    batchId: string,
    packetId: string,
 options?: SecondParameter<typeof customCoreInstance<PacketGraphDetail>>,) => {
      return customCoreInstance<PacketGraphDetail>(
      {url: `/api/v1/shadow/batches/${orgCategory}/${orgId}/${batchId}/packets/${packetId}/graph`, method: 'GET'
    },
      options);
    }
  
/**
 * List dead-letter queue entries for an organization with pagination.

Returns failed packets that exhausted retries during batch processing.
Supports optional filtering by batch_id and pagination via limit/offset.

Args:
    org_category: Organization category
    org_id: Organization ID
    token: Authentication token
    batch_id: Optional filter by batch ID
    limit: Maximum entries per page (1-1000, default 100)
    offset: Number of entries to skip (default 0)

Returns:
    Paginated response with entries, total_count, limit, offset, has_more

Raises:
    HTTPException: 403 if unauthorized, 422 if invalid pagination params
 * @summary List Dead Letters
 */
const listDeadLetters = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    params?: ListDeadLettersParams,
 options?: SecondParameter<typeof customCoreInstance<PaginatedDeadLetterResponse>>,) => {
      return customCoreInstance<PaginatedDeadLetterResponse>(
      {url: `/api/v1/shadow/${orgCategory}/${orgId}/dlq`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Retry a failed document from the dead-letter queue.

Starts a new ShadowCalculatorWorkflow for the specified document.

Args:
    org_category: Organization category
    org_id: Organization ID
    doc_id: Document ID to retry
    token: Authentication token

Returns:
    RetryDeadLetterResponse with new workflow ID and run ID

Raises:
    HTTPException: 403 if unauthorized
 * @summary Retry Dead Letter
 */
const retryDeadLetter = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    docId: string,
 options?: SecondParameter<typeof customCoreInstance<RetryDeadLetterResponse>>,) => {
      return customCoreInstance<RetryDeadLetterResponse>(
      {url: `/api/v1/shadow/${orgCategory}/${orgId}/dlq/${docId}/retry`, method: 'POST'
    },
      options);
    }
  
/**
 * Delete a dead-letter queue entry.

Used to clean up DLQ entries after successful retry or manual resolution.
Returns whether the entry existed and was deleted.

Args:
    org_category: Organization category
    org_id: Organization ID
    doc_id: Document ID to delete
    token: Authentication token

Returns:
    DeleteDeadLetterResponse with deleted status

Raises:
    HTTPException: 403 if unauthorized
 * @summary Delete Dead Letter
 */
const deleteDeadLetter = (
    orgCategory: 'public' | 'internal' | 'personal',
    orgId: string,
    docId: string,
 options?: SecondParameter<typeof customCoreInstance<DeleteDeadLetterResponse>>,) => {
      return customCoreInstance<DeleteDeadLetterResponse>(
      {url: `/api/v1/shadow/${orgCategory}/${orgId}/dlq/${docId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * List all threads for a workspace. The messages are not included in the response.
 * @summary List Workspace Threads
 */
const listThreads = (
    workspaceId: string,
 options?: SecondParameter<typeof customCoreInstance<ThreadSummary[]>>,) => {
      return customCoreInstance<ThreadSummary[]>(
      {url: `/api/v1/workspace/${workspaceId}/thread`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Create Thread
 */
const createThread = (
    workspaceId: string,
    threadCreate: ThreadCreate,
 options?: SecondParameter<typeof customCoreInstance<UIThread>>,) => {
      return customCoreInstance<UIThread>(
      {url: `/api/v1/workspace/${workspaceId}/thread`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: threadCreate
    },
      options);
    }
  
/**
 * @summary Get Thread
 */
const getThread = (
    workspaceId: string,
    threadId: string,
 options?: SecondParameter<typeof customCoreInstance<UIThread>>,) => {
      return customCoreInstance<UIThread>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Delete Thread
 */
const deleteThread = (
    workspaceId: string,
    threadId: string,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * @summary Set Thread Title
 */
const setThreadTitle = (
    workspaceId: string,
    threadId: string,
    threadTitleUpdate: ThreadTitleUpdate,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/title`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: threadTitleUpdate
    },
      options);
    }
  
/**
 * Automatically infer and set a thread title based on the first user message.
 * @summary Infer Thread Title
 */
const inferThreadTitle = (
    workspaceId: string,
    threadId: string,
 options?: SecondParameter<typeof customCoreInstance<ThreadTitleUpdate>>,) => {
      return customCoreInstance<ThreadTitleUpdate>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/title/infer`, method: 'POST'
    },
      options);
    }
  
/**
 * @summary Set Thread Knowledge From Orgs
 */
const setThreadKnowledge = (
    workspaceId: string,
    threadId: string,
    threadKnowledgeUpdate: ThreadKnowledgeUpdate,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/knowledge`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: threadKnowledgeUpdate
    },
      options);
    }
  
/**
 * Send a user message and get the completion for a thread.
 * @summary Get Completion With User Message
 */
const getCompletionWithUserMessage = (
    workspaceId: string,
    threadId: string,
    threadCreateMessage: ThreadCreateMessage,
 options?: SecondParameter<typeof customCoreInstance<GetCompletionWithUserMessage200Item[]>>,) => {
      return customCoreInstance<GetCompletionWithUserMessage200Item[]>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/completion`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: threadCreateMessage
    },
      options);
    }
  
/**
 * Stream completion with progress updates and source previews via SSE. Returns Server-Sent Events with progress percentage, source previews, structured result, and completion signal.
 * @summary Stream Completion With User Message
 */
const streamCompletionWithUserMessage = (
    workspaceId: string,
    threadId: string,
    threadCreateMessage: ThreadCreateMessage,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/completion/stream`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: threadCreateMessage
    },
      options);
    }
  
/**
 * Set feedback for a specific message in a thread. Use 1 for thumbs up, -1 for thumbs down, 0 for no feedback.
 * @summary Set Thread Message Feedback
 */
const setThreadMessageFeedback = (
    workspaceId: string,
    threadId: string,
    messageIndex: number,
    threadMessageFeedbackRequest: ThreadMessageFeedbackRequest,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/workspace/${workspaceId}/thread/${threadId}/message/${messageIndex}/feedback`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: threadMessageFeedbackRequest
    },
      options);
    }
  
/**
 * Export all threads across workspaces as a CSV of question/answer rows. Restricted to provisioners only. Uses pagination under the hood; adjust filters to limit size.
 * @summary Export all threads as a flattened CSV
 */
const exportAllThreadsCsv = (
    params?: ExportAllThreadsCsvParams,
 options?: SecondParameter<typeof customCoreInstance<unknown>>,) => {
      return customCoreInstance<unknown>(
      {url: `/api/v1/thread-reports/threads.csv`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Sync User Profile
 */
const syncUserProfile = (
    
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/user-profile/sync`, method: 'POST'
    },
      options);
    }
  
/**
 * @summary Get Own Profile
 */
const getOwnUserProfile = (
    
 options?: SecondParameter<typeof customCoreInstance<GetOwnUserProfile200>>,) => {
      return customCoreInstance<GetOwnUserProfile200>(
      {url: `/api/v1/user-profile/me`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Get Profile
 */
const getUserProfile = (
    userId: string,
 options?: SecondParameter<typeof customCoreInstance<GetUserProfile200>>,) => {
      return customCoreInstance<GetUserProfile200>(
      {url: `/api/v1/user-profile/${userId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary List Profiles
 */
const listUserProfiles = (
    
 options?: SecondParameter<typeof customCoreInstance<UserProfileSummary[]>>,) => {
      return customCoreInstance<UserProfileSummary[]>(
      {url: `/api/v1/user-profile`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Get Own Workspace
 */
const getOwnWorkspace = (
    
 options?: SecondParameter<typeof customCoreInstance<GetOwnWorkspace200>>,) => {
      return customCoreInstance<GetOwnWorkspace200>(
      {url: `/api/v1/workspace`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Provision Workspace
 */
const provisionWorkspace = (
    
 options?: SecondParameter<typeof customCoreInstance<Workspace>>,) => {
      return customCoreInstance<Workspace>(
      {url: `/api/v1/workspace/provision`, method: 'POST'
    },
      options);
    }
  
/**
 * @summary Create Capability
 */
const createCapability = (
    capabilityCreate: CapabilityCreate,
 options?: SecondParameter<typeof customCoreInstance<Capability>>,) => {
      return customCoreInstance<Capability>(
      {url: `/api/v1/capability-catalog`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: capabilityCreate
    },
      options);
    }
  
/**
 * @summary List Capabilities
 */
const listCapabilities = (
    params?: ListCapabilitiesParams,
 options?: SecondParameter<typeof customCoreInstance<Capability[]>>,) => {
      return customCoreInstance<Capability[]>(
      {url: `/api/v1/capability-catalog`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Get Capability
 */
const getCapability = (
    capabilityId: string,
 options?: SecondParameter<typeof customCoreInstance<Capability>>,) => {
      return customCoreInstance<Capability>(
      {url: `/api/v1/capability-catalog/${capabilityId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Update Capability
 */
const updateCapability = (
    capabilityId: string,
    capabilityUpdate: CapabilityUpdate,
 options?: SecondParameter<typeof customCoreInstance<Capability>>,) => {
      return customCoreInstance<Capability>(
      {url: `/api/v1/capability-catalog/${capabilityId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: capabilityUpdate
    },
      options);
    }
  
/**
 * @summary Delete Capability
 */
const deleteCapability = (
    capabilityId: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/capability-catalog/${capabilityId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * Create a new company configuration and mint version 1.
 * @summary Create Company Config
 */
const createCompanyConfig = (
    companyConfigCreate: CompanyConfigCreate,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion>>,) => {
      return customCoreInstance<CompanyConfigVersion>(
      {url: `/api/v1/onboarding/company-configs`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: companyConfigCreate
    },
      options);
    }
  
/**
 * List company configurations (latest published version per config).
 * @summary List Company Configs
 */
const listCompanyConfigs = (
    params?: ListCompanyConfigsParams,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion[]>>,) => {
      return customCoreInstance<CompanyConfigVersion[]>(
      {url: `/api/v1/onboarding/company-configs`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Get the latest published version of a company configuration.
 * @summary Get Company Config Overview
 */
const getCompanyConfigOverview = (
    configCode: string,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion>>,) => {
      return customCoreInstance<CompanyConfigVersion>(
      {url: `/api/v1/onboarding/company-configs/${configCode}`, method: 'GET'
    },
      options);
    }
  
/**
 * Save a new version onto an existing company configuration.
 * @summary Save Company Config Version
 */
const saveCompanyConfigVersion = (
    configCode: string,
    companyConfigVersionCreate: CompanyConfigVersionCreate,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion>>,) => {
      return customCoreInstance<CompanyConfigVersion>(
      {url: `/api/v1/onboarding/company-configs/${configCode}/versions`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: companyConfigVersionCreate
    },
      options);
    }
  
/**
 * Return the full version history (change log) for a company configuration.
 * @summary Get Company Config Change Log
 */
const getCompanyConfigChangeLog = (
    configCode: string,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion[]>>,) => {
      return customCoreInstance<CompanyConfigVersion[]>(
      {url: `/api/v1/onboarding/company-configs/${configCode}/versions`, method: 'GET'
    },
      options);
    }
  
/**
 * Return a specific published version of a company configuration.
 * @summary Get Company Config Version
 */
const getCompanyConfigVersion = (
    configCode: string,
    version: number,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion>>,) => {
      return customCoreInstance<CompanyConfigVersion>(
      {url: `/api/v1/onboarding/company-configs/${configCode}/versions/${version}`, method: 'GET'
    },
      options);
    }
  
/**
 * Retire (soft-delete) a specific version of a company configuration.
 * @summary Retire Company Config Version
 */
const retireCompanyConfigVersion = (
    configCode: string,
    version: number,
    companyConfigRetire: CompanyConfigRetire,
 options?: SecondParameter<typeof customCoreInstance<CompanyConfigVersion>>,) => {
      return customCoreInstance<CompanyConfigVersion>(
      {url: `/api/v1/onboarding/company-configs/${configCode}/versions/${version}`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: companyConfigRetire
    },
      options);
    }
  
/**
 * @summary List Api Keys
 */
const listMcpApiKeys = (
    
 options?: SecondParameter<typeof customCoreInstance<ApiKeySummary[]>>,) => {
      return customCoreInstance<ApiKeySummary[]>(
      {url: `/api/v1/mcp/api_keys`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Create Api Key
 */
const createMcpApiKey = (
    apiKeyCreate: ApiKeyCreate,
 options?: SecondParameter<typeof customCoreInstance<ApiKeyCreated>>,) => {
      return customCoreInstance<ApiKeyCreated>(
      {url: `/api/v1/mcp/api_keys`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: apiKeyCreate
    },
      options);
    }
  
/**
 * @summary Revoke Api Key
 */
const revokeMcpApiKey = (
    keyId: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/mcp/api_keys/${keyId}`, method: 'DELETE'
    },
      options);
    }
  
/**
 * @summary Create a new pending eval annotation
 */
const createEvalAnnotation = (
    createAnnotationRequest: CreateAnnotationRequest,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotation>>,) => {
      return customCoreInstance<EvalAnnotation>(
      {url: `/api/v1/eval/annotations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createAnnotationRequest
    },
      options);
    }
  
/**
 * Provisioner-only. Returns a paginated page of annotation rows for the given source artifact, ordered by ``created_at`` ascending (ties broken by ``annotation_id`` ascending). The ``continuation_token`` is opaque to callers and ``null`` when no further pages remain.
 * @summary List annotation history for a single source artifact
 */
const listEvalAnnotationsBySourceRef = (
    params: ListEvalAnnotationsBySourceRefParams,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotationListResponse>>,) => {
      return customCoreInstance<EvalAnnotationListResponse>(
      {url: `/api/v1/eval/annotations/source-ref`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Provisioner-only. Returns a paginated page of approved + sanitised + not-yet-exported annotations, ordered by ``created_at`` ascending (ties broken by ``annotation_id`` ascending). ``workspace_id`` narrows to a single tenant; omitting it scans across partitions and is bounded by ``page_size``.
 * @summary List approved + sanitised + not-yet-exported annotations
 */
const listEvalAnnotationExportCandidates = (
    params?: ListEvalAnnotationExportCandidatesParams,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotationListResponse>>,) => {
      return customCoreInstance<EvalAnnotationListResponse>(
      {url: `/api/v1/eval/annotations/export-candidates`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Read a single annotation by workspace_id + annotation_id
 */
const getEvalAnnotation = (
    workspaceId: string,
    annotationId: string,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotation>>,) => {
      return customCoreInstance<EvalAnnotation>(
      {url: `/api/v1/eval/annotations/${workspaceId}/${annotationId}`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Move an annotation along the review lifecycle
 */
const updateEvalAnnotationReview = (
    workspaceId: string,
    annotationId: string,
    updateReviewRequest: UpdateReviewRequest,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotation>>,) => {
      return customCoreInstance<EvalAnnotation>(
      {url: `/api/v1/eval/annotations/${workspaceId}/${annotationId}/review`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateReviewRequest
    },
      options);
    }
  
/**
 * @summary Record sanitisation for an annotation
 */
const markEvalAnnotationSanitised = (
    workspaceId: string,
    annotationId: string,
    markSanitisedRequest: MarkSanitisedRequest,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotation>>,) => {
      return customCoreInstance<EvalAnnotation>(
      {url: `/api/v1/eval/annotations/${workspaceId}/${annotationId}/sanitisation`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: markSanitisedRequest
    },
      options);
    }
  
/**
 * @summary Flip an approved + sanitised annotation to EXPORTED
 */
const markEvalAnnotationExported = (
    workspaceId: string,
    annotationId: string,
    markExportedRequest: MarkExportedRequest,
 options?: SecondParameter<typeof customCoreInstance<EvalAnnotation>>,) => {
      return customCoreInstance<EvalAnnotation>(
      {url: `/api/v1/eval/annotations/${workspaceId}/${annotationId}/exported`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: markExportedRequest
    },
      options);
    }
  
/**
 * @summary List Notifications
 */
const listNotifications = (
    params: ListNotificationsParams,
 options?: SecondParameter<typeof customCoreInstance<NotificationEvent[]>>,) => {
      return customCoreInstance<NotificationEvent[]>(
      {url: `/api/v1/notifications`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Mark Notification Read
 */
const markNotificationRead = (
    notificationId: string,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/notifications/${notificationId}/mark_read`, method: 'POST'
    },
      options);
    }
  
/**
 * @summary Mark All Notifications Read
 */
const markAllNotificationsRead = (
    params: MarkAllNotificationsReadParams,
 options?: SecondParameter<typeof customCoreInstance<void>>,) => {
      return customCoreInstance<void>(
      {url: `/api/v1/notifications/mark_all_read`, method: 'POST',
        params
    },
      options);
    }
  
/**
 * Create a new onboarding workload (Tier 1 header).
 * @summary Create Onboarding Workload
 */
const createOnboardingWorkload = (
    onboardingWorkloadCreate: OnboardingWorkloadCreate,
 options?: SecondParameter<typeof customCoreInstance<OnboardingWorkload>>,) => {
      return customCoreInstance<OnboardingWorkload>(
      {url: `/api/v1/onboarding/workloads`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: onboardingWorkloadCreate
    },
      options);
    }
  
/**
 * List onboarding workloads for a workspace.
 * @summary List Onboarding Workloads
 */
const listOnboardingWorkloads = (
    params: ListOnboardingWorkloadsParams,
 options?: SecondParameter<typeof customCoreInstance<WorkloadView[]>>,) => {
      return customCoreInstance<WorkloadView[]>(
      {url: `/api/v1/onboarding/workloads`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Get a single onboarding workload by id.
 * @summary Get Onboarding Workload
 */
const getOnboardingWorkload = (
    workloadId: string,
 options?: SecondParameter<typeof customCoreInstance<WorkloadView>>,) => {
      return customCoreInstance<WorkloadView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Stage a raw source upload to blob and start SourceMappingWorkflow to map it into a Tier 2 OnboardingEntityRecord.
 * @summary Create Onboarding Import
 */
const createOnboardingImport = (
    workloadId: string,
    bodyCreateOnboardingImport: BodyCreateOnboardingImport,
 options?: SecondParameter<typeof customCoreInstance<ImportAccepted>>,) => {const formData = new FormData();
formData.append(`file`, bodyCreateOnboardingImport.file)
formData.append(`entity_type`, bodyCreateOnboardingImport.entity_type)
if(bodyCreateOnboardingImport.config_code !== undefined && bodyCreateOnboardingImport.config_code !== null) {
 formData.append(`config_code`, bodyCreateOnboardingImport.config_code)
 }
if(bodyCreateOnboardingImport.config_version !== undefined && bodyCreateOnboardingImport.config_version !== null) {
 formData.append(`config_version`, bodyCreateOnboardingImport.config_version.toString())
 }
if(bodyCreateOnboardingImport.company_code !== undefined && bodyCreateOnboardingImport.company_code !== null) {
 formData.append(`company_code`, bodyCreateOnboardingImport.company_code)
 }
if(bodyCreateOnboardingImport.scope_type !== undefined && bodyCreateOnboardingImport.scope_type !== null) {
 formData.append(`scope_type`, bodyCreateOnboardingImport.scope_type)
 }
if(bodyCreateOnboardingImport.provider_id !== undefined && bodyCreateOnboardingImport.provider_id !== null) {
 formData.append(`provider_id`, bodyCreateOnboardingImport.provider_id)
 }
if(bodyCreateOnboardingImport.if_match_source_version !== undefined && bodyCreateOnboardingImport.if_match_source_version !== null) {
 formData.append(`if_match_source_version`, bodyCreateOnboardingImport.if_match_source_version)
 }

      return customCoreInstance<ImportAccepted>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/imports`, method: 'POST',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData
    },
      options);
    }
  
/**
 * Get a single source-import job by id.
 * @summary Get Onboarding Import
 */
const getOnboardingImport = (
    workloadId: string,
    importId: string,
 options?: SecondParameter<typeof customCoreInstance<ImportView>>,) => {
      return customCoreInstance<ImportView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/imports/${importId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Get the current Tier 2 entity slice for a workload/entity type.
 * @summary Get Onboarding Entity Record
 */
const getOnboardingEntityRecord = (
    workloadId: string,
    entityType: EntityType,
 options?: SecondParameter<typeof customCoreInstance<EntityRecordView>>,) => {
      return customCoreInstance<EntityRecordView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/entities/${entityType}`, method: 'GET'
    },
      options);
    }
  
/**
 * Apply canonical-field edits under a required If-Match precondition, re-run the shared validator, and transition the entity-record state machine.
 * @summary Patch Onboarding Entity Record
 */
const patchOnboardingEntityRecord = (
    workloadId: string,
    entityType: EntityType,
    entityRecordPatch: EntityRecordPatch,
 options?: SecondParameter<typeof customCoreInstance<EntityRecordView>>,) => {
      return customCoreInstance<EntityRecordView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/entities/${entityType}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: entityRecordPatch
    },
      options);
    }
  
/**
 * Run the shared validator on demand (Pacific rules, and optionally Christmas validateOnly when destination_dry_run is set — PRD D-4), persist a Validation job document, and transition the entity-record state machine.
 * @summary Create Onboarding Entity Validation
 */
const createOnboardingEntityValidation = (
    workloadId: string,
    entityType: EntityType,
    createOnboardingEntityValidationBody: CreateOnboardingEntityValidationBody,
 options?: SecondParameter<typeof customCoreInstance<ValidationAccepted>>,) => {
      return customCoreInstance<ValidationAccepted>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/entities/${entityType}/validations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createOnboardingEntityValidationBody
    },
      options);
    }
  
/**
 * Get a single validation job by id (workload-scoped poll route).
 * @summary Get Onboarding Validation
 */
const getOnboardingValidation = (
    workloadId: string,
    validationId: string,
 options?: SecondParameter<typeof customCoreInstance<ValidationView>>,) => {
      return customCoreInstance<ValidationView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/validations/${validationId}`, method: 'GET'
    },
      options);
    }
  
/**
 * Push the workload's validated canonical entities to a target Christmas environment (Tier 3 MigrationRun).
 * @summary Create Onboarding Migration Run
 */
const createOnboardingMigrationRun = (
    workloadId: string,
    migrationRunCreate: MigrationRunCreate,
 options?: SecondParameter<typeof customCoreInstance<RunAccepted>>,) => {
      return customCoreInstance<RunAccepted>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/runs`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: migrationRunCreate
    },
      options);
    }
  
/**
 * List migration runs for a workload.
 * @summary List Onboarding Migration Runs
 */
const listOnboardingMigrationRuns = (
    workloadId: string,
 options?: SecondParameter<typeof customCoreInstance<RunView[]>>,) => {
      return customCoreInstance<RunView[]>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/runs`, method: 'GET'
    },
      options);
    }
  
/**
 * Get a single migration run by id (poll route).
 * @summary Get Onboarding Migration Run
 */
const getOnboardingMigrationRun = (
    workloadId: string,
    runId: string,
 options?: SecondParameter<typeof customCoreInstance<RunView>>,) => {
      return customCoreInstance<RunView>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/runs/${runId}`, method: 'GET'
    },
      options);
    }
  
/**
 * List audit events recorded for a workload (cursor-paginated), filterable by action and timestamp range.
 * @summary List Onboarding Workload Audit Events
 */
const listOnboardingWorkloadAuditEvents = (
    workloadId: string,
    params?: ListOnboardingWorkloadAuditEventsParams,
 options?: SecondParameter<typeof customCoreInstance<AuditEventPage>>,) => {
      return customCoreInstance<AuditEventPage>(
      {url: `/api/v1/onboarding/workloads/${workloadId}/audit-events`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Returns a time series of unique active user counts grouped by the chosen period granularity. An active user is one who created at least one new thread in the period. Users are identified via workspace_id → workspace.creator_user_id. Empty buckets (zero activity) are included so charts render without gaps.
 * @summary Active user time series grouped by period
 */
const getActiveUsers = (
    params: GetActiveUsersParams,
 options?: SecondParameter<typeof customCoreInstance<ActiveUsersResponse>>,) => {
      return customCoreInstance<ActiveUsersResponse>(
      {url: `/api/v1/platform-analytics/active-users`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Completion request counts per user
 */
const getCompletionsPerUser = (
    params: GetCompletionsPerUserParams,
 options?: SecondParameter<typeof customCoreInstance<CompletionsPerUserResponse>>,) => {
      return customCoreInstance<CompletionsPerUserResponse>(
      {url: `/api/v1/platform-analytics/completions-per-user`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Returns a time series of total completion counts grouped by the chosen period granularity. A completion is a non-request (assistant) message in a thread. Empty buckets (zero completions) are included so charts render without gaps.
 * @summary Completion counts grouped by period as a time series
 */
const getCompletionTimeseries = (
    params: GetCompletionTimeseriesParams,
 options?: SecondParameter<typeof customCoreInstance<CompletionTimeseriesResponse>>,) => {
      return customCoreInstance<CompletionTimeseriesResponse>(
      {url: `/api/v1/platform-analytics/completion-timeseries`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Conversation depth stats and distribution buckets
 */
const getThreadDepth = (
    params: GetThreadDepthParams,
 options?: SecondParameter<typeof customCoreInstance<ThreadDepthResponse>>,) => {
      return customCoreInstance<ThreadDepthResponse>(
      {url: `/api/v1/platform-analytics/thread-depth`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Most referenced documents
 */
const getDocumentSources = (
    params: GetDocumentSourcesParams,
 options?: SecondParameter<typeof customCoreInstance<DocumentSourcesResponse>>,) => {
      return customCoreInstance<DocumentSourcesResponse>(
      {url: `/api/v1/platform-analytics/document-sources`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Thumbs up/down ratios
 */
const getFeedbackSummary = (
    params: GetFeedbackSummaryParams,
 options?: SecondParameter<typeof customCoreInstance<FeedbackSummaryResponse>>,) => {
      return customCoreInstance<FeedbackSummaryResponse>(
      {url: `/api/v1/platform-analytics/feedback-summary`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary New workspace provisions over time
 */
const getUserGrowth = (
    params: GetUserGrowthParams,
 options?: SecondParameter<typeof customCoreInstance<UserGrowthResponse>>,) => {
      return customCoreInstance<UserGrowthResponse>(
      {url: `/api/v1/platform-analytics/user-growth`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Member/admin counts per org
 */
const getOrgAdoption = (
    
 options?: SecondParameter<typeof customCoreInstance<OrgAdoptionResponse>>,) => {
      return customCoreInstance<OrgAdoptionResponse>(
      {url: `/api/v1/platform-analytics/org-adoption`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary Monthly returning users (MRU) and retention rate
 */
const getReturningUsers = (
    params: GetReturningUsersParams,
 options?: SecondParameter<typeof customCoreInstance<ReturningUsersResponse>>,) => {
      return customCoreInstance<ReturningUsersResponse>(
      {url: `/api/v1/platform-analytics/returning-users`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Monthly returning orgs (MRO) and retention rate
 */
const getReturningOrgs = (
    params: GetReturningOrgsParams,
 options?: SecondParameter<typeof customCoreInstance<ReturningOrgsResponse>>,) => {
      return customCoreInstance<ReturningOrgsResponse>(
      {url: `/api/v1/platform-analytics/returning-orgs`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Dashboard summary with totals, active users, and document health
 */
const getOverview = (
    params: GetOverviewParams,
 options?: SecondParameter<typeof customCoreInstance<OverviewResponse>>,) => {
      return customCoreInstance<OverviewResponse>(
      {url: `/api/v1/platform-analytics/overview`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary Topic clustering of thread questions via UMAP + HDBSCAN
 */
const getTopicClusters = (
    params: GetTopicClustersParams,
 options?: SecondParameter<typeof customCoreInstance<TopicClusterResponse>>,) => {
      return customCoreInstance<TopicClusterResponse>(
      {url: `/api/v1/platform-analytics/topic-clusters`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Provisioner-only. Returns a page of feedback events with enough Q&A, citation, knowledge-org, and annotation context for an internal admin to decide whether the row is a useful golden candidate. api_key/eval-tagged threads are excluded from the underlying repository sweep.
 * @summary Paginated list of feedback-bearing Q&A rows for steward review
 */
const listFeedbackItems = (
    params?: ListFeedbackItemsParams,
 options?: SecondParameter<typeof customCoreInstance<FeedbackReviewListResponse>>,) => {
      return customCoreInstance<FeedbackReviewListResponse>(
      {url: `/api/v1/platform-analytics/feedback-items`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * Provisioner-only. Returns the ordered conversation messages, citations, knowledge orgs, jurisdiction, latest annotation summary, observability links, and informational PII highlight spans for the selected feedback event. api_key/eval-tagged threads are reported as not found at the repository layer.
 * @summary Conversation trace projection for a single feedback item
 */
const getFeedbackTrace = (
    workspaceId: string,
    threadId: string,
    assistantMessageIndex: number,
 options?: SecondParameter<typeof customCoreInstance<FeedbackTraceResponse>>,) => {
      return customCoreInstance<FeedbackTraceResponse>(
      {url: `/api/v1/platform-analytics/feedback-items/${workspaceId}/${threadId}/${assistantMessageIndex}/trace`, method: 'GET'
    },
      options);
    }
  
/**
 * @summary MCP interaction counts grouped by platform user
 */
const getMcpInteractionsPerUser = (
    params: GetMcpInteractionsPerUserParams,
 options?: SecondParameter<typeof customCoreInstance<McpInteractionsPerUserResponse>>,) => {
      return customCoreInstance<McpInteractionsPerUserResponse>(
      {url: `/api/v1/platform-analytics/mcp-interactions-per-user`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary MCP interaction counts grouped by period as a time series
 */
const getMcpInteractionTimeseries = (
    params: GetMcpInteractionTimeseriesParams,
 options?: SecondParameter<typeof customCoreInstance<McpInteractionTimeseriesResponse>>,) => {
      return customCoreInstance<McpInteractionTimeseriesResponse>(
      {url: `/api/v1/platform-analytics/mcp-interaction-timeseries`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary MCP interaction summary statistics
 */
const getMcpOverview = (
    params: GetMcpOverviewParams,
 options?: SecondParameter<typeof customCoreInstance<McpOverviewResponse>>,) => {
      return customCoreInstance<McpOverviewResponse>(
      {url: `/api/v1/platform-analytics/mcp-overview`, method: 'GET',
        params
    },
      options);
    }
  
/**
 * @summary MCP interaction velocity (interactions per hour) over time
 */
const getMcpVelocity = (
    params: GetMcpVelocityParams,
 options?: SecondParameter<typeof customCoreInstance<McpVelocityResponse>>,) => {
      return customCoreInstance<McpVelocityResponse>(
      {url: `/api/v1/platform-analytics/mcp-velocity`, method: 'GET',
        params
    },
      options);
    }
  
return {getLiveness,getReadiness,createAgent,listAgents,getAgent,updateAgentBlueprint,deleteAgent,createServiceAccount,listServiceAccounts,getServiceAccount,deleteServiceAccount,grantCapability,revokeCapability,syncCapability,syncAllCapabilitySnapshots,invokeAgent,listInvocations,getInvocationStatus,listDocumentsByOrg,getDocumentDownloadUrl,getChunk,provisionOrg,listOrgsByCategory,deprovisionOrg,listOrgsByUser,getOrg,updateOrgName,updateOrgOnboardingStatus,listOrgRolesByUser,listOrgRolesByOrg,addOrgRole,removeOrgRole,createInvitation,respondToInvitationByToken,respondToInvitationById,createBulkInvitations,listInvitationsBySelf,listInvitationsByOrg,rescindInvitation,getOnboardingCompany,listMyPayEnvironments,postComponentsParameters,getComponentParameters,getPayComponentMappings,savePayComponentMappings,clearPayComponentMappings,importPayComponents,getShadowRunSummary,getShadowRunSummaryById,getBatchSankey,listBatchPackets,getBatchPacketDetail,getBatchPacketGraph,listDeadLetters,retryDeadLetter,deleteDeadLetter,listThreads,createThread,getThread,deleteThread,setThreadTitle,inferThreadTitle,setThreadKnowledge,getCompletionWithUserMessage,streamCompletionWithUserMessage,setThreadMessageFeedback,exportAllThreadsCsv,syncUserProfile,getOwnUserProfile,getUserProfile,listUserProfiles,getOwnWorkspace,provisionWorkspace,createCapability,listCapabilities,getCapability,updateCapability,deleteCapability,createCompanyConfig,listCompanyConfigs,getCompanyConfigOverview,saveCompanyConfigVersion,getCompanyConfigChangeLog,getCompanyConfigVersion,retireCompanyConfigVersion,listMcpApiKeys,createMcpApiKey,revokeMcpApiKey,createEvalAnnotation,listEvalAnnotationsBySourceRef,listEvalAnnotationExportCandidates,getEvalAnnotation,updateEvalAnnotationReview,markEvalAnnotationSanitised,markEvalAnnotationExported,listNotifications,markNotificationRead,markAllNotificationsRead,createOnboardingWorkload,listOnboardingWorkloads,getOnboardingWorkload,createOnboardingImport,getOnboardingImport,getOnboardingEntityRecord,patchOnboardingEntityRecord,createOnboardingEntityValidation,getOnboardingValidation,createOnboardingMigrationRun,listOnboardingMigrationRuns,getOnboardingMigrationRun,listOnboardingWorkloadAuditEvents,getActiveUsers,getCompletionsPerUser,getCompletionTimeseries,getThreadDepth,getDocumentSources,getFeedbackSummary,getUserGrowth,getOrgAdoption,getReturningUsers,getReturningOrgs,getOverview,getTopicClusters,listFeedbackItems,getFeedbackTrace,getMcpInteractionsPerUser,getMcpInteractionTimeseries,getMcpOverview,getMcpVelocity}};
export type GetLivenessResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getLiveness']>>>
export type GetReadinessResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getReadiness']>>>
export type CreateAgentResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createAgent']>>>
export type ListAgentsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listAgents']>>>
export type GetAgentResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getAgent']>>>
export type UpdateAgentBlueprintResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['updateAgentBlueprint']>>>
export type DeleteAgentResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deleteAgent']>>>
export type CreateServiceAccountResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createServiceAccount']>>>
export type ListServiceAccountsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listServiceAccounts']>>>
export type GetServiceAccountResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getServiceAccount']>>>
export type DeleteServiceAccountResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deleteServiceAccount']>>>
export type GrantCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['grantCapability']>>>
export type RevokeCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['revokeCapability']>>>
export type SyncCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['syncCapability']>>>
export type SyncAllCapabilitySnapshotsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['syncAllCapabilitySnapshots']>>>
export type InvokeAgentResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['invokeAgent']>>>
export type ListInvocationsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listInvocations']>>>
export type GetInvocationStatusResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getInvocationStatus']>>>
export type ListDocumentsByOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listDocumentsByOrg']>>>
export type GetDocumentDownloadUrlResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getDocumentDownloadUrl']>>>
export type GetChunkResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getChunk']>>>
export type ProvisionOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['provisionOrg']>>>
export type ListOrgsByCategoryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOrgsByCategory']>>>
export type DeprovisionOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deprovisionOrg']>>>
export type ListOrgsByUserResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOrgsByUser']>>>
export type GetOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOrg']>>>
export type UpdateOrgNameResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['updateOrgName']>>>
export type UpdateOrgOnboardingStatusResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['updateOrgOnboardingStatus']>>>
export type ListOrgRolesByUserResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOrgRolesByUser']>>>
export type ListOrgRolesByOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOrgRolesByOrg']>>>
export type AddOrgRoleResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['addOrgRole']>>>
export type RemoveOrgRoleResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['removeOrgRole']>>>
export type CreateInvitationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createInvitation']>>>
export type RespondToInvitationByTokenResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['respondToInvitationByToken']>>>
export type RespondToInvitationByIdResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['respondToInvitationById']>>>
export type CreateBulkInvitationsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createBulkInvitations']>>>
export type ListInvitationsBySelfResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listInvitationsBySelf']>>>
export type ListInvitationsByOrgResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listInvitationsByOrg']>>>
export type RescindInvitationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['rescindInvitation']>>>
export type GetOnboardingCompanyResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingCompany']>>>
export type ListMyPayEnvironmentsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listMyPayEnvironments']>>>
export type PostComponentsParametersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['postComponentsParameters']>>>
export type GetComponentParametersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getComponentParameters']>>>
export type GetPayComponentMappingsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getPayComponentMappings']>>>
export type SavePayComponentMappingsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['savePayComponentMappings']>>>
export type ClearPayComponentMappingsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['clearPayComponentMappings']>>>
export type ImportPayComponentsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['importPayComponents']>>>
export type GetShadowRunSummaryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getShadowRunSummary']>>>
export type GetShadowRunSummaryByIdResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getShadowRunSummaryById']>>>
export type GetBatchSankeyResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getBatchSankey']>>>
export type ListBatchPacketsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listBatchPackets']>>>
export type GetBatchPacketDetailResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getBatchPacketDetail']>>>
export type GetBatchPacketGraphResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getBatchPacketGraph']>>>
export type ListDeadLettersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listDeadLetters']>>>
export type RetryDeadLetterResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['retryDeadLetter']>>>
export type DeleteDeadLetterResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deleteDeadLetter']>>>
export type ListThreadsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listThreads']>>>
export type CreateThreadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createThread']>>>
export type GetThreadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getThread']>>>
export type DeleteThreadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deleteThread']>>>
export type SetThreadTitleResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['setThreadTitle']>>>
export type InferThreadTitleResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['inferThreadTitle']>>>
export type SetThreadKnowledgeResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['setThreadKnowledge']>>>
export type GetCompletionWithUserMessageResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompletionWithUserMessage']>>>
export type StreamCompletionWithUserMessageResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['streamCompletionWithUserMessage']>>>
export type SetThreadMessageFeedbackResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['setThreadMessageFeedback']>>>
export type ExportAllThreadsCsvResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['exportAllThreadsCsv']>>>
export type SyncUserProfileResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['syncUserProfile']>>>
export type GetOwnUserProfileResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOwnUserProfile']>>>
export type GetUserProfileResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getUserProfile']>>>
export type ListUserProfilesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listUserProfiles']>>>
export type GetOwnWorkspaceResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOwnWorkspace']>>>
export type ProvisionWorkspaceResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['provisionWorkspace']>>>
export type CreateCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createCapability']>>>
export type ListCapabilitiesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listCapabilities']>>>
export type GetCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCapability']>>>
export type UpdateCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['updateCapability']>>>
export type DeleteCapabilityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['deleteCapability']>>>
export type CreateCompanyConfigResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createCompanyConfig']>>>
export type ListCompanyConfigsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listCompanyConfigs']>>>
export type GetCompanyConfigOverviewResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompanyConfigOverview']>>>
export type SaveCompanyConfigVersionResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['saveCompanyConfigVersion']>>>
export type GetCompanyConfigChangeLogResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompanyConfigChangeLog']>>>
export type GetCompanyConfigVersionResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompanyConfigVersion']>>>
export type RetireCompanyConfigVersionResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['retireCompanyConfigVersion']>>>
export type ListMcpApiKeysResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listMcpApiKeys']>>>
export type CreateMcpApiKeyResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createMcpApiKey']>>>
export type RevokeMcpApiKeyResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['revokeMcpApiKey']>>>
export type CreateEvalAnnotationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createEvalAnnotation']>>>
export type ListEvalAnnotationsBySourceRefResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listEvalAnnotationsBySourceRef']>>>
export type ListEvalAnnotationExportCandidatesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listEvalAnnotationExportCandidates']>>>
export type GetEvalAnnotationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getEvalAnnotation']>>>
export type UpdateEvalAnnotationReviewResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['updateEvalAnnotationReview']>>>
export type MarkEvalAnnotationSanitisedResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['markEvalAnnotationSanitised']>>>
export type MarkEvalAnnotationExportedResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['markEvalAnnotationExported']>>>
export type ListNotificationsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listNotifications']>>>
export type MarkNotificationReadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['markNotificationRead']>>>
export type MarkAllNotificationsReadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['markAllNotificationsRead']>>>
export type CreateOnboardingWorkloadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createOnboardingWorkload']>>>
export type ListOnboardingWorkloadsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOnboardingWorkloads']>>>
export type GetOnboardingWorkloadResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingWorkload']>>>
export type CreateOnboardingImportResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createOnboardingImport']>>>
export type GetOnboardingImportResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingImport']>>>
export type GetOnboardingEntityRecordResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingEntityRecord']>>>
export type PatchOnboardingEntityRecordResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['patchOnboardingEntityRecord']>>>
export type CreateOnboardingEntityValidationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createOnboardingEntityValidation']>>>
export type GetOnboardingValidationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingValidation']>>>
export type CreateOnboardingMigrationRunResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['createOnboardingMigrationRun']>>>
export type ListOnboardingMigrationRunsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOnboardingMigrationRuns']>>>
export type GetOnboardingMigrationRunResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOnboardingMigrationRun']>>>
export type ListOnboardingWorkloadAuditEventsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listOnboardingWorkloadAuditEvents']>>>
export type GetActiveUsersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getActiveUsers']>>>
export type GetCompletionsPerUserResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompletionsPerUser']>>>
export type GetCompletionTimeseriesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getCompletionTimeseries']>>>
export type GetThreadDepthResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getThreadDepth']>>>
export type GetDocumentSourcesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getDocumentSources']>>>
export type GetFeedbackSummaryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getFeedbackSummary']>>>
export type GetUserGrowthResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getUserGrowth']>>>
export type GetOrgAdoptionResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOrgAdoption']>>>
export type GetReturningUsersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getReturningUsers']>>>
export type GetReturningOrgsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getReturningOrgs']>>>
export type GetOverviewResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getOverview']>>>
export type GetTopicClustersResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getTopicClusters']>>>
export type ListFeedbackItemsResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['listFeedbackItems']>>>
export type GetFeedbackTraceResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getFeedbackTrace']>>>
export type GetMcpInteractionsPerUserResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getMcpInteractionsPerUser']>>>
export type GetMcpInteractionTimeseriesResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getMcpInteractionTimeseries']>>>
export type GetMcpOverviewResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getMcpOverview']>>>
export type GetMcpVelocityResult = NonNullable<Awaited<ReturnType<ReturnType<typeof getPayrollAgenticAPI>['getMcpVelocity']>>>
